import { Buffer } from 'buffer';
import { concat, defer, EMPTY, forkJoin, from, lastValueFrom, Observable, of, tap, throwError, timer, zip } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  finalize,
  ignoreElements,
  last,
  map,
  mergeMap,
  repeat,
  retry,
  RetryConfig,
  take,
  shareReplay,
  takeUntil,
  toArray,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import * as semver from 'semver';
import { decode as base64ToArrayBuffer } from 'base64-arraybuffer';
import {
  computeGearRatio,
  Device,
  emptyAppBase64,
  factoryResetDefaultFilesToKeep,
  isValidDeviceRefObj,
  makeDeviceRefObj,
  parsePlantModelCsv,
  stackUnlockFilename,
} from './device';
import {
  DeviceParameter,
  makeDeviceParameterId,
  splitDeviceParameterId,
  uiConfigWithDefaultPdoMapping,
} from './device-parameter';
import {
  dummyHardwareDescriptionFileContentTemplate,
  extractProductId,
  getImageIdFromHardwareDescription,
  getSerialNumberFromHardwareDescription,
  HardwareDescription,
  makeDeviceSerialNumberFromAddress,
  StackInfo,
} from './hardware-description';
import { MotionMasterReqResSocket } from './motion-master-req-res-socket';
import { transformMotionMasterMessageToStatus } from './operators';
import {
  betaParameterIds,
  getParameterEsiExtension,
  getParameterEsiExtensionFallback,
  getParameterValue,
  isSmmParameterIndex,
  makeParameterId,
  ParameterEsiExtension,
  parameterIdToPlotAxis,
  parameterIdToPlotColor,
  parameterIdToRadix,
  ParameterInputType,
  ParameterPlot,
  UIFeaturesConfig,
  valueTypeToParameterTypeValueKeyMap,
} from './parameter';
import { SystemLogLine } from './system-log-line';
import {
  MotionMasterMessage,
  ParameterValueType,
  ParameterTypeValue,
  DeviceParameterValuesStatus,
  ParameterTypeValueKey,
  DeviceRefObj,
  DeviceRef,
  isArrayOfDeviceSerialNumbers,
  isObjectOfDeviceSerialNumberToValues,
  FullAutoTuningStatus,
  SystemVersionStatus,
  DeviceInfoStatus,
  DeviceParameterInfoStatus,
  GetDeviceParameterInfoRequest,
  GetDeviceParameterValuesRequest,
  GetMultiDeviceParameterValuesRequest,
  MultiDeviceParameterValuesStatus,
  SetDeviceParameterValuesRequest,
  SetMultiDeviceParameterValuesRequest,
  GetDeviceFileListRequest,
  DeviceFileListStatus,
  GetDeviceFileRequest,
  DeviceFileStatus,
  SetDeviceFileRequest,
  DeleteDeviceFileRequest,
  ResetDeviceFaultRequest,
  DeviceFaultResetStatus,
  StopDeviceRequest,
  DeviceStopStatus,
  StartDeviceFirmwareInstallationRequest,
  DeviceFirmwareInstallationStatus,
  GetDeviceLogRequest,
  DeviceLogStatus,
  StartCoggingTorqueRecordingRequest,
  CoggingTorqueRecordingStatus,
  GetCoggingTorqueDataRequest,
  CoggingTorqueDataStatus,
  StartOffsetDetectionRequest,
  OffsetDetectionStatus,
  StartPlantModelIdentificationRequest,
  PlantIdentificationStatus,
  ComputeAutoTuningGainsRequest,
  AutoTuningStatus,
  SetMotionControllerParametersRequest,
  MotionControllerStatus,
  EnableMotionControllerRequest,
  SetSignalGeneratorParametersRequest,
  SignalGeneratorStatus,
  StopSignalGeneratorRequest,
  StartMonitoringDeviceParameterValuesRequest,
  MonitoringParameterValuesStatus,
  StopMonitoringDeviceParameterValuesRequest,
  GetEthercatNetworkStateRequest,
  EthercatNetworkStateStatus,
  SetEthercatNetworkStateRequest,
  StartNarrowAngleCalibrationRequest,
  NarrowAngleCalibrationStatus,
  SetSystemClientTimeoutRequest,
  StartSystemIdentificationRequest,
  SystemIdentificationStatus,
  CirculoEncoderMagnetDistanceStatus,
  StartCirculoEncoderNarrowAngleCalibrationProcedureRequest,
  CirculoEncoderNarrowAngleCalibrationProcedureStatus,
  GetDeviceCiA402StateRequest,
  DeviceCiA402StateStatus,
  SetDeviceCiA402StateRequest,
  SystemLogStatus,
  StartDeviceSiiRestoreRequest,
  DeviceSiiRestoreStatus,
  StartOpenLoopFieldControlRequest,
  OpenLoopFieldControlStatus,
  ComputeFullAutoTuningGainsRequest,
  StartFullAutoTuningRequest,
  StopFullAutoTuningRequest,
  StartCirculoEncoderConfigurationRequest,
  CirculoEncoderConfigurationStatus,
  StopCirculoEncoderNarrowAngleCalibrationProcedureRequest,
  StartOsCommandRequest,
  OsCommandStatus,
  RestoreDefaultParametersType,
  DeviceParameterAddressValue,
  DeviceParameterIds,
  DeviceParameterInfo,
  RequestStatusMessage,
  FirmwareProductSupportedOptions,
  UiPdoMapping,
  UiConfig,
  ExecuteForwardCallRequest,
  ForwardCallStatus,
  PlantModel,
  NotchFilter,
  LoadConfigStrategy,
  SystemInformationStatus,
  SetPdoModeRequest,
  PdoModeStatus,
  GetDeviceSiiRequest,
  DeviceSiiStatus,
  SmmAutomatedUpdate,
  SmmAutomatedUpdateProgress,
  SmmSoftwareUpdateProgress,
  SmmSoftwareUpdateStage,
} from './types';
import { getCia402State, Cia402State, ControlwordCommand, ModesOfOperation } from './cia402';
import { cia402Transitions } from './cia402';
import { createControlwordCommand } from './cia402';
import {
  createEncoderRegisterCommunicationOsCommand,
  createMotorPhaseOrderDetectionOsCommand,
  createPhaseResistanceMeasurementOsCommand,
  createPhaseInductanceMeasurementOsCommand,
  EncoderRegisterCommunicationOsCommandResponse,
  MotorPhaseOrderDetectionOsCommandResponse,
  OpenPhaseDetectionOsCommandResponse,
  OsCommandMode,
  OsCommandResponse,
  parseOsCommandResponse,
  PhaseResistanceMeasurementOsCommandResponse,
  PhaseInductanceMeasurementOsCommandResponse,
  createPolePairDetectionOsCommand,
  PolePairDetectionOsCommandResponse,
  createCommutationOffsetMeasurementOsCommand,
  CommutationOffsetMeasurementOsCommandResponse,
  createIcMuCalibrationModeOsCommand,
  IcMuCalibrationModeOsCommandResponse,
  createOpenLoopFieldModeOsCommand,
  OpenLoopFieldModeOsCommandResponse,
  createOpenPhaseDetectionOsCommand,
  createHrdStreamingOsCommand,
  HrdStreamingOsCommandResponse,
  createTorqueConstantMeasurementOsCommand,
  TorqueConstantMeasurementOsCommandResponse,
  SkippedCyclesCounterOsCommandResponse,
  createSkippedCyclesCounterOsCommand,
  createIgnoreBissStatusBitsOsCommand,
  IgnoreBissStatusBitsOsCommandResponse,
  createSystemIdentificationOsCommand,
  SystemIdentificationOsCommandResponse,
  createTriggerErrorOsCommand,
  KublerEncoderCommandOsCommandResponse,
  createUseInternalEncoderVelocityOsCommand,
  UseInternalEncoderVelocityOsCommandResponse,
  KublerEncoderRegisterCommunicationOsCommandResponse,
  createKublerEncoderRegisterCommunicationOsCommand,
  mapOsCommandResponse,
  SmmAcyclicHandlerOsCommandResponse,
  createKublerEncoderOsCommand,
  KublerEncoderSubcommandId,
  createKublerEncoderCommandCheckFirmwareUpdateSizeOsCommand,
  createKublerEncoderCommandWriteFirmwareOsCommand,
  isOsCommandResponse,
  HrdStreamingOsCommandAction,
  HrdStreamingOsCommandDataIndex,
  createSmmAcyclicHandlerReadSmmParameterOsCommand,
  createSmmAcyclicHandlerOsCommand,
  SmmAcyclicHandlerSubcommandId,
  createSmmAcyclicHandlerConfigureSoftwareUpdateOsCommand,
  SmmAcyclicHandlerLoadParametersForVerificationOsCommandResponse,
  SystemIdentificationOsCommandStartProcedure,
  ChirpSignalOptions,
  parseHrdBuffer,
  OsCommandAbortResult,
  describeOsCommandFailure,
} from './os-command';
import {
  createRandomColor,
  decodeTextContent,
  mapByProperty,
  parseSystemLogContent,
  resolveAfter,
  setBitHigh,
  setBitLow,
} from './util';
import { IntegroVariant, parseIntegroVariantBuffer } from './integro-variant';
import { ConfigFile, ConfigParameter } from './config-file';
import { findSomanetProductById, isSomanetProductIdInRange, ProductFamily, resolveEncoderProductId } from './product';
import { MotionMasterClientIndexedDB } from './mmcidb';
import {
  packSmmParameterValuesForVerification,
  packSmmChangePassword,
  packSmmCredentials,
  packSmmParameterValues,
  packSmmSoftwareUpdateCrc,
  packSmmValidationReport,
  resolveSmmOd,
  SmmParameter,
  unpackSmmParameterValues,
  convertGroupedSmmParametersToTransmitData,
  parseSmmBinaryHeaders,
  getInformationFromSmmFirmwareVersion,
  bitwiseDecodeNumberVersion,
  resolveSmmSoftwareUpdateTimings,
  assertValidSmmCredential,
  SMM_PASSWORD_MIN_LENGTH,
  SMM_USERNAME_MIN_LENGTH,
} from './smm';
import { DataType } from 'python-struct';
import { SmmOd } from './smm-od';
import { extractFirmwareId } from './firmware';
import { esiFilename, esiZipFilename, extractEsiFromFirmwarePackage } from './firmware-esi';
import { fetchSupportedAis, fetchSupportedDios } from './fetch';
import {
  CirculoExternalEncoderType,
  CirculoIntegratedEncoderI2CDeviceId,
  CirculoIntegratedEncoderIcmuCommands,
  CirculoIntegratedEncoderIcmuParameters,
  circuloIntegratedEncoderKeyConfigurationRegisterAddresses,
  buildCirculoIntegratedEncoderReport,
  circuloIntegratedEncoderAmplitudeControlRegisters,
  CirculoIntegratedEncoderReport,
  icMuHardRevRegister,
  getCirculoEncoderMaxPermissiblePhaseError,
} from './circulo';
import { CirculoEncoderRegisterAddress, EncoderRegisterError, mapCirculoEncoderRegisterValueToErrors } from './encoder';
import { parsePdoMappingEntry } from './pdo';
import { IntegroIntegratedEncoderError, parseIntegroIntegratedEncoderError } from './integro';
import { OffsetDetectionStepId, OffsetDetectionSteps, offsetDetectionSteps } from './offset-detection';
import { getParameterFormattedUnitDisplay } from './units';
import { gpioVoltageLevelOptions } from './gpio';
const { xmlToEsi } = require('ecatmod');

/**
 * This class contains methods for making requests to Motion Master using the injected request/response socket.
 *
 * Each request message defined in the proto file has a corresponding request method in this class.
 * For some requests, like firmware installation, Motion Master will send progress messages until the firmware installation is complete.
 *
 * A call to a request method will return an Observable of: (1) the corresponding status message,
 * for example a call to `getDeviceInfo` will return an instance of `MotionMasterMessage.Status.DeviceInfo`,
 * (2) request status - one of "succeeded," "failed," or "running.", and (3) request message id.
 *
 * If the request fails on Motion Master, the function will throw an error. It can also throw an error if the request times out.
 *
 * Request methods have an optional messageId input parameter.
 * If `messageId` is not provided, one will be generated before sending the request message to Motion Master.
 * Status messages (responses) received from Motion Master that correspond to the previous request will have the same `messageId` as the one in the request.
 * This is how we match requests with response messages in full-duplex asynchronous communication (WebSockets, ZeroMQ).
 * Matching request/response by message id is inspired by the [JSON-RPC 2.0 Specification](https://www.jsonrpc.org/specification).
 *
 * Almost all request methods have a mandatory `requestTimeout` input parameter.
 * This parameter specifies how long (in milliseconds) the client will wait for the status (response) messages corresponding to the previous request.
 * If the status message is not received in due time, the request method will throw a timeout error.
 * The error doesn't mean the request has failed on Motion Master, but rather that the client has given up waiting for the response(s).
 * It's possible to stop some ongoing procedures on Motion Master with the corresponding stop messages, for example, StopFullAutoTuning.
 *
 * @example
 * How to handle a request method timeout error:
 * ```typescript
 * req.getDeviceInfo(10).subscribe({
 *   next: (status) => console.log(status),
 *   error: (err: unknown) => {
 *     if (err instanceof TimeoutError) {
 *       alert('Request has timed out');
 *     }
 *   }
 * });
 * ```
 *
 * Request methods for a particular device, like `getDeviceParameterInfo`, accept the type of request message as the first input parameter as defined in the proto file.
 * For `getDeviceParameterInfo`, that's `MotionMasterMessage.Request.IGetDeviceParameterInfo`. This type is extended (via type intersection) by `DeviceRefObj`.
 * This is done in order to support multiple ways to reference a device. Motion Master only supports device addresses, which it re-generates for each device on power cycle.
 * Because device addresses can change, for scripts and applications, it's preferred to use a device reference that won't change.
 * The device serial number in the hardware description file uniquely identifies the device and doesn't change. This is what each request message is extended with.
 * Before sending the request message to Motion Master, the device address gets resolved and is set on the request message.
 * Besides the device address and device serial number, devices can be referenced by position (sequence number in the network chain).
 *
 * It is ensured that the Observable returned from a call to the request method will eventually complete, so a call to unsubscribe is not required.
 * The Observable will complete when the request status is either "succeeded" or "failed," or it will complete (error) due to a timeout.
 * Finding out if a request has completed, failed, or is still ongoing is different for each request - it depends on the status messages, see {@link requestStatusResolver}.
 * Request methods handle this and for each request, they will emit status messages with one of "succeeded," "failed," or "running" request statuses.
 *
 * Besides the request methods, this class contains many helper methods such as the ones for getting and setting device parameter values and
 * saving all parameters, which involves complex logic like multiple repeating requests to determine if saving all parameters has completed successfully.
 */
export class MotionMasterReqResClient {
  /**
   * Maps a device address to a map of parameter ID to a type value key.
   *
   * @example
   * ```typescript
   * new Map([
   *   [2045285448, new Map([
   *     ["0x1018:02", {
   *       id: '0x1018:02',
   *       index: 4120,
   *       subindex: 2,
   *       name: 'Product Code',
   *       unit: '',
   *       group: 'Identity object',
   *       readAccess: true,
   *       writeAccess: false,
   *       min: undefined,
   *       max: undefined,
   *       valueType: 7, // UNSIGNED32
   *       typeValueKey: "uintValue"
   *     }]
   *   ])]
   * ]);
   * ```
   *
   * When setting a parameter value, only one of int, uint, float, string, or raw type value fields must be set.
   * This is defined in the proto file SetDeviceParameterValues request message.
   *
   * Device parameters have a richer set of base types defined in ESI, and they are provided by the DeviceParameterInfo status message.
   * When device parameter info is fetched, this map gets updated by mapping device parameter types to "oneof" proto types, e.g., `INTEGER32 -> intValue`.
   *
   * This map helps in simplifying the API by allowing users to set parameter values without specifying the type value field.
   * One can simply call `setParameterValue(2045285448, 0x2004, 0x03, 2500)`, and the correct type value field on the proto message will be set, `uintValue` in this example.
   */
  readonly deviceParameterInfoMap = new Map<number, Map<string, DeviceParameterInfo>>();

  /**
   * A list of devices available on the network.
   * Each device entry specifies its type, position in the network, and address.
   * Additionally, it includes a parsed Integro variant and hardware description for each device.
   */
  readonly devices: Device[] = [];

  readonly mmcidb: MotionMasterClientIndexedDB = new MotionMasterClientIndexedDB();

  constructor(public readonly socket: MotionMasterReqResSocket) {}

  /**
   * Clears the state of an instance of this class.
   *
   * This method is typically called when Motion Master is deinitializing or the socket is closing.
   * Both maps are implicitly populated when a device or parameter type value key gets resolved.
   */
  clearDevicesInformation() {
    this.deviceParameterInfoMap.clear();
    this.devices.length = 0;
  }

  //
  // Requests
  //

  /**
   * Pings the system.
   *
   * The client must send messages to Motion Master at regular intervals for Motion Master to consider it alive.
   * When the client is considered "not alive", Motion Master will stop the ongoing procedures and monitorings started by that client.
   * Each client on the Motion Master side has its own default timeout, which can be changed by {@link setSystemClientTimeout}.
   */
  pingSystem(messageId?: string): Observable<RequestStatusMessage> {
    const pingSystem = MotionMasterMessage.Request.PingSystem.create();
    this.sendRequest({ pingSystem }, messageId);
    return of({ request: 'succeeded' });
  }

  /**
   * Get the system version.
   *
   * Retrieve the version of Motion Master. The version follows [Semantic Versioning 2.0.0](https://semver.org/).
   */
  getSystemVersion(requestTimeout: number, messageId?: string): Observable<SystemVersionStatus> {
    const getSystemVersion = MotionMasterMessage.Request.GetSystemVersion.create();
    const id = this.sendRequest({ getSystemVersion }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SystemVersion>(
        'systemVersion',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Retrieve system information from Motion Master.
   *
   * The returned information includes:
   * - `version`: The Motion Master version, following [Semantic Versioning 2.0.0](https://semver.org/).
   * - `startOptions`: The command-line arguments used to start Motion Master.
   */
  getSystemInformation(requestTimeout: number, messageId?: string): Observable<SystemInformationStatus> {
    const getSystemInformation = MotionMasterMessage.Request.GetSystemInformation.create();
    const id = this.sendRequest({ getSystemInformation }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SystemInformation>(
        'systemInformation',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Get device info.
   *
   * Device info includes a list of devices on the network. Each device has a type, position in the network, and a device address.
   * At the time of writing, the device alias is not yet supported.
   *
   * Motion Master will assign a unique device address to each device in the list.
   * The device address is what the client libraries use to make requests like `getDeviceParameter` for a particular device.
   * The device address is only valid for the duration of the session, that is, until Motion Master re-initializes the devices, for example,
   * when devices are power cycled or the Motion Master process is restarted.
   */
  getDeviceInfo(requestTimeout: number, messageId?: string): Observable<DeviceInfoStatus> {
    const getDeviceInfo = MotionMasterMessage.Request.GetDeviceInfo.create();
    const id = this.sendRequest({ getDeviceInfo }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceInfo>('deviceInfo', requestTimeout, id)
    );
  }

  /**
   * Get device parameter info.
   *
   * Device parameter info includes a list of all parameters for the requested device, but without the parameter values.
   * Getting device parameter values can take more than 20ms per parameter using the IgH EtherCAT Master library.
   * Not all clients require all the parameter values at once, like OBLAC Drives, so this method is useful for getting
   * the list of available parameters and then separately obtaining the values of some required parameters.
   *
   * The returned list of device parameters will include some basic data for each parameter: index, subindex,
   * name, unit, group (ARRAY or RECORD name for subitems), read and write access, min and max, and value type.
   * Min and max values are not yet fully supported - they contain the min/max values for the used base type.
   * The same info and more can be read from the "SOMANET_CiA_402.xml" file, but getting info from the ESI file is more complicated.
   * It requires an XML parsing library and knowing how to read objects info from a dictionary and assigned modules.
   */
  getDeviceParameterInfo(
    props: GetDeviceParameterInfoRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceParameterInfoStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getDeviceParameterInfo = MotionMasterMessage.Request.GetDeviceParameterInfo.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getDeviceParameterInfo }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceParameterInfo>(
            'deviceParameterInfo',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get device parameter values.
   *
   * As mentioned earlier, fetching parameter values can take 20ms or more. However,
   * it can also fail completely or timeout on the master side, registering as a "cannot refresh value" error.
   */
  getDeviceParameterValues(
    props: GetDeviceParameterValuesRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceParameterValuesStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getDeviceParameterValues = MotionMasterMessage.Request.GetDeviceParameterValues.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getDeviceParameterValues }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceParameterValues>(
            'deviceParameterValues',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get multi-device parameter values.
   *
   * Allows requesting parameter values from multiple devices at the same time.
   */
  getMultiDeviceParameterValues(
    props: GetMultiDeviceParameterValuesRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<MultiDeviceParameterValuesStatus> {
    const getMultiDeviceParameterValues = MotionMasterMessage.Request.GetMultiDeviceParameterValues.create(props);
    const id = this.sendRequest({ getMultiDeviceParameterValues }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.MultiDeviceParameterValues>(
        'multiDeviceParameterValues',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Set device parameter values.
   */
  setDeviceParameterValues(
    props: SetDeviceParameterValuesRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceParameterValuesStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setDeviceParameterValues = MotionMasterMessage.Request.SetDeviceParameterValues.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ setDeviceParameterValues }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceParameterValues>(
            'deviceParameterValues',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Set multi-device parameter values.
   *
   * Allows setting parameter values on multiple devices at the same time.
   */
  setMultiDeviceParameterValues(
    props: SetMultiDeviceParameterValuesRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<MultiDeviceParameterValuesStatus> {
    const setMultiDeviceParameterValues = MotionMasterMessage.Request.SetMultiDeviceParameterValues.create(props);
    const id = this.sendRequest({ setMultiDeviceParameterValues }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.MultiDeviceParameterValues>(
        'multiDeviceParameterValues',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Get device file list.
   *
   * Motion Master will use Filetransfer over EtherCAT (FoE) to obtain the list of files from a device.
   * There isn't an actual command for getting the list, but rather the content of a specially named file "fs-getlist" is the file list.
   * The list can be read like any other file from a device using the IgH EtherCAT Master for Linux CLI program:
   *
   * ```
   * $ ethercat foe_read fs-getlist
   * ```
   *
   * At the time of writing this document, "fs-getlist" can return a maximum of 32 files (subject to change) in the list. This is a firmware limitation.
   * However, more than 32 files can be stored on the device flash memory, but they won't appear in the list.
   * Also, note that FoE can behave differently in the bootstrap firmware, which runs in the BOOT EtherCAT state, and
   * SOMANET firmware, which runs in other EtherCAT states like PREOP/OP. Bootstrap tends to be buggier since it cannot be updated easily, so
   * Motion Master has certain workarounds to overcome these FoE differences and bugs.
   */
  getDeviceFileList(
    props: GetDeviceFileListRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFileListStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getDeviceFileList = MotionMasterMessage.Request.GetDeviceFileList.create({ ...props, deviceAddress });
        const id = this.sendRequest({ getDeviceFileList }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceFileList>(
            'deviceFileList',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get device file.
   *
   * Motion Master uses Filetransfer over EtherCAT (FoE) to read and send back the content of a file from the device flash memory.
   *
   * The IgH EtherCAT Master library used by Motion Master limits the file read buffer to 9KB, so any file written to flash that is larger than 9KB
   * needs to be split into parts of max 9KB. Motion Master does this automatically on request to {@link setDeviceFile}.
   *
   * Upon receiving this request, Motion Master will first read the list of files on a device, see {@link getDeviceFileList}.
   * It does that in order to determine if the requested file, for example, "SOMANET_CiA_402.xml," has been stored in multiple parts.
   * The parts are named like "SOMANET_CiA_402.xml.zip.part000," "SOMANET_CiA_402.xml.zip.part001," and so on.
   * Motion Master will read all parts of the requested file, unzip it, and send the content back to a client.
   * Since the number of items in the list of files is limited to 32 (based on the firmware version), Motion Master might not see the file,
   * but in any case, it will try to read the content of it.
   * If the file exists, it will return its content; otherwise, it will return the file not found error.
   *
   * Zipped files will be automatically unzipped, and their content returned; for example, "stack_image.svg.zip" can be stored
   * in one or multiple files depending on the size. No matter how it's stored, it will be unzipped, and its content returned as
   * text (SVG) instead of zipped content (binary).
   *
   * In BOOT EtherCAT state, it's only allowed to read the .hardware_description file. This was done because
   * the bootloader firmware would hang when trying to read a corrupted or missing file.
   *
   * If the requested file content is empty, Motion Master will return a not found error. This is subject to change now that Motion Master reads the file error codes.
   *
   * The ESI file ("SOMANET_CiA_402.xml" or "SOMANET_CiA_402.xml.zip") is never read from the device, see {@link getEsiFromIndexedDB}.
   */
  getDeviceFile(props: GetDeviceFileRequest, requestTimeout: number, messageId?: string): Observable<DeviceFileStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress: number) => {
        if (props.name === esiFilename || props.name === esiZipFilename) {
          return this.getEsiFromIndexedDB(deviceAddress, props.name, messageId);
        }
        return this.getDeviceFileUsingResolvedDeviceAddress(deviceAddress, props, requestTimeout, messageId);
      })
    );
  }

  /**
   * Returns the ESI file cached in IndexedDB under the firmware version the device reports in 0x100A:00.
   *
   * The ESI file is never read from the device. Firmware installation no longer writes it to the device (see
   * {@link installFirmwareAndCacheEsi}), and Motion Master does not remove a skipped file, so the file on the device may
   * belong to an older firmware. Neither the file nor its ESI revision tells which firmware it belongs to.
   * Caching it under the installed version would hide the correct ESI file for good.
   *
   * The ESI file gets into IndexedDB only from a firmware package: at firmware installation, see
   * {@link cacheEsiFromFirmwarePackage}, or when a user loads it from a package in OBLAC Drives.
   *
   * Returns a failed status with the NOT_FOUND error code when the ESI file is not cached for the installed firmware,
   * or when there is no IndexedDB, as outside the browser. Returns a failed status with the FAILED error code when the
   * firmware version or IndexedDB can't be read.
   */
  private getEsiFromIndexedDB(deviceAddress: number, name: string, messageId?: string): Observable<DeviceFileStatus> {
    const failed = (code: MotionMasterMessage.Status.DeviceFile.Error.Code, message: string): DeviceFileStatus => ({
      request: 'failed',
      name,
      deviceAddress,
      error: { code, message },
      messageId,
    });

    if (!(globalThis as { indexedDB?: IDBFactory }).indexedDB) {
      return of(
        failed(
          MotionMasterMessage.Status.DeviceFile.Error.Code.NOT_FOUND,
          'The ESI file is not read from the device, and there is no IndexedDB to read it from.'
        )
      );
    }

    return this.getParameterValue<string>(deviceAddress, 0x100a, 0x00).pipe(
      mergeMap((firmwareVersion: string) =>
        from(this.mmcidb.open().then(() => this.mmcidb.getEsi(firmwareVersion))).pipe(
          map((data: Uint8Array | null): DeviceFileStatus => {
            if (data) {
              return {
                request: 'succeeded',
                name,
                deviceAddress,
                content: data,
                success: {
                  code: MotionMasterMessage.Status.DeviceFile.Success.Code.UNSPECIFIED,
                  message: 'ESI file successfully retrieved from IndexedDB.',
                },
                messageId,
              };
            }
            return failed(
              MotionMasterMessage.Status.DeviceFile.Error.Code.NOT_FOUND,
              `The ESI file for firmware ${firmwareVersion} is not cached. The ESI file is not read from the device.`
            );
          })
        )
      ),
      catchError((err) => {
        console.error('Failed to retrieve the ESI file from IndexedDB:', err);
        const reason = err instanceof Error ? err.message : String(err);
        return of(
          failed(
            MotionMasterMessage.Status.DeviceFile.Error.Code.FAILED,
            `Failed to retrieve the ESI file from IndexedDB: ${reason}`
          )
        );
      })
    );
  }

  private getDeviceFileUsingResolvedDeviceAddress(
    deviceAddress: number,
    props: GetDeviceFileRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFileStatus> {
    const getDeviceFile = MotionMasterMessage.Request.GetDeviceFile.create({ ...props, deviceAddress });
    const id = this.sendRequest({ getDeviceFile }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceFile>('deviceFile', requestTimeout, id),
      mergeMap((status) => this.fixProductionScrewUp(status))
    );
  }

  /**
   * Fixing a production screw-up that affects a very important customer. Added on February 29th, 2024.
   * In addition to addressing the production issue, this function will return a default `.hardware_description` file
   * if it cannot be found on the device and `stack_info.json` is also missing.
   */
  protected async fixProductionScrewUp(status: DeviceFileStatus): Promise<DeviceFileStatus> {
    if (status.name === '.hardware_description') {
      if (status.content) {
        const text = decodeTextContent(status.content);
        if (text.includes('\\":')) {
          const replacedText = text.replace(/\\"/g, '"').replace(/"{/, '{').replace(/}"/, '}');
          const content = new TextEncoder().encode(replacedText);

          this.unlockProtectedFiles(status.deviceAddress!, 10000).subscribe({
            next: () => {
              this.setDeviceFile(
                { content, deviceAddress: status.deviceAddress, name: '.hardware_description', overwrite: true },
                10000
              ).subscribe({
                error(err) {
                  if (err instanceof Error) {
                    console.warn(`Failed to replace the invalid .hardware_description file. Error: ${err.message}`);
                  }
                },
              });
            },
            error: (err) => {
              if (err instanceof Error) {
                console.warn(`Failed to unlock protected files. Error: ${err.message}`);
              }
            },
          });

          status.content = content;
        }
      } else {
        if (status.deviceAddress) {
          const stackInfoJson = await lastValueFrom(this.getFile(status.deviceAddress, 'stack_info.json'));
          if (!stackInfoJson) {
            const hw: HardwareDescription = JSON.parse(JSON.stringify(dummyHardwareDescriptionFileContentTemplate));
            hw.device.serialNumber = makeDeviceSerialNumberFromAddress(status.deviceAddress);
            const content = new TextEncoder().encode(JSON.stringify(hw));
            status.content = content;
            // @ts-ignore
            status['data'] = 'success';
            status.error = null;
            status.request = 'succeeded';
            status.success = {};
          }
        }
      }
    }
    return status;
  }

  /**
   * Set device file.
   *
   * Motion Master uses Filetransfer over EtherCAT (FoE) to write files to the device flash memory.
   *
   * The IgH EtherCAT Master library used by Motion Master limits the file read buffer to 9KB, so any file written to flash that is larger than 9KB
   * needs to be split into parts of max 9KB. Motion Master does this automatically by first zipping the content and then writing the parts.
   * The parts are named in the following manner: "SOMANET_CiA_402.xml.zip.part000," "SOMANET_CiA_402.xml.zip.part001," and so on.
   */
  setDeviceFile(props: SetDeviceFileRequest, requestTimeout: number, messageId?: string): Observable<DeviceFileStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setDeviceFile = MotionMasterMessage.Request.SetDeviceFile.create({ ...props, deviceAddress });
        const id = this.sendRequest({ setDeviceFile }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceFile>('deviceFile', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Delete device file.
   *
   * Motion Master uses Filetransfer over EtherCAT (FoE) to delete files from the device flash memory.
   *
   * If the file to delete is written in parts, Motion Master will ensure that all parts are deleted.
   * It does this by reading the list of files on a device, see {@link getDeviceFileList}, removing all parts from that list, and then
   * re-reading the list again to ensure that there are no leftovers if there were more than 32 files in the list. If there are still
   * more than 32 files in the list, Motion Master will blindly try to make sure there are no file parts left in the memory (may or may not work).
   */
  deleteDeviceFile(
    props: DeleteDeviceFileRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFileStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const deleteDeviceFile = MotionMasterMessage.Request.DeleteDeviceFile.create({ ...props, deviceAddress });
        const id = this.sendRequest({ deleteDeviceFile }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceFile>('deviceFile', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Reset device fault.
   *
   * When an error occurs on a device, the device will go into the Fault reaction active state (transition 13) and then automatically to the Fault state (transition 14).
   * The CiA402 state of a device can be derived from the value of the Statusword object (0x6041:00).
   * In order to exit the Fault state and enter the Switch on disabled state (transition 15), the master must send a Fault reset command via the Controlword object (0x6040:00).
   *
   * When this request is sent to Motion Master, it will attempt to reset the fault. Resetting the fault can either succeed or fail.
   * While resetting the fault, Motion Master will use the current Controlword value and set the Reset fault bit to 1.
   * However, upon completion, whether successful or encountering an error, Motion Master will set it back to 0.
   *
   * Motion Master will return a "No Fault" warning when this request is sent, and the device is not in the Fault or Fault reaction active state.
   * If this request is sent when the device is in the Fault reaction active state, Motion Master will
   * wait for the automatic transition (14) to the Fault state before attempting to reset the fault.
   * When Motion Master sets the Controlword Fault bit to 1, it will also periodically check if the device is no longer in the Fault state.
   * After a few seconds, if the device is still in the Fault state, Motion Master will give up and return a timeout error.
   */
  resetDeviceFault(
    props: ResetDeviceFaultRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFaultResetStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const resetDeviceFault = MotionMasterMessage.Request.ResetDeviceFault.create({ ...props, deviceAddress });
        const id = this.sendRequest({ resetDeviceFault }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IDeviceFaultReset>(
            'deviceFaultReset',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Stop device.
   *
   * Motion Master will use the current value of the Controlword object and set the command on it to Quick stop.
   * It will then wait for up to 2 seconds for a device to enter one of the following CiA402 states: Switch on disabled, Operation enabled, Fault.
   * This is achieved by periodically checking the value of the Statusword object. If the device fails to enter one of those states within the specified time,
   * Motion Master will return a timeout or a quick stop failed error.
   */
  stopDevice(props: StopDeviceRequest, requestTimeout: number, messageId?: string): Observable<DeviceStopStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const stopDevice = MotionMasterMessage.Request.StopDevice.create({ ...props, deviceAddress });
        const id = this.sendRequest({ stopDevice }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceStop>('deviceStop', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Start device firmware installation.
   *
   * When Motion Master receives this request for a device, it will perform the following steps:
   * - Switch the device to the BOOT EtherCAT state; the device will then run the bootloader instead of the firmware.
   * - Validate the firmware package.
   * - Delete files found in the package from the device flash memory (SOMANET_CiA_402.xml and stack_image.svg).
   * - Write files other than the firmware binaries and SII from the package to the device (SOMANET_CiA_402.xml and stack_image.svg).
   * - (Optional) Install the SII file using the IgH EtherCAT Master library write SII function, or SOEM EEPROM tool.
   * - Write the firmware binaries to the device using predefined names like app_firmware.bin and com_firmware.bin.
   *
   * During the firmware installation, Motion Master will send progress messages back to clients.
   * If Motion Master encounters a failure in any step other than SII write, it will send an error. If SII write fails, only a warning will be sent.
   */
  startDeviceFirmwareInstallation(
    props: StartDeviceFirmwareInstallationRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFirmwareInstallationStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startDeviceFirmwareInstallation = MotionMasterMessage.Request.StartDeviceFirmwareInstallation.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startDeviceFirmwareInstallation }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceFirmwareInstallation>(
            'deviceFirmwareInstallation',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Extracts the ESI file (SOMANET_CiA_402.xml) from a firmware package and stores its unzipped content in
   * IndexedDB, keyed by the given firmware version.
   *
   * IndexedDB is the only source of the ESI file: {@link getDeviceFile} never reads it from the drive. This is what
   * allows the ESI file (SOMANET_CiA_402.xml.zip) to be skipped during firmware installation, which avoids
   * slow and unreliable FoE transfers and works around devices (e.g. NetX) that reject writing that file.
   *
   * It is a no-op when run outside the browser, when IndexedDB is unavailable, or when the package does not
   * contain an ESI file (for example, an empty firmware package).
   *
   * @param firmwareVersion - The firmware version to key the ESI by; this must match the value reported by the
   *                          device parameter 0x100a:00 after installation, which {@link getDeviceFile} uses.
   * @param firmwarePackageContent - The firmware package ZIP archive content.
   * @returns `true` if an ESI was extracted and stored, `false` if it was a no-op (no IndexedDB or no ESI).
   */
  async cacheEsiFromFirmwarePackage(firmwareVersion: string, firmwarePackageContent: Uint8Array): Promise<boolean> {
    if (!(globalThis as { indexedDB?: IDBFactory }).indexedDB) {
      return false;
    }

    const esi = extractEsiFromFirmwarePackage(firmwarePackageContent);
    if (!esi) {
      return false;
    }

    await this.mmcidb.open();
    await this.mmcidb.putEsi(firmwareVersion, esi);
    return true;
  }

  /**
   * Installs a firmware package while keeping ESI handling local, by wrapping {@link startDeviceFirmwareInstallation}
   * without changing its behavior. Compared to a plain installation, this:
   * - Defaults to skipping the ESI file (SOMANET_CiA_402.xml.zip) so it is not written to the drive, which avoids
   *   slow and unreliable FoE transfers and works around devices (e.g. NetX) that reject writing that file. This is
   *   user-overridable: when `props.skipFiles` is provided it is passed through unchanged, so a caller can choose to
   *   still write the ESI by omitting it from that list.
   * - Extracts the ESI from the package and stores it in IndexedDB keyed by firmware version (see
   *   {@link cacheEsiFromFirmwarePackage}) *before* the installation starts, so {@link getDeviceFile} can return it
   *   as soon as the installation ends. IndexedDB is the only source of the ESI file: {@link getDeviceFile} never
   *   reads it from the drive, even when the ESI is also written there. A caching failure is logged but does not
   *   prevent installation; the ESI file is then not found until a user loads it from the package (Load ESI in
   *   OBLAC Drives).
   *
   * @param firmwareVersion - The firmware version to key the cached ESI by; this must match the value reported by
   *                          the device parameter 0x100a:00 after installation, which {@link getDeviceFile} uses.
   *                          For OBLAC firmware packages this is the version encoded in the package filename, see
   *                          {@link extractFirmwareVersionFromFirmwarePackageFilename}. When it cannot be determined
   *                          (e.g. an arbitrary package with an unrecognized filename), pass an empty value to skip
   *                          caching; the ESI file is then not found unless it was cached before.
   */
  installFirmwareAndCacheEsi(
    firmwareVersion: string | undefined,
    props: StartDeviceFirmwareInstallationRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceFirmwareInstallationStatus> {
    // Skip the ESI by default, but respect an explicitly provided skipFiles list so the caller can override it.
    const skipFiles = props.skipFiles ?? [esiZipFilename];

    // Cache the ESI before installation starts: with it skipped on the drive, it must already be in IndexedDB
    // by the time Motion Master reads it back after installation. A caching failure must not block the install.
    const cacheEsi =
      firmwareVersion && props.firmwarePackageContent
        ? this.cacheEsiFromFirmwarePackage(firmwareVersion, props.firmwarePackageContent)
        : Promise.resolve();

    return from(
      cacheEsi.catch((err) => {
        console.error('Failed to cache ESI from firmware package:', err);
      })
    ).pipe(mergeMap(() => this.startDeviceFirmwareInstallation({ ...props, skipFiles }, requestTimeout, messageId)));
  }

  /**
   * Get device log.
   *
   * The device log is stored in two files due to the IgH EtherCAT Master library 9KB file size limitation, as explained in {@link getDeviceFile}.
   * The device automatically writes the log to logging_curr.log and copies that file to logging_prev.log once it reaches 9KB.
   * When Motion Master receives this request, it will read both of these files and return their combined contents.
   */
  getDeviceLog(props: GetDeviceLogRequest, requestTimeout: number, messageId?: string): Observable<DeviceLogStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getDeviceLog = MotionMasterMessage.Request.GetDeviceLog.create({ ...props, deviceAddress });
        const id = this.sendRequest({ getDeviceLog }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IDeviceLog>('deviceLog', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Start cogging torque recording.
   *
   * Cogging torque compensation (CTC) recording can be initiated with or without auto-config.
   * In the proto file, skipping the auto-config is currently named skip_auto_tuning. The field name is deprecated and will be renamed.
   *
   * If CTC recording is started without auto-config, users must ensure that the encoder on the motor shaft is used for all functions: commutation, position, and velocity.
   * If this is not the case, Motion Master will return an invalid encoder configuration error.
   * Recording can also fail if the position controller is not tuned.
   *
   * If CTC recording is started with auto-config, Motion Master will:
   * - Change the encoder configuration to have all encoder functions on the motor shaft.
   * - Disable CTC.
   * - Change the velocity feed forward value.
   * - Change the position and velocity filter types and cutoff frequencies.
   * - Run iterative sharpening position controller auto-tuning to compute the optimal PID values (gains) for CTC recording.
   *
   * All parameter values that Motion Master changes as part of the auto-config are first stored in memory and once the CTC recording is done, they are reverted back.
   *
   * CTC recording is initiated by changing the Modes of operation (0x6060) parameter value to -1 (Cogging compensation recording mode).
   * During the operation, Motion Master will monitor the Cogging torque compensation state parameter (0x2008:01) to know when the recording is in progress and when it's done.
   *
   * The result of CTC recording is the cogging_torque.bin file written to the device flash memory. The size of this file is 2048 bytes.
   * Each pair of bytes represents a 16-bit signed integer value in mNm (millinewton-meter).
   *
   * This request will turn the motor.
   */
  startCoggingTorqueRecording(
    props: StartCoggingTorqueRecordingRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<CoggingTorqueRecordingStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startCoggingTorqueRecording = MotionMasterMessage.Request.StartCoggingTorqueRecording.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startCoggingTorqueRecording }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.ICoggingTorqueRecording>(
            'coggingTorqueRecording',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get cogging torque data.
   *
   * The cogging_torque.bin file is the result of cogging torque compensation recording, as explained in {@link startCoggingTorqueRecording}.
   * The size of this file is 2048 bytes, where each pair of bytes represents a 16-bit signed integer value in mNm (millinewton-meter).
   *
   * Motion Master will read the cogging_torque.bin file, convert the bytes to 16-bit signed integer values, and return them as an array of numbers.
   */
  getCoggingTorqueData(
    props: GetCoggingTorqueDataRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<CoggingTorqueDataStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getCoggingTorqueData = MotionMasterMessage.Request.GetCoggingTorqueData.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getCoggingTorqueData }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.ICoggingTorqueData>(
            'coggingTorqueData',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start offset detection.
   *
   * Running offset detection differs for firmwares <v5 and >=v5.
   *
   * For devices running firmware >=v5, Motion Master will execute multiple OS commands.
   * Before initiating any command, the device is switched to Modes of operation -2 (Diagnostics mode), and
   * it's put into the operation-enabled CiA402 state. The OS commands are then executed in the following order:
   * - Open phase detection
   * - Phase resistance measurement
   * - Phase inductance measurement
   * - Pole pair detection
   * - Motor phase order detection
   * - Commutation offset measurement
   *
   * Motion Master will return an error if any of the following OS commands fail: open phase detection,
   * phase order detection, commutation offset measurement.
   * If other OS commands fail, Motion Master will continue with the procedure and only return a warning.
   *
   * Once the commutation offset measurement command completes, it will update the following parameter values:
   * - 0x2001:00 Commutation angle offset to the computed offset value
   * - 0x2009:01 Commutation offset State to 2 (OFFSET_VALID)
   * - 0x2003:05 Motor phases inverted will be changed by the firmware
   *
   * For devices running firmware <5, the same Modes of operation -2 is used, but it's named Commutation offset detection mode,
   * and it will run as a single procedure as soon as the device is switched to CiA402 operation-enabled state.
   * The end result is the same; the procedure will update 0x2001:00 and 0x2009:01 parameter values.
   *
   * This request will turn the motor.
   */
  startOffsetDetection(
    props: StartOffsetDetectionRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<OffsetDetectionStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startOffsetDetection = MotionMasterMessage.Request.StartOffsetDetection.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startOffsetDetection }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IOffsetDetection>(
            'offsetDetection',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * @deprecated use {@link startSystemIdentification} instead
   */
  startPlantIdentification(
    props: StartPlantModelIdentificationRequest & DeviceRefObj,
    requestTimeout: number,
    messageId?: string
  ): Observable<PlantIdentificationStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startPlantIdentification = MotionMasterMessage.Request.StartPlantIdentification.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startPlantIdentification }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IPlantIdentification>(
            'plantIdentification',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Compute auto-tuning gains.
   *
   * Auto-tuning gains can be computed for the position or the velocity controller.
   *
   * The prerequisites for running this procedure are a configured motor and the existence of
   * the plant_model.csv file created by previously running the system identification {@link startSystemIdentification}.
   * Motion Master will return an error if the plant_model.csv file is not found.
   *
   * In OBLAC Drives, this request is called when the tuning sliders (damping ratio, bandwidth) are moved and
   * during the CTC recording {@link startCoggingTorqueRecording} when auto-config is not skipped.
   *
   * Motion Master will compute the PID gains using a proprietary algorithm and update the Kp, Ki, Kd values of the 0x2011 and 0x2012 parameters.
   */
  computeAutoTuningGains(
    props: ComputeAutoTuningGainsRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<AutoTuningStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const computeAutoTuningGains = MotionMasterMessage.Request.ComputeAutoTuningGains.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ computeAutoTuningGains }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IAutoTuning>('autoTuning', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Set motion controller parameters.
   *
   * This request sets the target value for a device on Motion Master.
   *
   * A previous call to {@link enableMotionController} will set the mode of operation to CSP, CSV, or CST and optionally enable filtering.
   */
  setMotionControllerParameters(
    props: SetMotionControllerParametersRequest,
    messageId?: string
  ): Observable<RequestStatusMessage> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setMotionControllerParameters = MotionMasterMessage.Request.SetMotionControllerParameters.create({
          ...props,
          deviceAddress,
        });
        this.sendRequest({ setMotionControllerParameters }, messageId);
        return of({ request: 'succeeded' } as RequestStatusMessage);
      })
    );
  }

  /**
   * Enable motion controller.
   *
   * This request will set the mode of operation to CSP, CSV, or CST and optionally enable filtering.
   *
   * If filtering is enabled, Motion Master will compute an intermediate target value in each cycle, and depending on the active mode of operation,
   * set the value of one of 0x607A Target position, 0x60FF Target velocity, 0x6071 Target torque to that value.
   *
   * The target value can be updated with {@link setMotionControllerParameters}.
   */
  enableMotionController(
    props: EnableMotionControllerRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<MotionControllerStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const enableMotionController = MotionMasterMessage.Request.EnableMotionController.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ enableMotionController }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.MotionController>(
            'motionController',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Disable motion controller.
   *
   * Depending on the current CiA402 state, this request will do the following:
   * - If the device is in Quick stop active, it will transition to Switch on disabled state.
   * - If the device is in Operation enabled, it will transition to Switched on state.
   */
  disableMotionController(
    props: MotionMasterMessage.Request.IDisableMotionController & DeviceRefObj,
    requestTimeout: number,
    messageId?: string
  ): Observable<MotionControllerStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const disableMotionController = MotionMasterMessage.Request.DisableMotionController.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ disableMotionController }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.MotionController>(
            'motionController',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Set signal generator parameters.
   *
   * This request will update the signal generator parameter values for a device on Motion Master.
   * The selected signal generator is then started by {@link startSignalGenerator}.
   * The signal generator must be started within the next 60 seconds before Motion Master invalidates the previously set parameters.
   */
  setSignalGeneratorParameters(
    props: SetSignalGeneratorParametersRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<SignalGeneratorStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setSignalGeneratorParameters = MotionMasterMessage.Request.SetSignalGeneratorParameters.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ setSignalGeneratorParameters }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SignalGenerator>(
            'signalGenerator',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start signal generator.
   *
   * This request will start one of the signal generators based on the
   * previously set parameters with {@link setSignalGeneratorParameters}.
   *
   * Signal generators are started by changing the mode of operation, putting the device into Operation enabled CiA402 state,
   * and setting the target value. The mode of operation set depends on the type of signal generator and the selected controller:
   * - For the simple & advanced step response and the sine wave, one of CSP, CSV, or CST is set.
   * - For the ramp, trapezoidal, and bidirectional signal generators, also called profiles, one of the PP, PV, TQ is set.
   *
   * Currently, the only profile type that the firmware supports is the ramp profile.
   * Motion Master uses this profile type to construct and run the following signals: ramp, trapezoidal, and bidirectional.
   * Sine wave is also a profile, but it is currently being generated on Motion Master alone.
   * Firmwares >=5.2 will support generating and running sine wave profiles on devices themselves. This will enable lossless profile
   * execution on possibly higher frequencies. Future Motion Master versions will switch to this method.
   *
   * For the simple step response, Motion Master will only set the target value, wait for the specified holding duration,
   * and then send a Quick Stop command.
   *
   * The advanced step response is similar to the simple one, but, after the holding duration, it will set the new target
   * multiple times depending on the shape, and then send a Quick Stop if the repeat option is set to NO. If repeat is set to YES,
   * then the signal generator will run indefinitely, and the users must stop it manually.
   *
   * Before running a profile, Motion Master will check if the device supports the profile modes by looking at 0x6502 Supported drive modes.
   * If profile modes are not supported, then the profiles will be generated on Motion Master. This means the targets will be computed upfront.
   * If profiles are supported, then the ramp, trapezoidal, and bidirectional signal generators are run in profile mode of operation.
   * To prepare the signal generator for execution, Motion Master will set the following parameters:
   * - 0x6081:00 Profile velocity (only for the position controller)
   * - 0x6083:00 Profile acceleration
   * - 0x6084:00 Profile deceleration
   * Motion Master knows when the profile has completed by comparing the demand with the actual value, taking into account the
   * holding duration and the shape of the profile. Once the profile is considered complete, Motion Master will send a Quick Stop, but
   * only if repeat is set to NO.
   *
   * The sine wave profile is generated upfront on Motion Master. Targets are computed for each master cycle (millisecond).
   * Once the last computed target has been set, Motion Master will send a Quick Stop if repeat is set to NO.
   *
   * For all signal generators, Motion Master will check the requested target against the currently set software position limits
   * and, if necessary, alter it while returning a warning.
   *
   * This request will turn the motor.
   */
  startSignalGenerator(
    props: MotionMasterMessage.Request.IStartSignalGenerator & DeviceRefObj,
    requestTimeout: number,
    messageId?: string
  ): Observable<SignalGeneratorStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startSignalGenerator = MotionMasterMessage.Request.StartSignalGenerator.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startSignalGenerator }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SignalGenerator>(
            'signalGenerator',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Stop signal generator.
   *
   * This request will send a Quick Stop command to the device.
   */
  stopSignalGenerator(
    props: StopSignalGeneratorRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<SignalGeneratorStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const stopSignalGenerator = MotionMasterMessage.Request.StopSignalGenerator.create({ ...props, deviceAddress });
        const id = this.sendRequest({ stopSignalGenerator }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SignalGenerator>(
            'signalGenerator',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start monitoring device parameter values.
   *
   * When monitoring is started, Motion Master will begin and continue reading values from the device
   * and send them back to a client at the specified interval on the given topic.
   *
   * The request is made through the req/res socket {@link MotionMasterReqResSocket},
   * but the status messages will arrive on the pub/sub socket {@link MotionMasterPubSubSocket}.
   *
   * The same monitoring can read both PDO and SDO values. PDO values will be updated on every Motion Master cycle (1ms).
   * How fast SDO values will update depends on the number of SDOs being monitored. Motion Master reads the SDO values in a sequence,
   * so, for example, if there are 10 SDOs and it takes 20ms to read a single SDO, it will take approximately 200ms for each SDO to get updated.
   * IgH EtherCAT Master supports non-blocking SDO reading with a request to read and check the state of the request (one of not used, busy, failed, succeeded).
   * A future version of Motion Master will likely leverage this and update SDO values out of order as each of the requests to update SDO succeeds.
   * This should decrease the time it takes to read multiple SDOs.
   * If a parameter is required to be updated more frequently, it should be mapped as a PDO.
   *
   * Firmware versions >=5.2 support SDO complete access, but it is currently not being supported in Motion Master.
   * With SDO complete access, RECORD and ARRAY subitems can be read with a single request.
   *
   * The interval is given in microseconds.
   *
   * The topic should be unique. The previous monitoring will stop if the given topic is reused.
   * Clients can easily stop one another's monitoring by using the same topic.
   * Prefixing topics with the client id or some other unique identifier is suggested.
   *
   * When the monitoring request is received, Motion Master will mark all requested parameters as stale.
   * The first time a parameter gets refreshed from a device, it'll be marked as not stale.
   * Motion Master won't start publishing parameter values until all parameters are marked as not stale.
   * This was done to satisfy the firmware test client applications.
   *
   * Stopping the ongoing monitoring is done through the req/res socket using
   * the request message ID of the initial start monitoring message {@link stopMonitoringDeviceParameterValues}.
   */
  startMonitoringDeviceParameterValues(
    props: StartMonitoringDeviceParameterValuesRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<MonitoringParameterValuesStatus> {
    const startMonitoringDeviceParameterValues =
      MotionMasterMessage.Request.StartMonitoringDeviceParameterValues.create(props);
    const id = this.sendRequest({ startMonitoringDeviceParameterValues }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.MonitoringParameterValues>(
        'monitoringParameterValues',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Stop monitoring device parameter values.
   *
   * The Message ID used for {@link startMonitoringDeviceParameterValues} uniquely identifies a monitoring and is used for stopping it.
   */
  stopMonitoringDeviceParameterValues(
    props: StopMonitoringDeviceParameterValuesRequest,
    messageId?: string
  ): Observable<RequestStatusMessage> {
    const stopMonitoringDeviceParameterValues =
      MotionMasterMessage.Request.StopMonitoringDeviceParameterValues.create(props);
    this.sendRequest({ stopMonitoringDeviceParameterValues }, messageId);
    return of({ request: 'succeeded' } as RequestStatusMessage);
  }

  /**
   * Get the EtherCAT network state.
   *
   * Returns one of the EtherCAT network states: BOOT, INIT, PRE-OP, SAFE-OP, OP.
   * The state is returned as an enum value: INIT: 1, PREOP: 2, BOOT: 3, SAFEOP: 4, OP: 5.
   */
  getEthercatNetworkState(
    props: GetEthercatNetworkStateRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<EthercatNetworkStateStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getEthercatNetworkState = MotionMasterMessage.Request.GetEthercatNetworkState.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getEthercatNetworkState }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IEthercatNetworkState>(
            'ethercatNetworkState',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Set the EtherCAT network state.
   *
   * Set the EtherCAT network state to one of: BOOT, INIT, PRE-OP, SAFE-OP, OP.
   * The state is set by an enum value: INIT: 1, PREOP: 2, BOOT: 3, SAFEOP: 4, OP: 5.
   *
   * When switching from PRE-OP to OP, MM will reinitialize slaves to update the PDO mapping.
   */
  setEthercatNetworkState(
    props: SetEthercatNetworkStateRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<EthercatNetworkStateStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setEthercatNetworkState = MotionMasterMessage.Request.SetEthercatNetworkState.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ setEthercatNetworkState }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.IEthercatNetworkState>(
            'ethercatNetworkState',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * @deprecated use {@link startCirculoEncoderNarrowAngleCalibrationProcedure} instead
   */
  startNarrowAngleCalibration(
    props: StartNarrowAngleCalibrationRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<NarrowAngleCalibrationStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startNarrowAngleCalibration = MotionMasterMessage.Request.StartNarrowAngleCalibration.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startNarrowAngleCalibration }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.INarrowAngleCalibration>(
            'narrowAngleCalibration',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Set system client timeout.
   *
   * This request will update the client timeout.
   *
   * The client timeout specifies how long Motion Master will wait for the client to send a ping or any other message before
   * considering it gone and clearing the resources (stopping procedures and monitorings) related to that client.
   *
   * The default client timeout is 1000ms.
   *
   * Clients will typically change this once the socket connection is established.
   */
  setSystemClientTimeout(props: SetSystemClientTimeoutRequest, messageId?: string): Observable<RequestStatusMessage> {
    const setSystemClientTimeout = MotionMasterMessage.Request.SetSystemClientTimeout.create(props);
    this.sendRequest({ setSystemClientTimeout }, messageId);
    return of({ request: 'succeeded' });
  }

  /**
   * Start system identification.
   *
   * This request will run the system identification procedure, which identifies the plant model.
   *
   * Pre-conditions: a configured motor and configured encoders.
   *
   * The chirp signal that the procedure generates only uses the torque controller.
   * The chirp signal is generated on Motion Master, and then, on every master cycle (1ms), the target torque value is set on a device.
   * Motion Master will send a Quick Stop to a device after the last target value is set.
   *
   * The request can fail for multiple reasons: the device is in the fault state, cannot set the op mode state,
   * or the system identification fails because not enough data points have been collected.
   * While running the chirp signal, the data points are collected, but due to potential communication lag, it can happen
   * that the master doesn't collect enough data points. If more than 80% of data points are missing, the procedure will fail.
   * If between 20%-80% of data points are missing, only a warning will be returned. The exact thresholds are subject to change.
   *
   * Before computing the plant model data based on the collected data points, Motion Master will interpolate the missing data points.
   * Plant model data is then computed and written to the plant_model.csv file on the device flash memory.
   * The plant model file is later used to compute the PID values using {@link computeAutoTuningGains} and {@link startFullAutoTuning}.
   * It's also used indirectly in {@link startCoggingTorqueRecording} when auto-config is turned on.
   * Motion Master will also update the integral limit values in 0x2011: Velocity controller and 0x2012: Position controller during this procedure.
   *
   * In future versions of the firmware, the chirp signal would be executed on the devices themselves, so no data points will be missing,
   * and the system identification will be able to run at higher frequencies and with higher precision.
   *
   * This request will turn the motor.
   */
  startSystemIdentification(
    props: StartSystemIdentificationRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<SystemIdentificationStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startSystemIdentification = MotionMasterMessage.Request.StartSystemIdentification.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startSystemIdentification }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.ISystemIdentification>(
            'systemIdentification',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get Circulo encoder magnet distance.
   *
   * This feature is only available for firmwares >=v5. For older firmwares, Motion Master will return an error.
   *
   * Motion Master will read registers 0x2B and 0x2F on the specified encoder (by encoder ordinal), and then it will compute the magnet distance.
   * The computation depends on the Circulo type, encoder port, and ring revision.
   */
  getCirculoEncoderMagnetDistance(
    props: MotionMasterMessage.Request.IGetCirculoEncoderMagnetDistance & DeviceRefObj,
    requestTimeout: number,
    messageId?: string
  ): Observable<CirculoEncoderMagnetDistanceStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getCirculoEncoderMagnetDistance = MotionMasterMessage.Request.GetCirculoEncoderMagnetDistance.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getCirculoEncoderMagnetDistance }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.ICirculoEncoderMagnetDistance>(
            'circuloEncoderMagnetDistance',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start the Circulo encoder narrow-angle calibration procedure.
   *
   * This feature is only available on firmwares >=v5.
   *
   * Before starting the procedure, Motion Master will check and return an error if:
   * - the firmware version is not >=v5
   * - software position limits are set so that the required motor rotation is not possible
   * - for SMM devices, the encoder source type is not set to None
   * - the velocity resolution determined by the gear ratio, SI unit velocity, and feed constant is not high enough
   *
   * Before starting the procedure, Motion Master will do the following:
   * - send OS command 14 (Ignore BiSS status bits) so that no firmware warnings or errors are raised during the procedure.
   * - set certain encoder registers to their default values.
   * - set the BiSS encoder to raw mode.
   * - change the values of some device parameters: profile acceleration, deceleration, max motor speed, etc.
   *
   * The parameter values Motion Master sets depend on the type of the device (fetched from the hardware description file).
   * Once the operation has completed, Motion Master will roll back all of the above parameters to their initial values.
   *
   * Procedure:
   * - The procedure is run in velocity profile mode of operation.
   * - The procedure will do multiple iterations until the encoder data is satisfactory.
   * - The procedure can fail for various reasons: data out of range, motor too slow, bad data, internal error, etc.
   * - The procedure will run a maximum of 13 calibration iterations, up to 2 mins in total.
   * - One iteration turns the motor in one direction, and then the next one reverses the direction.
   * - Each iteration will record encoder data into HRD files (4kHz) that contain the raw data from the specified encoder.
   * - Motion Master will remove previous high-resolution data (HRD) files after each iteration. HRD streaming is configured/started using OS commands.
   * - After each iteration, which lasts ~6 seconds, the recorded data in HRD files is read and passed to the iC House library.
   * - The recorded data is sliced into 32-bit pairs of values of master and nonius track data. Motion Master creates two arrays out of this data and
   * passes that to the iC House library which computes the new calibration parameters depending on encoder type sine and cosine offset, gain, etc.
   * - The iterations are being repeated until the encoder (raw) data satisfies a certain threshold.
   *
   * The result of a successful calibration is setting multiple parameters into registers of the specified encoder.
   *
   * At the end of the procedure Motion Master will rotate the motor back to the starting position and run commutation offset detection.
   * There will also be a final iteration that only measures the values of a (now) calibrated encoder for display.
   *
   * Every progress status carries `maxPermissiblePhaseError`, the limit the calibration library measures the ring
   * against. Motion Master 5.8.3 and later report it. An older Motion Master reports 0, and this method fills in
   * the limit derived from the product and the external encoder type instead. That limit is only right for the
   * Circulo 7 and Circulo 9 rings. Score a progress status with `computeCirculoEncoderCalibrationProgressResult`.
   *
   * This request will turn the motor.
   */
  startCirculoEncoderNarrowAngleCalibrationProcedure(
    props: StartCirculoEncoderNarrowAngleCalibrationProcedureRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<CirculoEncoderNarrowAngleCalibrationProcedureStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startCirculoEncoderNarrowAngleCalibrationProcedure =
          MotionMasterMessage.Request.StartCirculoEncoderNarrowAngleCalibrationProcedure.create({
            ...props,
            deviceAddress,
          });
        const id = this.sendRequest({ startCirculoEncoderNarrowAngleCalibrationProcedure }, messageId);

        // Resolved at most once, and only when a progress status reports no limit
        const productMaxPermissiblePhaseError$ = defer(() => this.resolveDevice(props)).pipe(
          map((device) =>
            getCirculoEncoderMaxPermissiblePhaseError(
              device.hardwareDescription?.device.serialNumber ? device.hardwareDescription : device.serialNumber,
              props.externalEncoderType ?? CirculoExternalEncoderType.NONE
            )
          ),
          shareReplay(1)
        );

        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure>(
            'circuloEncoderNarrowAngleCalibrationProcedure',
            requestTimeout,
            id
          ),
          concatMap((status) => {
            const progress = status.progress;

            if (!progress || (progress.maxPermissiblePhaseError ?? 0) > 0) {
              return of(status);
            }

            return productMaxPermissiblePhaseError$.pipe(
              map((maxPermissiblePhaseError) => ({ ...status, progress: { ...progress, maxPermissiblePhaseError } }))
            );
          })
        );
      })
    );
  }

  /**
   * Get device CiA402 state.
   */
  getDeviceCia402State(
    props: GetDeviceCiA402StateRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceCiA402StateStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const getDeviceCia402State = MotionMasterMessage.Request.GetDeviceCiA402State.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ getDeviceCia402State }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceCiA402State>(
            'deviceCia402State',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Set device CiA402 state.
   */
  setDeviceCia402State(
    props: SetDeviceCiA402StateRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceCiA402StateStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const setDeviceCia402State = MotionMasterMessage.Request.SetDeviceCiA402State.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ setDeviceCia402State }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceCiA402State>(
            'deviceCia402State',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Get system log.
   *
   * The system log represents the collected standard output from the Motion Master process.
   *
   * This request will return the entire system log, which can be a maximum of approximately 2MB.
   *
   * The log level can be set as an environmental variable when the Motion Master process is started.
   * The log level can be selected in the OBLAC Drives Update Service expert mode before installing a release.
   * The supported log levels are:
   * - 6: Request content
   * - 5: Request type
   * - 4: Parameter value
   * - 3: Monitoring
   * - 2: Verbose 2
   * - 1: Verbose 1
   * - 0: Info
   * - 1: Warnings
   * - 2: Errors
   * - 3: Fatal errors
   */
  getSystemLog(requestTimeout: number, messageId?: string): Observable<SystemLogStatus> {
    const getSystemLog = MotionMasterMessage.Request.GetSystemVersion.create();
    const id = this.sendRequest({ getSystemLog }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.SystemLog>('systemLog', requestTimeout, id)
    );
  }

  /**
   * Start device SII restore.
   *
   * This request uses the EEPROM tool from SOEM to overwrite the SII portion of the EEPROM on a device.
   *
   * Motion Master will verify the provided SII file before writing it to EEPROM. It will:
   * - Check the file content length,
   * - Compute CRC from a part of the SII file content and compare it to the CRC inside the file,
   * - Check the SII category types in the category header.
   *
   * Unlike IgH EtherCAT Master, which identifies interfaces by MAC address, the SOEM tool requires an adapter name.
   * Before calling the SOEM tool, Motion Master will find the adapter name by looking into /sys/class/net.
   *
   * The SOEM tool binary is delivered as a part of the Motion Master Docker image.
   */
  startDeviceSiiRestore(
    props: StartDeviceSiiRestoreRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<DeviceSiiRestoreStatus> {
    const startDeviceSiiRestore = MotionMasterMessage.Request.StartDeviceSiiRestore.create(props);
    const id = this.sendRequest({ startDeviceSiiRestore }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceSiiRestore>(
        'deviceSiiRestore',
        requestTimeout,
        id
      )
    );
  }

  /**
   * Start open-loop field control.
   *
   * The precondition for running the open-loop field control is to have a configured motor.
   * No encoder configuration or offset detection is required.
   *
   * Open-loop field control is started by changing the Modes of operation (0x6060) parameter value to -3 (Open-loop field mode) and
   * setting the CiA402 state to Ready to switch on.
   * After that, Motion Master will run several OS commands that configure the open-loop field control parameters:
   * - Start angle [milliradian]
   * - End angle [milliradian]
   * - Max rotational speed [radian/s]
   * - Rotational acceleration [radian/s^2]
   * - Start length [permille of rated torque]
   * - End length [permille of rated torque]
   * - Length speed [permille of rated torque]
   *
   * After the configuration, Motion Master will switch the device to Operation enabled CiA402 state, and the profile will start.
   *
   * While the open-loop field control is running, Motion Master will monitor the target reached bit in the statusword.
   * When the target reached bit becomes 1, Motion Master will stop the procedure by changing the CiA402 state to Switched on.
   *
   * Motion Master will return an error if the device goes out of the Operation enabled state before the target reached bit becomes 1.
   * There is no timeout on the Motion Master side.
   *
   * This request will turn the motor.
   */
  startOpenLoopFieldControl(
    props: StartOpenLoopFieldControlRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<OpenLoopFieldControlStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startOpenLoopFieldControl = MotionMasterMessage.Request.StartOpenLoopFieldControl.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startOpenLoopFieldControl }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.OpenLoopFieldControl>(
            'openLoopFieldControl',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * @deprecated use {@link startFullAutoTuning} instead
   */
  computeFullAutoTuningGains(
    props: ComputeFullAutoTuningGainsRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<FullAutoTuningStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const computeFullAutoTuningGains = MotionMasterMessage.Request.ComputeFullAutoTuningGains.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ computeFullAutoTuningGains }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.FullAutoTuning>(
            'fullAutoTuning',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start full auto-tuning.
   *
   * The preconditions for running the full auto-tuning are a properly configured motor, brake, and encoders,
   * and having executed the offset detection and the system identification procedures.
   *
   * In order to successfully run the full auto-tuning procedure, the motor must not rotate, and the device must not be in CiA402 Operation enabled.
   *
   * Motion Master will try to read the plant_model.csv file, and if it doesn't exist, it will return an error.
   *
   * Before the operation starts, Motion Master will measure the actual velocity for 500ms at a standstill to determine the encoder noise.
   * The noise is then sent, along with some other parameters (velocity feedback filter, feed forward, DC link voltage, firmware version, and similar)
   * and data to a proprietary computation algorithm.
   * ADC current ratio, which is used in the computation script, is determined by the firmware version and the device hardware ID (e.g., 9500, 9501, 8501...).
   *
   * Full auto-tuning will compute the PID gains, and Motion Master will update the parameter values in the velocity or position controller, depending on the request.
   *
   * Motion Master will return the damping ratio, settling time, and bandwidth values when the procedure completes. UI will use these values to update the sliders in the Tuning screen.
   *
   * This procedure can be stopped with {@link stopFullAutoTuning}.
   */
  startFullAutoTuning(
    props: StartFullAutoTuningRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<FullAutoTuningStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startFullAutoTuning = MotionMasterMessage.Request.StartFullAutoTuning.create({ ...props, deviceAddress });
        const id = this.sendRequest({ startFullAutoTuning }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.FullAutoTuning>(
            'fullAutoTuning',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Stop full auto-tuning.
   *
   * Motion Master has only one exit point for this procedure, which is after the computation algorithm ends.
   * This means that, once stopped, the procedure will compute the gains, but Motion Master will not update the controller parameters.
   * A previously started full auto-tuning request will complete with an aborted error.
   */
  stopFullAutoTuning(
    props: StopFullAutoTuningRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<FullAutoTuningStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const stopFullAutoTuning = MotionMasterMessage.Request.StopFullAutoTuning.create({ ...props, deviceAddress });
        const id = this.sendRequest({ stopFullAutoTuning }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.FullAutoTuning>(
            'fullAutoTuning',
            requestTimeout,
            id
          ),
          map((status) => {
            if (
              status.success?.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.POSITION_STOPPED ||
              status.success?.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.VELOCITY_STOPPED
            ) {
              // Full auto tuning status is shared between start and stop.
              // When full auto tuning has stopped this request is considered successful.
              status.request = 'succeeded';
            }
            return status;
          })
        );
      })
    );
  }

  /**
   * Start Circulo encoder configuration.
   *
   * The preconditions for this procedure are to have a Circulo device with internal encoders and firmware >=v5.
   *
   * This procedure will set various internal encoder registers based on the hardware description, Circulo device type, and the encoder ordinal.
   *
   * Before updating the registers, Motion Master will send an OS command to ignore the BiSS status bits.
   * Once the procedure is done, the value of this register will be reverted.
   *
   * Depending on the setup, this procedure can take up to 30 seconds.
   *
   * This procedure can fail if it's already running, if it's not supported because of the firmware version,
   * or if the hardware description file is missing.
   *
   * While this procedure runs, Motion Master will report its progress.
   */
  startCirculoEncoderConfiguration(
    props: StartCirculoEncoderConfigurationRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<CirculoEncoderConfigurationStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startCirculoEncoderConfiguration = MotionMasterMessage.Request.StartCirculoEncoderConfiguration.create({
          ...props,
          deviceAddress,
        });
        const id = this.sendRequest({ startCirculoEncoderConfiguration }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.CirculoEncoderConfiguration>(
            'circuloEncoderConfiguration',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * @deprecated use {@link stopDevice} instead
   */
  stopCirculoEncoderNarrowAngleCalibrationProcedure(
    props: StopCirculoEncoderNarrowAngleCalibrationProcedureRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<CirculoEncoderNarrowAngleCalibrationProcedureStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const stopCirculoEncoderNarrowAngleCalibrationProcedure =
          MotionMasterMessage.Request.StopCirculoEncoderNarrowAngleCalibrationProcedure.create({
            ...props,
            deviceAddress,
          });
        const id = this.sendRequest({ stopCirculoEncoderNarrowAngleCalibrationProcedure }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure>(
            'circuloEncoderNarrowAngleCalibrationProcedure',
            requestTimeout,
            id
          )
        );
      })
    );
  }

  /**
   * Start OS command.
   *
   * This request will execute an OS command and return the result as a byte array.
   *
   * Motion Master will check if an OS command is already running and use a mutex to ensure only one OS command runs at a time.
   *
   * This request can fail if the requested OS command is not supported.
   *
   * Motion Master will check if the OS command has failed and return an error.
   *
   * The OS command can timeout if it takes longer than the provided timeout in milliseconds, in which case Motion Master will return an error.
   */
  startOsCommand(
    props: StartOsCommandRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<OsCommandStatus> {
    return this.resolveDeviceAddress(props).pipe(
      mergeMap((deviceAddress) => {
        const startOsCommand = MotionMasterMessage.Request.StartOsCommand.create({ ...props, deviceAddress });
        const id = this.sendRequest({ startOsCommand }, messageId);
        return this.socket.message$.pipe(
          transformMotionMasterMessageToStatus<MotionMasterMessage.Status.OsCommand>('osCommand', requestTimeout, id)
        );
      })
    );
  }

  /**
   * Execute the forward call request.
   *
   * This request is used to execute any function provided by the Standalone Auto-tuning Service which is started by the Motion Master.
   */
  executeForwardCall(
    props: ExecuteForwardCallRequest,
    requestTimeout: number,
    messageId?: string
  ): Observable<ForwardCallStatus> {
    const executeForwardCall = MotionMasterMessage.Request.ExecuteForwardCall.create(props);
    const id = this.sendRequest({ executeForwardCall }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.ForwardCall>('forwardCall', requestTimeout, id)
    );
  }

  /**
   * Set PDO mode.
   */
  setPdoMode(props: SetPdoModeRequest, requestTimeout: number, messageId?: string): Observable<PdoModeStatus> {
    const setPdoMode = MotionMasterMessage.Request.SetPdoMode.create(props);
    const id = this.sendRequest({ setPdoMode }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.PdoMode>('pdoMode', requestTimeout, id)
    );
  }

  /**
   * Send request.
   *
   * Create a Motion Master message with the provided request, optionally generate a unique message ID,
   * and then send the message through the socket.
   *
   * @returns the message ID
   */
  sendRequest(request: MotionMasterMessage.IRequest, messageId?: string): string {
    const id = messageId ?? v4();
    const message = MotionMasterMessage.create({ request, id });
    this.socket.send(message);
    return id;
  }

  /**
   * Get device SII.
   */
  getDeviceSii(props: GetDeviceSiiRequest, requestTimeout: number, messageId?: string): Observable<DeviceSiiStatus> {
    const getDeviceSii = MotionMasterMessage.Request.GetDeviceSii.create(props);
    const id = this.sendRequest({ getDeviceSii }, messageId);
    return this.socket.message$.pipe(
      transformMotionMasterMessageToStatus<MotionMasterMessage.Status.DeviceSii>('deviceSii', requestTimeout, id)
    );
  }

  //
  // Helpers
  //

  /**
   * Get the device serial number by device address.
   *
   * This function does not make any requests; instead, it utilizes the previously mapped device serial number to an instance of a device.
   */
  getSerialNumberByDeviceAddress(deviceAddress: number): string | undefined {
    for (const device of this.devices) {
      if (device.deviceAddress === deviceAddress) {
        return device.serialNumber;
      }
    }
    return;
  }

  /**
   * Resolve a device.
   *
   * Resolves the device object by its reference, which can be a position, serial number, or device address.
   *
   * This function will make requests to retrieve the list of devices and the .hardware_description file for each device,
   * but only if it has not been previously retrieved for this session.
   *
   * @throws errors if the device reference is invalid
   */
  resolveDevice(deviceRef: DeviceRef | DeviceRefObj, requestTimeout = 20000): Observable<Device> {
    const deviceRefObj = typeof deviceRef === 'object' ? deviceRef : makeDeviceRefObj(deviceRef);

    if (!isValidDeviceRefObj(deviceRefObj)) {
      return throwError(() => new Error(`Device ref object is not valid ${JSON.stringify(deviceRefObj)}`));
    }

    const device = this.getCachedDeviceByDeviceRefObj(deviceRefObj);

    if (device) {
      return of(device);
    } else {
      return this.getDevices(requestTimeout).pipe(
        tap((devices) => {
          this.devices.length = 0;
          devices.forEach((device) => this.devices.push(device));
        }),
        map(() => {
          const device = this.getCachedDeviceByDeviceRefObj(deviceRefObj);

          if (!device) {
            throw new Error(`Device is not found by deviceRefObj ${JSON.stringify(deviceRefObj)}`);
          }

          return device;
        })
      );
    }
  }

  /**
   * Resolve device address.
   *
   * This method should return the device address by a device reference object,
   * or throw an error if the device address cannot be found.
   *
   * Refer to the description of this class for more information.
   */
  resolveDeviceAddress(deviceRef: DeviceRef | DeviceRefObj, requestTimeout = 60000): Observable<number> {
    const deviceRefObj = typeof deviceRef === 'object' ? deviceRef : makeDeviceRefObj(deviceRef);

    // This avoids a circular call to getDevices() from resolveDevice().
    // If a device is referenced by its address, there's no need to resolve it by reading its .hardware_description file to determine the serial number.
    if (deviceRefObj.deviceAddress) {
      return of(deviceRefObj.deviceAddress);
    }

    return this.resolveDevice(deviceRefObj, requestTimeout).pipe(map((device) => device.deviceAddress));
  }

  /**
   * Get devices.
   *
   * Retrieve device information from Motion Master.
   *
   * For each device, it reads and parses the `.hardware_description` and `.variant` files, then assigns the data to the device object.
   */
  getDevices(requestTimeout = 10000): Observable<Device[]> {
    return this.getDeviceInfo(requestTimeout).pipe(
      mergeMap((deviceInfo) => {
        const devices = (deviceInfo.devices ?? []) as unknown as Device[];
        return forkJoin(
          devices.map(async (deviceInfo) => {
            const { deviceAddress } = deviceInfo;

            const hardwareDescription = await lastValueFrom(this.getHardwareDescription(deviceAddress));
            const stackInfo = hardwareDescription ? undefined : await lastValueFrom(this.getStackInfo(deviceAddress));

            let serialNumber: string | undefined = undefined;

            if (hardwareDescription) {
              serialNumber = getSerialNumberFromHardwareDescription(hardwareDescription);
            } else if (stackInfo) {
              serialNumber = stackInfo.stack_serial_number;
            }

            const id = serialNumber ?? makeDeviceSerialNumberFromAddress(deviceInfo.deviceAddress);

            let deviceProductId: string | undefined = undefined;
            let productId: string | undefined = undefined;
            let versionedDeviceProductId: string | undefined = undefined;
            let versionedProductId: string | undefined = undefined;

            try {
              ({ deviceProductId, productId, versionedDeviceProductId, versionedProductId } = extractProductId(
                hardwareDescription ?? stackInfo ?? id
              ));
            } catch (err: unknown) {
              if (err instanceof Error) {
                console.warn(`Unable to parse serial number ${id}: ${err.message}`);
              }
            }

            let integroVariant: IntegroVariant | undefined = undefined;

            try {
              integroVariant = await lastValueFrom(this.getIntegroVariant(deviceAddress));
            } catch (err: unknown) {
              if (err instanceof Error) {
                console.warn(`Unable to retrieve Integro variant for device ${id}: ${err.message}`);
              }
            }

            const uiFeaturesConfig = await this.getUIFeaturesConfig(deviceAddress);

            let imageId: string | undefined = undefined;

            if (hardwareDescription) {
              imageId = getImageIdFromHardwareDescription(hardwareDescription);
            }

            const device = {
              ...deviceInfo,
              deviceProductId,
              hardwareDescription,
              id,
              imageId,
              integroVariant,
              productId,
              serialNumber,
              stackInfo,
              uiFeaturesConfig,
              versionedDeviceProductId,
              versionedProductId,
            } as Device;

            device.fwid = extractFirmwareId(device);

            return device;
          })
        );
      })
    );
  }

  getParameterValue<T extends ParameterValueType = number>(
    deviceParameterId: string,
    loadFromCache?: boolean,
    requestTimeout?: number,
    messageId?: string
  ): Observable<T>;
  getParameterValue<T extends ParameterValueType = number>(
    deviceRef: DeviceRef,
    index: number,
    subindex: number,
    loadFromCache?: boolean,
    requestTimeout?: number,
    messageId?: string
  ): Observable<T>;
  getParameterValue<T extends ParameterValueType = number>(
    a: string | number,
    b?: boolean | number,
    c?: number,
    d?: boolean | number | string,
    e?: number,
    f?: string
  ): Observable<T> {
    try {
      const { deviceRefObj, index, subindex, loadFromCache, requestTimeout, messageId } =
        this.resolveGetDeviceParameterValueArguments(a, b, c, d, e, f);
      return this.createGetParameterValueObservable(
        deviceRefObj,
        index,
        subindex,
        loadFromCache,
        requestTimeout,
        messageId
      );
    } catch (err) {
      return throwError(() => err);
    }
  }

  upload<T extends ParameterValueType = number>(
    deviceParameterId: string,
    loadFromCache?: boolean,
    requestTimeout?: number,
    messageId?: string
  ): Promise<T>;
  upload<T extends ParameterValueType = number>(
    deviceRef: DeviceRef,
    index: number,
    subindex: number,
    loadFromCache?: boolean,
    requestTimeout?: number,
    messageId?: string
  ): Promise<T>;
  upload<T extends ParameterValueType = number>(
    a: string | number,
    b?: boolean | number,
    c?: number,
    d?: boolean | number | string,
    e?: number,
    f?: string
  ): Promise<T> {
    const { deviceRefObj, index, subindex, loadFromCache, requestTimeout, messageId } =
      this.resolveGetDeviceParameterValueArguments(a, b, c, d, e, f);

    return lastValueFrom(
      this.createGetParameterValueObservable<T>(deviceRefObj, index, subindex, loadFromCache, requestTimeout, messageId)
    );
  }

  private createGetParameterValueObservable<T extends ParameterValueType = number>(
    deviceRefObj: DeviceRefObj,
    index: number,
    subindex: number,
    loadFromCache = false,
    requestTimeout = 1000,
    messageId?: string
  ) {
    return defer(() => {
      return this.getDeviceParameterValues(
        { ...deviceRefObj, parameters: [{ index, subindex, loadFromCache }] },
        requestTimeout,
        messageId
      ).pipe(
        map(({ parameterValues }) => {
          const parameterValue = parameterValues?.[0] as
            | MotionMasterMessage.Status.DeviceParameterValues.ParameterValue
            | undefined;

          if (parameterValue?.success) {
            const typeValue = parameterValue.typeValue;
            if (typeValue) {
              return parameterValues?.[0][typeValue] as T;
            }
          }

          const props = { ...deviceRefObj, index, subindex, loadFromCache, requestTimeout, messageId };

          const errorCodeName =
            MotionMasterMessage.Status.DeviceParameterValues.ParameterValue.Error.Code[
              parameterValues?.[0]?.error?.code ?? 0
            ];
          throw new Error(`Failed to get parameter value (${errorCodeName}) for ${JSON.stringify(props)}`);
        })
      );
    });
  }

  private resolveGetDeviceParameterValueArguments(
    a: string | number,
    b?: boolean | number,
    c?: number,
    d?: boolean | number | string,
    e?: number,
    f?: string
  ) {
    let deviceSerialNumber: string;
    let index: number | undefined;
    let subindex = 0;
    let loadFromCache = false;
    let requestTimeout = 1000;
    let deviceRefObj: DeviceRefObj | undefined;
    let messageId: string | undefined;

    if (typeof b === 'number' && typeof c === 'number') {
      // index, subindex, e.g. 0x6060, 1
      deviceRefObj = makeDeviceRefObj(a);
      index = b;
      subindex = c;
      if (typeof d === 'boolean') {
        loadFromCache = d;
      }
      if (typeof e === 'number') {
        requestTimeout = e;
      }
      if (typeof f === 'string') {
        messageId = f;
      }
    } else if (typeof a === 'string') {
      // device parameter id, e.g. "0x2110:1A.8502-03-0001353-2115"
      [deviceSerialNumber, index, subindex] = splitDeviceParameterId(a);
      deviceRefObj = makeDeviceRefObj(deviceSerialNumber);
      if (typeof b === 'boolean') {
        loadFromCache = b;
      }
      if (typeof c === 'number') {
        requestTimeout = c;
      }
      if (typeof d === 'string') {
        messageId = d;
      }
    }

    if (!index) {
      throw new Error('Device parameter index is not provided!');
    }

    if (!deviceRefObj) {
      throw new Error('Device address, position or serial number is not provided!');
    }

    return { deviceRefObj, index, subindex, loadFromCache, requestTimeout, messageId };
  }

  getParameterValues(
    ids: DeviceParameterIds,
    loadFromCache = false,
    requestTimeout = 9000,
    messageId?: string
  ): Observable<ParameterValueType[]> {
    return this.resolveGetParameterValuesIds(ids).pipe(
      mergeMap((tuples) => {
        const collection: MotionMasterMessage.Request.IGetDeviceParameterValues[] = [];

        for (const [deviceAddress, index, subindex] of tuples) {
          let item = collection.find((c) => c.deviceAddress === deviceAddress);
          if (!item) {
            item = { deviceAddress, parameters: [], sendProgress: false };
            collection.push(item);
          }
          item.parameters?.push({ index, subindex, loadFromCache });
        }

        return this.getMultiDeviceParameterValues({ collection }, requestTimeout, messageId).pipe(
          map((multiDeviceParameterValues) => {
            if (!multiDeviceParameterValues?.collection) {
              throw new Error(`Multi device parameter values collection is empty`);
            }
            return multiDeviceParameterValues.collection
              .map(({ parameterValues, deviceAddress }) => {
                if (!parameterValues) {
                  throw new Error(`No parameterValues for device ${deviceAddress}`);
                }

                return parameterValues.map((parameterValue) => {
                  const value = parameterValue as
                    | MotionMasterMessage.Status.DeviceParameterValues.ParameterValue
                    | undefined;
                  const parameterId = makeParameterId(value?.index, value?.subindex);

                  // Ignoring errors for some parameters that might not be available on all devices.
                  // For example, "0x2625:06 Filter time for safe analog input" is not available on SMM firmware v2.25.
                  if (parameterValue.error) {
                    console.warn(
                      `Failed to get parameter value (${parameterValue.error.code}) for ${parameterId} (${deviceAddress})`
                    );
                  }

                  const typeValue = value?.typeValue;
                  if (typeValue) {
                    return parameterValue[typeValue] as ParameterValueType;
                  } else {
                    console.warn(`No type value for parameter ${parameterId} (${deviceAddress})`);
                    return 0;
                  }
                });
              })
              .flat();
          })
        );
      })
    );
  }

  uploadMany(
    ids: DeviceParameterIds,
    loadFromCache = false,
    requestTimeout = 9000,
    messageId?: string
  ): Promise<ParameterValueType[]> {
    return lastValueFrom(this.getParameterValues(ids, loadFromCache, requestTimeout, messageId));
  }

  resolveGetParameterValuesIds(ids: DeviceParameterIds) {
    if (ids.length === 0) {
      return throwError(() => new Error('The provided array of device parameter ids or addresses is empty!'));
    }

    let refs: [DeviceRef, number, number][];
    if (isArrayOfDeviceSerialNumbers(ids)) {
      refs = ids.map((id) => splitDeviceParameterId(id));
    } else {
      refs = ids;
    }

    return defer(() => {
      return forkJoin(
        refs.map(([deviceRef, index, subindex]) => {
          return this.resolveDeviceAddress(makeDeviceRefObj(deviceRef)).pipe(
            map((deviceAddress) => [deviceAddress, index, subindex])
          );
        })
      );
    });
  }

  setParameterValue(
    deviceParameterId: string,
    value: ParameterValueType,
    valueTypeKey?: keyof Required<ParameterTypeValue>,
    requestTimeout?: number,
    messageId?: string
  ): Observable<void>;
  setParameterValue(
    deviceRef: DeviceRef,
    index: number,
    subindex: number,
    value: ParameterValueType,
    valueTypeKey?: keyof Required<ParameterTypeValue>,
    requestTimeout?: number,
    messageId?: string
  ): Observable<void>;
  setParameterValue(
    a: number | string,
    b: ParameterValueType | number,
    c?: keyof Required<ParameterTypeValue> | number,
    d?: number | ParameterValueType,
    e?: keyof Required<ParameterTypeValue>,
    f?: number,
    g?: string
  ): Observable<void> {
    try {
      const { deviceRefObj, index, subindex, value, valueTypeKey, requestTimeout, messageId } =
        this.resolveSetDeviceParameterValueArguments(a, b, c, d, e, f, g);
      return this.createSetParameterValueObservable(
        deviceRefObj,
        index,
        subindex,
        value,
        valueTypeKey,
        requestTimeout,
        messageId
      );
    } catch (err) {
      return throwError(() => err);
    }
  }

  download(
    deviceParameterId: string,
    value: ParameterValueType,
    valueTypeKey?: keyof Required<ParameterTypeValue>,
    requestTimeout?: number,
    messageId?: string
  ): Promise<void>;
  download(
    deviceRef: DeviceRef,
    index: number,
    subindex: number,
    value: ParameterValueType,
    valueTypeKey?: keyof Required<ParameterTypeValue>,
    requestTimeout?: number,
    messageId?: string
  ): Promise<void>;
  download(
    a: number | string,
    b: ParameterValueType | number,
    c?: keyof Required<ParameterTypeValue> | number,
    d?: number | ParameterValueType,
    e?: keyof Required<ParameterTypeValue>,
    f?: number,
    g?: string
  ): Promise<void> {
    const { deviceRefObj, index, subindex, value, valueTypeKey, requestTimeout, messageId } =
      this.resolveSetDeviceParameterValueArguments(a, b, c, d, e, f, g);

    return lastValueFrom(
      this.createSetParameterValueObservable(
        deviceRefObj,
        index,
        subindex,
        value,
        valueTypeKey,
        requestTimeout,
        messageId
      )
    );
  }

  private createSetParameterValueObservable(
    deviceRefObj: DeviceRefObj,
    index: number,
    subindex: number,
    value: ParameterValueType,
    valueTypeKey?: ParameterTypeValueKey,
    requestTimeout = 1000,
    messageId?: string
  ) {
    return defer(() => {
      return this.resolveDeviceParameterTypeValueKey(deviceRefObj, index, subindex, valueTypeKey).pipe(
        mergeMap((valueTypeKey) => {
          const parameterValue: MotionMasterMessage.Request.SetDeviceParameterValues.IParameterValue = {
            index,
            subindex,
            [valueTypeKey]: value,
          };

          return this.setDeviceParameterValues(
            { ...deviceRefObj, parameterValues: [parameterValue] },
            requestTimeout,
            messageId
          ).pipe(
            map(({ parameterValues }) => {
              const parameterValue = parameterValues?.[0] as
                | MotionMasterMessage.Status.DeviceParameterValues.ParameterValue
                | undefined;

              if (parameterValue?.success) {
                const typeValue = parameterValue.typeValue;
                if (typeValue) {
                  return;
                }
              }

              const props = { ...deviceRefObj, index, subindex, value, valueTypeKey, requestTimeout, messageId };

              const errorCodeName =
                MotionMasterMessage.Status.DeviceParameterValues.ParameterValue.Error.Code[
                  parameterValues?.[0]?.error?.code ?? 0
                ];
              throw new Error(`Failed to set parameter value (${errorCodeName}) for ${JSON.stringify(props)}`);
            })
          );
        })
      );
    });
  }

  /**
   * Resolve set device parameter value arguments.
   */
  private resolveSetDeviceParameterValueArguments(
    a: number | string,
    b: ParameterValueType | number,
    c?: keyof Required<ParameterTypeValue> | number,
    d?: number | ParameterValueType,
    e?: keyof Required<ParameterTypeValue>,
    f?: number,
    g?: string
  ) {
    let deviceSerialNumber: string | undefined;
    let index: number | undefined;
    let subindex = 0;
    let value: ParameterValueType = 0;
    let valueTypeKey: keyof Required<ParameterTypeValue> | undefined;
    let requestTimeout = 1000;
    let deviceRefObj: DeviceRefObj | undefined;
    let messageId: string | undefined;

    if (typeof b === 'number' && typeof c === 'number') {
      // index, subindex, e.g. 0x6060, 1
      deviceRefObj = makeDeviceRefObj(a);
      index = b;
      subindex = c;
      if (d !== undefined) {
        value = d;
      }
      valueTypeKey = e;
      if (typeof f === 'number') {
        requestTimeout = f;
      }
      messageId = g;
    } else if (typeof a === 'string') {
      // device parameter id, e.g. "0x2110:1A.8502-03-0001353-2115"
      [deviceSerialNumber, index, subindex] = splitDeviceParameterId(a);
      deviceRefObj = makeDeviceRefObj(deviceSerialNumber);
      value = b;
      if (typeof c !== 'number') {
        valueTypeKey = c;
      }
      if (typeof d === 'number') {
        requestTimeout = d;
      }
      messageId = e;
    }

    if (!index) {
      throw new Error('Device parameter index is not provided!');
    }

    if (!deviceRefObj) {
      throw new Error('Device address, position or serial number is not provided!');
    }

    return { deviceRefObj, index, subindex, value, valueTypeKey, requestTimeout, messageId };
  }

  setParameterValues(
    ids: { [deviceParameterId: string]: ParameterValueType } | DeviceParameterAddressValue[],
    requestTimeout = 9000,
    messageId?: string
  ): Observable<void> {
    if (ids.length === 0) {
      return throwError(() => new Error('The provided array of device parameter ids or addresses is empty!'));
    }

    let refs: DeviceParameterAddressValue[];
    if (isObjectOfDeviceSerialNumberToValues(ids)) {
      refs = Object.entries(ids).map(([id, value]) => [...splitDeviceParameterId(id), value]);
    } else {
      refs = ids;
    }

    return defer(() => {
      return forkJoin(
        refs.map(([deviceRef, index, subindex, value, typeValueKey]) => {
          const deviceRefObj = makeDeviceRefObj(deviceRef);
          return this.resolveDeviceAddress(deviceRefObj).pipe(
            mergeMap((deviceAddress) => {
              return this.resolveDeviceParameterTypeValueKey(deviceRefObj, index, subindex, typeValueKey).pipe(
                map((typeValueKey): [number, number, number, ParameterValueType, ParameterTypeValueKey] => [
                  deviceAddress,
                  index,
                  subindex,
                  value,
                  typeValueKey,
                ])
              );
            })
          );
        })
      ).pipe(
        mergeMap((tuples) => {
          const collection: MotionMasterMessage.Request.ISetDeviceParameterValues[] = [];

          for (const [deviceAddress, index, subindex, value, typeValueKey] of tuples) {
            let item = collection.find((c) => c.deviceAddress === deviceAddress);
            if (!item) {
              item = { deviceAddress, parameterValues: [] };
              collection.push(item);
            }
            item.parameterValues?.push({ index, subindex, [typeValueKey]: value });
          }

          return this.setMultiDeviceParameterValues({ collection }, requestTimeout, messageId).pipe(
            map((multiDeviceParameterValues) => {
              multiDeviceParameterValues.collection?.map(({ parameterValues, deviceAddress }) => {
                if (!parameterValues) {
                  throw new Error(`No parameterValues for device ${deviceAddress}`);
                }
              });
            })
          );
        })
      );
    });
  }

  downloadMany(
    ids: { [deviceParameterId: string]: ParameterValueType } | DeviceParameterAddressValue[],
    requestTimeout = 9000,
    messageId?: string
  ): Promise<void> {
    return lastValueFrom(this.setParameterValues(ids, requestTimeout, messageId));
  }

  resolveDeviceParameterTypeValueKey(
    deviceRefObj: DeviceRefObj,
    index: number,
    subindex: number,
    typeValueKey?: ParameterTypeValueKey
  ): Observable<ParameterTypeValueKey> {
    if (typeValueKey) {
      return of(typeValueKey);
    }

    return this.resolveDeviceParameterInfoMap(deviceRefObj).pipe(
      map((infoMap) => {
        const parameterId = makeParameterId(index, subindex);
        const infoParameter = infoMap.get(parameterId);

        if (!infoParameter?.typeValueKey) {
          throw new Error(`No type value key is set for device ${JSON.stringify(deviceRefObj)} ${parameterId}`);
        }

        return infoParameter.typeValueKey;
      })
    ) as Observable<ParameterTypeValueKey>;
  }

  resolveDeviceParameterInfoMap(
    deviceRef: DeviceRef | DeviceRefObj,
    requestTimeout = 5000
  ): Observable<Map<string, DeviceParameterInfo>> {
    return this.resolveDevice(deviceRef).pipe(
      mergeMap(({ deviceAddress }) => {
        let infoMap = this.deviceParameterInfoMap.get(deviceAddress);

        if (infoMap) {
          return of(infoMap);
        }

        return this.getDeviceParameterInfo({ deviceAddress }, requestTimeout).pipe(
          map(({ parameters }) => {
            if (!parameters) {
              throw new Error(`Device parameter info has no parameters ${deviceAddress}`);
            }

            infoMap = new Map();
            this.deviceParameterInfoMap.set(deviceAddress, infoMap);

            for (const parameter of parameters) {
              if (parameter.valueType) {
                const typeValue = valueTypeToParameterTypeValueKeyMap.get(parameter.valueType);
                if (!typeValue) {
                  throw new Error(`No type value for device ${deviceAddress} in resolveDeviceParameterInfoMap`);
                }

                const serialNumber = this.getSerialNumberByDeviceAddress(deviceAddress);
                if (!serialNumber) {
                  throw new Error(`No serial number for device ${deviceAddress} in resolveDeviceParameterInfoMap`);
                }

                const id = makeDeviceParameterId(serialNumber, parameter.index, parameter.subindex);
                const key = makeParameterId(parameter);
                infoMap.set(key, {
                  ...parameter,
                  id,
                  index: parameter.index!,
                  subindex: parameter.subindex!,
                  name: parameter.name!,
                  typeValueKey: typeValue,
                });
              }
            }

            return infoMap;
          })
        );
      })
    );
  }

  /**
   * Retrieves and constructs device parameters with optional ESI (EtherCAT Slave Information) extensions.
   *
   * This method resolves the target device, optionally loads and parses an ESI XML file,
   * gathers device parameter metadata, and then fetches the actual parameter values.
   * Optionally includes progress updates during value retrieval.
   *
   * @param props - An object containing the following properties:
   * - `deviceId` or other identifying fields from `DeviceRefObj` to resolve the device.
   * - `loadFromCache` (optional): Whether read from the device or from the cache in Motion Master.
   * - `sendProgress` (optional): Whether to emit progress updates during parameter value retrieval.
   * - `extendWithEsi` (optional): If true, attempts to load and parse the ESI XML file to enrich parameters with metadata.
   * - `skipReadingValues` (optional): If true, skips reading parameter values and returns only the parameter structure
   *   resolved from device info, optionally extended with ESI metadata.
   * @param requestTimeout - Timeout in milliseconds to apply to the device parameter info and value retrieval. Defaults to 30,000 ms.
   *
   * @returns An Observable emitting an object containing:
   * - `parameters`: An array of fully built `DeviceParameter` objects.
   * - `progress`: A percentage (0–100) representing retrieval progress if `sendProgress` is enabled, otherwise 100.
   *
   * @throws An error if device parameter values could not be retrieved or are empty.
   */
  getDeviceParameters(
    props: {
      loadFromCache?: boolean;
      sendProgress?: boolean;
      extendWithEsi?: boolean;
      skipReadingValues?: boolean;
    } & DeviceRefObj,
    requestTimeout = 30000
  ): Observable<{ parameters: DeviceParameter[]; progress: number }> {
    const { loadFromCache, sendProgress, extendWithEsi, skipReadingValues } = props;

    return this.resolveDevice(props).pipe(
      mergeMap((device: Device) => {
        return concat(
          extendWithEsi ? this.upload(device.deviceAddress, 0x1018, 0x02) : of(undefined), // productCode
          extendWithEsi ? this.getFile(device.deviceAddress, 'SOMANET_CiA_402.xml', 30000) : of(undefined), // esiFileContent
          this.getDeviceParameterInfo({ deviceAddress: device.deviceAddress }, requestTimeout) // deviceParameterInfoStatus
        ).pipe(
          toArray(),
          mergeMap((results: any[]) => {
            let [productCode, esiFileContent, deviceParameterInfoStatus] = results as [
              number | undefined,
              Uint8Array | number[] | undefined,
              MotionMasterMessage.Status.IDeviceParameterInfo
            ];

            let esi: any = undefined;

            if (Array.isArray(esiFileContent)) {
              esiFileContent = new Uint8Array(esiFileContent);
            }

            if (esiFileContent instanceof Uint8Array) {
              const xml = new TextDecoder('utf-8').decode(esiFileContent!);
              esi = xmlToEsi(xml);
            }

            const buildDeviceParameter = (
              base: MotionMasterMessage.Status.DeviceParameterInfo.IParameter,
              index: number,
              subindex: number
            ): DeviceParameter => {
              const id = makeDeviceParameterId(device.id, index, subindex);
              const deviceParameter = { id, ...base, index, subindex } as DeviceParameter;

              const esiExtension: ParameterEsiExtension = esi
                ? getParameterEsiExtension(deviceParameter, esi?.value, productCode as number)
                : getParameterEsiExtensionFallback(deviceParameter);

              /**
               * Configure GPIO voltage (obj 0x2214:1) with device-aware labels.
               * Circulo supports 3.3V/5.0V selection; Node is fixed at 3.3V; Integro is fixed at 24V.
               * Use abstract labels ("default"/"alternative") with actual voltages appended per device to
               * prevent user confusion.
               */
              if (index === 0x2214 && subindex === 1) {
                const product = findSomanetProductById(device.deviceProductId);
                const gpioOptions = gpioVoltageLevelOptions[product?.family as ProductFamily];
                esiExtension.options = gpioOptions ?? esiExtension.options;
              }

              const parameterId = makeParameterId(index, subindex);

              const plot: ParameterPlot = {
                axis: parameterIdToPlotAxis[parameterId] ?? 'y2',
                color: parameterIdToPlotColor[parameterId] ?? createRandomColor(100),
                visible: true,
              };

              if (
                base.valueType === MotionMasterMessage.Status.DeviceParameterInfo.Parameter.ValueType.VISIBLE_STRING ||
                base.valueType === MotionMasterMessage.Status.DeviceParameterInfo.Parameter.ValueType.UNICODE_STRING
              ) {
                deviceParameter.value = '';
              } else if (
                base.valueType === MotionMasterMessage.Status.DeviceParameterInfo.Parameter.ValueType.OCTET_STRING
              ) {
                deviceParameter.value = [] as number[];
              } else {
                deviceParameter.value = 0;
                deviceParameter.radix = parameterIdToRadix[parameterId] ?? 10;
              }

              deviceParameter.isSmm = isSmmParameterIndex(index);
              deviceParameter.isBeta = betaParameterIds.includes(parameterId);

              deviceParameter.fieldName = id;

              return { ...deviceParameter, ...esiExtension, plot } as DeviceParameter;
            };

            if (skipReadingValues) {
              const deviceParameters = (deviceParameterInfoStatus.parameters ?? []).map((p) =>
                buildDeviceParameter(p, p.index ?? 0, p.subindex ?? 0)
              );
              return of({ parameters: deviceParameters, progress: 100 });
            }

            const parameters = deviceParameterInfoStatus.parameters?.map((p) => ({
              index: p.index ?? 0,
              subindex: p.subindex ?? 0,
              loadFromCache,
            }));

            return this.getDeviceParameterValues(
              { deviceAddress: device.deviceAddress, sendProgress, parameters },
              requestTimeout
            ).pipe(
              map((deviceParameterValuesStatus: DeviceParameterValuesStatus) => {
                if (!deviceParameterValuesStatus.parameterValues) {
                  throw new Error('Device parameter values is empty');
                }

                const deviceParameters = deviceParameterValuesStatus.parameterValues.map((value, i) => {
                  const index = value.index ?? 0;
                  const subindex = value.subindex ?? 0;
                  const deviceParameter = buildDeviceParameter(
                    { ...value, ...deviceParameterInfoStatus.parameters?.[i] },
                    index,
                    subindex
                  );
                  deviceParameter.value = getParameterValue(value);
                  return deviceParameter;
                });

                const progress = sendProgress ? deviceParameterValuesStatus?.progress?.percentage ?? 0 : 100;

                return { parameters: deviceParameters, progress };
              })
            );
          })
        );
      })
    );
  }

  /**
   * Get list of files.
   */
  getFiles(deviceRef: DeviceRef, requestTimeout = 30000, messageId?: string) {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFileList(deviceRefObj, requestTimeout, messageId).pipe(
      map((status) => status.fileList?.files ?? [])
    );
  }

  /**
   * Get file content.
   */
  getFile(
    deviceRef: DeviceRef,
    name: string,
    requestTimeout = 10000,
    messageId?: string
  ): Observable<Uint8Array | number[] | null | undefined> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFile({ ...deviceRefObj, name }, requestTimeout, messageId).pipe(map(({ content }) => content));
  }

  /**
   * Retrieves a file from the specified device and attempts to decode its content as a UTF-8 string.
   *
   * This method uses a strict decoder (`fatal: true`). If the file contains binary data
   * or invalid UTF-8 sequences, the operation will fail, allowing the caller to handle
   * the error (e.g., by falling back to a raw binary download).
   *
   * @param deviceRef - The reference object identifying the target device.
   * @param name - The name of the file to retrieve.
   * @param requestTimeout - The timeout for the request in milliseconds (default: 10000).
   * @param messageId - Optional ID for tracing the request.
   * @returns An Observable that emits the decoded string content of the file.
   * @throws {Error} If the file content is returned as undefined (file missing).
   * @throws {TypeError} If the file content is not valid UTF-8 text.
   */
  getDecodedFile(deviceRef: DeviceRef, name: string, requestTimeout = 10000, messageId?: string): Observable<string> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFile({ ...deviceRefObj, name }, requestTimeout, messageId).pipe(
      map(({ content }) => {
        if (content) {
          const decoder = new TextDecoder('utf-8', { fatal: true });
          const buffer = Uint8Array.from(content);
          return decoder.decode(buffer);
        }
        throw new Error(
          `The content for the device file '${name}' for device ${deviceRef} is undefined. The file is most likely missing.`
        );
      })
    );
  }

  /**
   * Retrieves the plant model data for a given device.
   *
   * Reads and parses the 'plant_model.csv' file associated with the device reference,
   * extracting numerator and denominator coefficients as arrays of numbers,
   * along with friction and inertia parameters.
   *
   * @param deviceRef - Reference to the device to retrieve the model from.
   * @param requestTimeout - Optional timeout in milliseconds for the request. Defaults to 10000 ms.
   * @param messageId - Optional message identifier for tracking or logging purposes.
   * @returns An Observable that emits the parsed PlantModel object or undefined if no content is available.
   */
  getPlantModel(deviceRef: DeviceRef, requestTimeout = 10000, messageId?: string): Observable<PlantModel | undefined> {
    return this.getDecodedFile(deviceRef, 'plant_model.csv', requestTimeout, messageId).pipe(
      map((content) => {
        return content ? parsePlantModelCsv(content) : undefined;
      })
    );
  }

  /**
   * Unlock protected files.
   *
   * This will enable the deletion of protected files.
   * Protected files begin with a period, such as '.hardware_description'.
   */
  unlockProtectedFiles(deviceRef: DeviceRef, requestTimeout = 10000, messageId?: string) {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFile({ ...deviceRefObj, name: stackUnlockFilename }, requestTimeout, messageId);
  }

  /**
   * Set file content.
   */
  setFile(
    deviceRef: DeviceRef,
    name: string,
    content: Uint8Array,
    overwrite = true,
    requestTimeout = 10000,
    messageId?: string
  ): Observable<DeviceFileStatus> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.setDeviceFile({ ...deviceRefObj, name, content, overwrite }, requestTimeout, messageId);
  }

  /**
   * Delete file.
   */
  deleteFile(
    deviceRef: DeviceRef,
    name: string,
    requestTimeout = 10000,
    messageId?: string
  ): Observable<DeviceFileStatus> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.deleteDeviceFile({ ...deviceRefObj, name }, requestTimeout, messageId);
  }

  /**
   * Retrieve and parse the `.hardware_description` file.
   */
  getHardwareDescription(
    deviceRef: DeviceRef,
    requestTimeout = 10000,
    messageId?: string
  ): Observable<HardwareDescription | undefined> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFile({ ...deviceRefObj, name: '.hardware_description' }, requestTimeout, messageId).pipe(
      mergeMap(async (status) => {
        status = await this.fixProductionScrewUp(status);
        if (!status.content) {
          return;
        }
        try {
          const data = decodeTextContent(status.content);
          return JSON.parse(data) as HardwareDescription;
        } catch (err) {
          console.warn(`Unable to parse .hardware_description for device ${deviceRef}: ${err}`);
          return;
        }
      })
    );
  }

  /**
   * Retrieve and parse the `stack_info.json` file.
   */
  getStackInfo(deviceRef: DeviceRef, requestTimeout = 10000, messageId?: string): Observable<StackInfo | undefined> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.getDeviceFile({ ...deviceRefObj, name: 'stack_info.json' }, requestTimeout, messageId).pipe(
      mergeMap(async (status) => {
        if (!status.content) {
          return;
        }
        const data = decodeTextContent(status.content);
        return JSON.parse(data) as StackInfo;
      })
    );
  }

  /**
   * Retrieve and parse the `.variant` file.
   */
  getIntegroVariant(deviceRef: DeviceRef, requestTimeout = 10000): Observable<IntegroVariant | undefined> {
    return this.getFile(deviceRef, '.variant', requestTimeout).pipe(
      map((content) => {
        if (!content) {
          return;
        }
        const buffer = Buffer.from(content);
        return parseIntegroVariantBuffer(buffer);
      })
    );
  }

  /**
   * Get system log lines.
   *
   * Get the system log content and parse it into an array of lines, where each line contains the date, uptime, ID, file, level, and message.
   */
  getSystemLogLines(requestTimeout = 5000, messageId?: string): Observable<SystemLogLine[]> {
    return this.getSystemLog(requestTimeout, messageId).pipe(
      map((systemLog) => parseSystemLogContent(systemLog.content ?? ''))
    );
  }

  /**
   * Retrieves the UI configuration from the device's `ui.config.json` file.
   *
   * @param deviceRef - Reference to the target device from which to fetch the config.
   * @param requestTimeout - Optional timeout in milliseconds for the file retrieval request (default is 10,000 ms).
   * @param messageId - Optional message identifier for tracking or logging purposes.
   * @returns An Observable emitting the parsed `UiConfig` object from the device, or an empty object if the content is unavailable.
   */
  getUiConfig(deviceRef: DeviceRef, requestTimeout = 10000, messageId?: string): Observable<UiConfig> {
    return this.getFile(deviceRef, 'ui.config.json', requestTimeout, messageId).pipe(
      map((content) => {
        if (content) {
          try {
            const buffer = Buffer.from(content);
            // @ts-ignore
            const text = decodeTextContent(buffer);
            return JSON.parse(text) as UiConfig;
          } catch {
            return {};
          }
        } else {
          return {};
        }
      })
    );
  }

  /**
   * Updates the UI configuration file `ui.config.json` on the device by writing the provided PDO mapping.
   *
   * This method retrieves the current UI configuration from the device,
   * updates its `pdoMapping` property with the provided mapping,
   * then serializes and writes the updated configuration back to the device file system.
   *
   * @param deviceRef - Reference to the target device containing the UI configuration file.
   * @param pdoMapping - The PDO mapping object to be saved within the UI configuration.
   * @returns An Observable that emits the status of the file write operation.
   */
  writePdoMappingToUiConfigFile(deviceRef: DeviceRef, pdoMapping: UiPdoMapping): Observable<DeviceFileStatus> {
    return this.getUiConfig(deviceRef).pipe(
      mergeMap((uiConfig) => {
        uiConfig.pdoMapping = pdoMapping;
        const jsonString = JSON.stringify(uiConfig);
        const content = new TextEncoder().encode(jsonString);
        return this.setFile(deviceRef, 'ui.config.json', content, true, 10000);
      })
    );
  }

  /**
   * Removes the PDO mapping from the UI configuration file `ui.config.json` on the device.
   *
   * This method fetches the existing UI configuration, deletes the `pdoMapping` property if present,
   * then writes the updated configuration back to the device.
   *
   * @param deviceRef - Reference to the target device containing the UI configuration file.
   * @returns An Observable that emits the status of the file write operation.
   */
  removePdoMappingFromUiConfigFile(deviceRef: DeviceRef): Observable<DeviceFileStatus> {
    return this.getUiConfig(deviceRef).pipe(
      mergeMap((uiConfig) => {
        if ('pdoMapping' in uiConfig) {
          delete uiConfig.pdoMapping;
        }
        const jsonString = JSON.stringify(uiConfig);
        const content = new TextEncoder().encode(jsonString);
        return this.setFile(deviceRef, 'ui.config.json', content, true, 10000);
      })
    );
  }

  /**
   * Store parameters.
   *
   * Command the device to store the parameters into config.csv, then delay initially and subsequently check if the value of
   * the Store parameters has changed to 1, indicating the successful completion.
   *
   * {@link https://www.synapticon.com/documentation/circulo/sw5.0/device_info/save_restore_config.html}
   */
  storeParameters(deviceRef: DeviceRef, retryConfig: RetryConfig = { count: 10, delay: 500 }): Observable<void> {
    return this.setParameterValue(deviceRef, 0x1010, 1, 0x65766173).pipe(
      // "evas"
      delay(2000),
      mergeMap(() => this.matchParameterValue(deviceRef, 0x1010, 1, 1).pipe(retry(retryConfig)))
    );
  }

  /**
   * Restore default parameters.
   *
   * Sends a command to the device to restore all default parameters (0x1011:01) or manufacturer default parameters (0x1011:04).
   * After setting the value to 0x64616f6c, the function briefly waits and checks if 0x1011:01 updates to 1,
   * confirming the restoration process has completed successfully. This check will retry a few times and will fail if the value doesn't revert to 1.
   *
   * For 0x1011:01, the configuration is loaded in the following order: first from the .factory_config file,
   * then from the config.csv file, and finally from the .assembly_config file, with values in the latter files overriding earlier ones.
   *
   * For 0x1011:04, the configuration is loaded from the .factory_config file, followed by the .assembly_config file,
   * with values in the latter overriding those in the former.
   *
   * Restoring default parameters will only override those stored in .factory_config, config.csv, and .assembly_config.
   * All other parameters, such as target values and PDO mappings, will remain unchanged.
   *
   * @throws Error if the device does not revert the value to 1 after the specified number of retries.
   *
   * For more information, refer to the documentation:
   * {@link https://www.synapticon.com/documentation/circulo/sw5.0/device_info/save_restore_config.html}
   */
  restoreDefaultParameters(
    deviceRef: DeviceRef,
    type: RestoreDefaultParametersType = RestoreDefaultParametersType.RESTORE_ALL_DEFAULT_PARAMETERS,
    retryConfig: RetryConfig = { count: 10, delay: 500 }
  ): Observable<void> {
    return this.setParameterValue(deviceRef, 0x1011, type, 0x64616f6c).pipe(
      // "daol"
      delay(2000),
      mergeMap(() => this.matchParameterValue(deviceRef, 0x1011, type, 1).pipe(retry(retryConfig)))
    );
  }

  /**
   * Match parameter value.
   *
   * This function will retrieve a parameter value from a device and compare it with the expected value.
   *
   * @throws Error if the values do not match.
   */
  matchParameterValue(deviceRef: DeviceRef, index: number, subindex: number, expectedValue: number): Observable<void> {
    return this.getParameterValue(deviceRef, index, subindex).pipe(
      map((value) => {
        if (value !== expectedValue) {
          throw new Error(`Device parameter value and the expected value don't match: ${value} ${expectedValue}`);
        }
      })
    );
  }

  matchCia402States(deviceRef: DeviceRef, expectedStates: Cia402State[]) {
    return this.getParameterValue(deviceRef, 0x6041, 0).pipe(
      map((value) => {
        const state = getCia402State(value);
        if (!expectedStates.includes(state)) {
          throw new Error(`Device CiA402 state and the expected state don't match: ${state} ${expectedStates}`);
        }
      })
    );
  }

  /**
   * Helper function to enable motion.
   */
  enableMotion(
    deviceRef: DeviceRef,
    controllerType: MotionMasterMessage.Request.EnableMotionController.ControllerType,
    filter = false,
    requestTimeout = 2000,
    messageId?: string
  ) {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.enableMotionController({ ...deviceRefObj, controllerType, filter }, requestTimeout, messageId);
  }

  /**
   * A helper function to disable motion.
   */
  disableMotion(deviceRef: DeviceRef, requestTimeout = 3000, messageId?: string) {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.disableMotionController(deviceRefObj, requestTimeout, messageId);
  }

  /**
   * A helper function to set the motion target.
   */
  setMotionTarget(deviceRef: DeviceRef, target: number, messageId?: string) {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    return this.setMotionControllerParameters({ ...deviceRefObj, target }, messageId);
  }

  /**
   * A helper function to set the value of the Modes of Operation parameter (0x6060:00).
   */
  setModesOfOperation(deviceRef: DeviceRef, modesOfOperation: ModesOfOperation) {
    return this.setParameterValue(deviceRef, 0x6060, 0, modesOfOperation);
  }

  /**
   * Get CiA402 state.
   */
  getCia402State(deviceRef: DeviceRef, requestTimeout = 5000, messageId?: string): Observable<Cia402State> {
    return this.getParameterValue(deviceRef, 0x6041, 0, false, requestTimeout, messageId).pipe(
      map((value) => getCia402State(value))
    );
  }

  /**
   * Transition to CiA402 state.
   *
   * This function will utilize the current value of the controlword and modify it to transition to the requested CiA402 state.
   *
   * Only one transition will occur, except when the current CiA402 state is SWITCH_ON_DISABLED. In this case,
   * the first transition is from SOD to RTSO, and then from RTSO to the requested CiA402 state.
   *
   * The Statusword will be polled to check if the device has successfully transitioned to the requested CiA402 state.
   * When transitioning to QSA, the Statusword will be checked against both QSA and SOD.
   * This is necessary because the automatic transition from QSA to SOD can occur faster than the client's polling interval.
   */
  async transitionToCia402State(deviceRef: DeviceRef, targetState: Cia402State): Promise<void> {
    let currentState = await lastValueFrom(this.getCia402State(deviceRef));

    if (targetState === currentState) {
      return;
    }

    if (currentState === Cia402State.NOT_READY_TO_SWITCH_ON || currentState === Cia402State.FAULT_REACTION_ACTIVE) {
      throw new Error(
        `It is not possible to transition from state ${currentState} to state ${targetState}. The transition from the current ${currentState} state is automatic.`
      );
    }

    let controlword = await this.upload(deviceRef, 0x6040, 0);

    let transition = cia402Transitions.find((t) => t.from === currentState && t.to === targetState);

    // From SOD it's only possible to transition to RTSO.
    if (currentState === Cia402State.SWITCH_ON_DISABLED && targetState !== Cia402State.READY_TO_SWITCH_ON) {
      transition = cia402Transitions.find((t) => t.from === Cia402State.READY_TO_SWITCH_ON && t.to === targetState);
    }

    if (!transition) {
      throw new Error(`It is not possible to transition from state ${currentState} to state ${targetState}.`);
    }

    if (currentState === Cia402State.SWITCH_ON_DISABLED) {
      const sodToRtsoTransition = cia402Transitions[0];
      const command = createControlwordCommand(controlword, sodToRtsoTransition.command);

      await lastValueFrom(
        this.setParameterValue(deviceRef, 0x6040, 0, command).pipe(
          mergeMap(() =>
            this.matchCia402States(deviceRef, [Cia402State.READY_TO_SWITCH_ON]).pipe(retry({ count: 5, delay: 1000 }))
          )
        )
      );

      controlword = await this.upload(deviceRef, 0x6040, 0);
      currentState = await lastValueFrom(this.getCia402State(deviceRef));

      if (targetState === currentState) {
        return;
      }
    }

    const command = createControlwordCommand(controlword, transition.command);

    const expectedStates = [targetState];
    if (targetState === Cia402State.QUICK_STOP_ACTIVE) {
      expectedStates.push(Cia402State.SWITCH_ON_DISABLED);
    }

    await lastValueFrom(
      this.setParameterValue(deviceRef, 0x6040, 0, command).pipe(
        mergeMap(() => this.matchCia402States(deviceRef, expectedStates).pipe(retry({ count: 10, delay: 1000 }))),
        catchError(() => {
          throw new Error(`Timeout occurred while transitioning from ${currentState} state to ${targetState} state.`);
        })
      )
    );
  }

  /**
   * Prepares the homing procedure by updating the controlword on the specified device.
   *
   * This function performs the following actions:
   * 1. Clears the halt bit (bit 8) by setting it to 0.
   * 2. Resets the homing bit (bit 4) by setting it to 0.
   * 3. Sends the updated controlword to the device.
   * 4. Triggers the rising edge of the homing bit (bit 4) by setting it to 1.
   * 5. Sends the controlword to the device again to confirm the rising edge.
   *
   * @param {DeviceRef} deviceRef - Reference to the device for which the controlword will be updated.
   * @param {boolean} [clearHaltBit=true] - Indicates whether to clear the halt bit before configuring the homing procedure.
   * @returns {Observable<void>} An observable that completes when the controlword has been successfully updated.
   */
  setControlwordToStartHoming(deviceRef: DeviceRef, clearHaltBit: boolean = true): Observable<void> {
    return this.getParameterValue(deviceRef, 0x6040, 0).pipe(
      mergeMap(async (value) => {
        if (clearHaltBit) {
          value = setBitLow(value, 8);
        }
        // Trigger the rising edge of the homing bit in the controlword.
        value = setBitLow(value, 4);
        await this.download(deviceRef, 0x6040, 0, value);
        value = setBitHigh(value, 4);
        await this.download(deviceRef, 0x6040, 0, value);
      })
    );
  }

  /**
   * The firmware distinguishes between two types of parameters (configuration objects):
   *
   * - **Live objects:** Parameters read by the firmware during each loop, regardless of the CiA402 state of the device.
   * - **On-demand objects:** Parameters read by the firmware only when one of the following conditions is met:
   *   - Communication with the master has just been established.
   *   - Configuration has just been loaded from `config.csv`, `.assembly_config`, or `.factory_config` files.
   *   - A transition in the CiA402 state machine from "Switch On Disabled" to "Ready to Switch On" occurs.
   *   - The Fault Reset bit of the Controlword rises (only when the device is in a Fault State).
   *
   * This function will reset the fault if the device is in a fault state;
   * otherwise, it will transition the CiA402 state from the current state to "Switch On Disabled," and then to "Ready to Switch On."
   * This process ensures that the values of the on-demand parameters, which do not apply immediately, are correctly applied.
   */
  async forceOnDemandParametersUpdate(deviceRef: DeviceRef): Promise<void> {
    const currentState = await lastValueFrom(this.getCia402State(deviceRef));
    if (currentState === Cia402State.FAULT) {
      try {
        await this.resetFault(deviceRef);
      } catch {} // ignore if the fault has been reset in the meantime
    } else {
      await this.transitionToCia402State(deviceRef, Cia402State.SWITCH_ON_DISABLED);
      await this.transitionToCia402State(deviceRef, Cia402State.READY_TO_SWITCH_ON);
    }
  }

  /**
   * Executes a reset fault on the specified device.
   *
   * Resetting a fault can only be done from the FAULT CiA402 state by transitioning to SWITCH_ON_DISABLED.
   * This function will do the rising edge, that is it will first set the controlword reset fault bit to 0, wait a bit, and then set it to 1.
   * If the reset is successful, the fault reset bit will be set back to 0.
   *
   * @param {DeviceRef} deviceRef - Reference to the device for which the fault will be reset.
   * @param [force=false] - Indicates whether to force the fault reset even if the device is not in the FAULT CiA402 state.
   * @returns {Promise<void>} Resolves when the reset fault operation is complete.
   */
  async resetFault(deviceRef: DeviceRef, force = false): Promise<void> {
    let currentState = await lastValueFrom(this.getCia402State(deviceRef));
    if (currentState !== Cia402State.FAULT && !force) {
      throw new Error(`Device ${deviceRef} is not in FAULT CiA402 state.`);
    }

    let controlword = await this.upload(deviceRef, 0x6040, 0);

    controlword = controlword & 0xff7f; // set fault reset bit (7) to 0
    await this.download(deviceRef, 0x6040, 0, controlword);
    await resolveAfter(10);

    controlword = createControlwordCommand(controlword, ControlwordCommand.FAULT_RESET); // set fault reset bit (7) to 1
    await this.download(deviceRef, 0x6040, 0, controlword);
    await resolveAfter(10);

    currentState = await lastValueFrom(this.getCia402State(deviceRef));
    if (currentState === Cia402State.SWITCH_ON_DISABLED) {
      controlword = controlword & 0xff7f; // set fault reset bit (7) to 0
      await this.download(deviceRef, 0x6040, 0, controlword);
    }
  }

  /**
   * Executes a quick stop on the specified device.
   *
   * A quick stop can only be initiated from the OPERATION_ENABLED state of the CiA402 state machine, transitioning to QUICK_STOP_ACTIVE.
   *
   * In CYCLIC_SYNC_TORQUE_MODE or TORQUE_PROFILE_MODE, the target torque and torque offset are set to 0 to prevent movement after the quick stop, as requested by the firmware team.
   *
   * @link https://www.wrike.com/open.htm?id=1452182434
   *
   * @param {DeviceRef} deviceRef - Reference to the device on which the quick stop will be performed.
   * @param [force=false] - Indicates whether to force the quick stop even if the device is not in the OPERATION_ENABLED CiA402 state.
   * @returns {Promise<void>} Resolves when the quick stop operation is complete.
   */
  async quickStop(deviceRef: DeviceRef, force = false): Promise<void> {
    const currentState = await lastValueFrom(this.getCia402State(deviceRef));

    if (currentState !== Cia402State.OPERATION_ENABLED && !force) {
      throw new Error(`Device ${deviceRef} is not in OPERATION_ENABLED CiA402 state.`);
    }

    const controlword = await this.upload(deviceRef, 0x6040, 0);
    const command = createControlwordCommand(controlword, ControlwordCommand.QUICK_STOP);

    await this.download(deviceRef, 0x6040, 0, command);

    const modesOfOperationDisplay = await this.upload(deviceRef, 0x6061, 0);
    if (
      modesOfOperationDisplay === ModesOfOperation.CYCLIC_SYNC_TORQUE_MODE ||
      modesOfOperationDisplay === ModesOfOperation.TORQUE_PROFILE_MODE
    ) {
      await this.download(deviceRef, 0x6071, 0, 0); // target torque
      await this.download(deviceRef, 0x60b2, 0, 0); // torque offset
    }
  }

  /**
   * Reset targets.
   *
   * Set torque and velocity targets to 0, and set the position target to the current position value.
   */
  async resetTargets(deviceRef: DeviceRef): Promise<void> {
    const positionActualValue = await this.upload(deviceRef, 0x6064, 0);
    await this.downloadMany([
      [deviceRef, 0x6071, 0, 0], // target torque
      [deviceRef, 0x607a, 0, positionActualValue], // target position
      [deviceRef, 0x60ff, 0, 0], // target velocity
    ]);
  }

  /**
   * Apply set point.
   *
   * When running in position profile mode and setting a new position target value, it's important to note that the device won't immediately move to the new position.
   * To execute a new position set-point or target value, you need to make certain changes to the controlword.
   *
   * To indicate that the new set-point is valid and should be executed, you need to trigger a rising edge of the new set-point bit in the controlword.
   * This rising edge serves as a signal to the device that a new set-point is being provided.
   *
   * Once the device receives the rising edge of the new set-point bit, it will set the set-point acknowledge bit in the statusword to 1.
   * This indicates that the device has acknowledged the new set-point and will execute the corresponding action, such as moving to the specified position.
   *
   * Afterwards, the device will signal its ability to accept new set-points by setting the set-point acknowledge bit back to 0 in the statusword.
   * This signifies that the device is ready to receive and process additional set-points.
   *
   * It's important to note that the device won't wait for the previous set-point to finish before executing a new set-point.
   * If you want to initiate a new set-point while the previous one is still being executed, you need to generate another rising edge of the new set-point bit in the controlword.
   */
  async applySetPoint(deviceRef: DeviceRef, setPointAckTimeout = 2000): Promise<void> {
    let controlword = await this.upload(deviceRef, 0x6040, 0);

    if ((controlword & 0x0010) > 0) {
      controlword = controlword & 0xffef; // set new set-point bit to 0
      await this.download(deviceRef, 0x6040, 0, controlword);
      // Wait for the device to drop set-point acknowledge so the next set produces a real rising edge.
      await this.waitForSetPointAcknowledge(deviceRef, false, setPointAckTimeout);
    }

    controlword = controlword | 0x0010 | 0x0020; // set new set-point and change set immediately bits to 1
    await this.download(deviceRef, 0x6040, 0, controlword);
    // Wait for the device to acknowledge the new set-point. Until it does, the target reached bit
    // (0x6041 bit 10) still reflects the previous set-point, so callers polling it would latch a stale value.
    await this.waitForSetPointAcknowledge(deviceRef, true, setPointAckTimeout);
  }

  private async waitForSetPointAcknowledge(deviceRef: DeviceRef, expected: boolean, timeoutMs: number): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (((await this.upload(deviceRef, 0x6041, 0)) & 0x1000) > 0 !== expected) {
      if (Date.now() >= deadline) {
        throw new Error(`Timed out waiting for set-point acknowledge to become ${expected ? 'set' : 'cleared'}`);
      }
      await resolveAfter(10);
    }
  }

  /**
   * Release brake.
   *
   * Releases the brake when it is software-controllable (brakeReleaseStrategy 0x2004:04 !== 0).
   * If already released, returns immediately; otherwise sets brake status (0x2004:07) to 2 and
   * waits pullTime (0x2004:03) + waitTime milliseconds before returning. The firmware blocks motion
   * and motion-related OS commands until the pull time expires.
   * Has no effect when brakeReleaseStrategy is 0 (Manual output voltage).
   *
   * @param waitTime - Additional time in milliseconds to wait on top of the brake pull time after
   *   releasing the brake (default: 50).
   */
  async releaseBrake(deviceRef: DeviceRef, waitTime = 50): Promise<void> {
    const [brakeReleaseStrategy, pullTime, brakeStatus] = (await this.uploadMany([
      [deviceRef, 0x2004, 4],
      [deviceRef, 0x2004, 3],
      [deviceRef, 0x2004, 7],
    ])) as [number, number, number];

    if (brakeReleaseStrategy !== 0 && brakeStatus !== 2) {
      await this.download(deviceRef, 0x2004, 7, 2);
      // Firmware blocks motion and motion-related OS commands until the pull time expires.
      await resolveAfter(pullTime + waitTime);
    }
  }

  /**
   * Engage brake.
   *
   * Engages the brake when it is software-controllable (brakeReleaseStrategy 0x2004:04 !== 0).
   * Sets brake status (0x2004:07) to 1 and waits waitTime milliseconds before returning.
   * Has no effect when brakeReleaseStrategy is 0 (Manual output voltage).
   *
   * @param waitTime - Time in milliseconds to wait after engaging the brake before returning (default: 50).
   */
  async engageBrake(deviceRef: DeviceRef, waitTime = 50): Promise<void> {
    const brakeReleaseStrategy = await this.upload(deviceRef, 0x2004, 4);
    if (brakeReleaseStrategy !== 0) {
      await this.download(deviceRef, 0x2004, 7, 1);
      await resolveAfter(waitTime);
    }
  }

  /**
   * Run OS command.
   *
   * The OS command is a standard way of triggering actions or services that cannot be accommodated through a single SDO upload/download.
   *
   * The Dictionary object 0x1023 is used for executing OS commands. The following is the subindex list and their meanings:
   *
   * - 1: Command (OCTET_STRING), 8 bytes
   *   - Byte 0: OS command ID, OS commands are identified by number, and here we specify which OS command to run, for example, 0 is encoder register communication
   *   - Byte 1-7: Service request data, the remaining 7 bytes serve as arguments to the OS command, specifying details such as what register to read the value from
   * - 2: Status (UNSIGNED8), 1 byte
   *   - 0: last command completed, no errors, no response data
   *   - 1: last command completed, no errors, response data available
   *   - 2: last command completed, error, no response data
   *   - 3: last command completed, error, response data available
   *   - 100-200: command executing with percentage (100 = 0%, 200 = 100%)
   *   - 255: command executing (if percentage display is not supported)
   * - 3: Response (OCTET_STRING), 8 bytes
   *   - Byte 0: Same as subindex 2
   *   - Byte 1: unused
   *   - Byte 2-7: Service response data, if the last command completed with response data, it will be available in these 6 bytes, for example, the value of the BiSS register
   *
   * Note that:
   * - As soon as the value of 1: Command Byte 0 changes, the OS command will start to execute.
   * - If the last command completed with response data, it will be available in bytes 2-7 of 3: Response subindex.
   * - The value of subindex 2: Status will be the same as that of 3: Response Byte 0.
   *
   * All possible cases of 0x1023:03 Response subindex:
   *
   * | Description                                           |  Byte 0 | Byte 1 |     Byte 2    | Byte 3 | Byte 4 | Byte 5 | Byte 6 | Byte 7 |
   * |-------------------------------------------------------|:-------:|:------:|:-------------:|:------:|:------:|:------:|:------:|:------:|
   * | Command in progress with percentage                   | 100-200 |    -   |       -       |    -   |    -   |    -   |    -   |    -   |
   * | Command in progress without percentage                |   255   |    -   |       -       |    -   |    -   |    -   |    -   |    -   |
   * | Command completed without errors and without response |    0    |    -   |       -       |    -   |    -   |    -   |    -   |    -   |
   * | Command completed without errors and with response    |    1    |    -   |      Data     |  Data  |  Data  |  Data  |  Data  |  Data  |
   * | Command completed with error and without response     |    2    |    -   |       -       |    -   |    -   |    -   |    -   |    -   |
   * | Command completed with error and with response        |    3    |    -   | OS error code |  Data  |  Data  |  Data  |  Data  |  Data  |
   *
   * Note that in the case of "Command completed without errors and with response," 6 bytes can be used for data.
   * However, only 5 bytes are available in the case of "Command completed with error and with response."
   * This limitation arises because Byte 2 in the latter response is allocated for the OS error code.
   * The OS error code serves to indicate the reason behind the error occurrence.
   *
   * 0x1023: OS command is used in combination with 0x1024: OS command mode object.
   * In our firmware, only the values 0 and 3 from the 0x1024 command mode object (USINT) are handled, and their meanings are as follows:
   * - 0: Execute the next command immediately (default)
   * - 3: Abort the current command and all commands in the buffer
   *
   * Note that if you abort the OS command, you must switch the value of 0x1024 back to 0 before executing another OS command.
   *
   * @param deviceRef The reference to the device on which the command will be executed.
   * @param command The command to be executed, represented as a Uint8Array.
   * @param commandTimeout The maximum time allowed for the command to complete.
   * @param responsePollingInterval The interval (in milliseconds) for polling the command response.
   * @param osCommandMode Specifies whether the next OS command should run immediately after the current one or abort the current command. Sets the OS Command Mode. Defaults to false.
   * @param fsBufferContent If true, the content of the fs-buffer will be read and assigned to the `fsBuffer` property of {@link OsCommandResponse}. If a Uint8Array is provided, the content will be written to the fs-buffer file. If undefined, only the OS command will be executed, and its response will be read and parsed.
   * @param fsBufferReadWriteTimeout The timeout for reading/writing the fs-buffer content, in milliseconds. Defaults to 30000.
   * @returns An observable of type T, which extends {@link OsCommandResponse}, representing the result of the command execution.
   */
  runOsCommand<T extends OsCommandResponse>(
    deviceRef: DeviceRef,
    command: Uint8Array,
    commandTimeout: number,
    responsePollingInterval = 1000,
    osCommandMode: boolean | OsCommandMode = false,
    fsBufferContent?: boolean | Uint8Array,
    fsBufferReadWriteTimeout = 30000
  ): Observable<T> {
    let response: OsCommandResponse | undefined;
    let fsBuffer: Uint8Array | number[] | null | undefined;

    // Use defer to ensure a request is made to Motion Master at each repeat step.
    const getResponse$ = defer(() =>
      this.getParameterValue<Uint8Array>(deviceRef, 0x1023, 3).pipe(
        map((value) => parseOsCommandResponse(value, command)),
        tap((r) => {
          r.fsBuffer = fsBuffer;
          response = r;
        }) // A side effect (storing to a local variable) is required for the conditional in the repeat operator.
      )
    );

    if (osCommandMode === true) {
      osCommandMode = OsCommandMode.EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY;
    }

    if (fsBufferContent instanceof Uint8Array) {
      return this.getParameterValue<string>(deviceRef, 0x100a, 0).pipe(
        // Check if streaming the fs-buffer is supported. For more details, refer to the "OS Command" documentation, "Command 11.11: Transmit Software".
        map((version) => semver.gte(version, '5.2.0')),
        mergeMap((isStreamingSupported) => {
          return concat(
            this.readOsCommandResponse(deviceRef), // Resets the OS command to idle, allowing the next command to be issued.
            typeof osCommandMode === 'number' ? this.setOsCommandMode(deviceRef, osCommandMode) : EMPTY,
            // Upload the fs-buffer content before executing the OS command, if streaming is not supported.
            !isStreamingSupported
              ? this.setFile(deviceRef, 'fs-buffer', fsBufferContent, true, fsBufferReadWriteTimeout)
              : EMPTY,
            this.setParameterValue(deviceRef, 0x1023, 1, command),
            // Upload the fs-buffer content after executing the OS command, but before reading the response, if streaming is supported.
            isStreamingSupported
              ? this.setFile(deviceRef, 'fs-buffer', fsBufferContent, true, fsBufferReadWriteTimeout)
              : EMPTY,
            getResponse$.pipe(
              repeat({
                delay: () =>
                  response && (response.request === 'succeeded' || response.request === 'failed')
                    ? EMPTY
                    : timer(responsePollingInterval),
              })
            )
          ).pipe(
            takeUntil(
              timer(commandTimeout).pipe(
                mergeMap(() =>
                  throwError(
                    () =>
                      new Error(
                        `A timeout has occurred for the OS command ${JSON.stringify(command)} on device ${deviceRef}.`
                      )
                  )
                )
              )
            ),
            filter(isOsCommandResponse),
            mapOsCommandResponse()
          ) as Observable<T>;
        })
      );
    } else {
      return concat(
        this.readOsCommandResponse(deviceRef), // Resets the OS command to idle, allowing the next command to be issued.
        typeof osCommandMode === 'number' ? this.setOsCommandMode(deviceRef, osCommandMode) : EMPTY,
        this.setParameterValue(deviceRef, 0x1023, 1, command),
        getResponse$.pipe(
          repeat({
            delay: () =>
              response && (response.request === 'succeeded' || response.request === 'failed')
                ? EMPTY
                : timer(responsePollingInterval),
          })
        )
      ).pipe(
        takeUntil(
          timer(commandTimeout).pipe(
            mergeMap(() =>
              throwError(
                () =>
                  new Error(
                    `A timeout has occurred for the OS command ${JSON.stringify(command)} on device ${deviceRef}.`
                  )
              )
            )
          )
        ),
        filter(isOsCommandResponse),
        mergeMap((response) => {
          if (response.request === 'succeeded' && fsBufferContent === true) {
            return this.getFile(deviceRef, 'fs-buffer', fsBufferReadWriteTimeout).pipe(
              map((value) => {
                response.fsBuffer = value;
                return response;
              })
            );
          }
          return of(response);
        }),
        mapOsCommandResponse()
      ) as Observable<T>;
    }
  }

  setOsCommandMode(deviceRef: DeviceRef, value: OsCommandMode) {
    return this.setParameterValue(deviceRef, 0x1024, 0, value);
  }

  /**
   * Abort the OS command in progress and leave the OS command mode ready for the next command.
   *
   * Writing 0x1024:00 = 3 is sticky - it keeps aborting every command that follows until the mode is set
   * back to 0. This method performs the whole sequence: it reads the last response to learn whether a
   * command was actually in progress, requests the abort, polls 0x1023:03 until the drive stops reporting
   * a command in progress, and always resets the mode to
   * {@link OsCommandMode.EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY} afterwards. Calling it with nothing in
   * progress is a safe way to clear a mode left at 3 by an earlier abort.
   *
   * Note that reading the response of a completed command resets the OS command to idle. The status
   * consumed that way is reported back as `lastStatus` / `lastErrorCode`.
   *
   * Unlike {@link MotionMasterReqResClient#runOsCommand}, a timeout does not throw: the returned result has
   * `request: 'failed'` and `timeout: true`, so the caller still learns what the drive last reported. Errors
   * from the underlying parameter reads and writes (an unreachable device, for example) are still thrown.
   *
   * @param deviceRef The reference to the device on which the OS command should be aborted.
   * @param commandTimeout How long to wait, in milliseconds, for the drive to stop reporting a command in progress.
   * @param responsePollingInterval The interval (in milliseconds) for polling the command response.
   */
  abortOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 250
  ): Observable<OsCommandAbortResult> {
    return defer(async () => {
      let response = await this.readOsCommandResponseOrUndefined(deviceRef);
      const wasInProgress = response?.request === 'running' || response?.request === 'started';
      let timedOut = false;

      try {
        await lastValueFrom(
          this.setOsCommandMode(deviceRef, OsCommandMode.ABORT_THE_CURRENT_COMMAND_AND_ALL_COMMANDS_IN_THE_BUFFER)
        );

        if (wasInProgress) {
          const deadline = Date.now() + commandTimeout;

          for (;;) {
            response = await this.readOsCommandResponseOrUndefined(deviceRef);

            if (response?.request !== 'running' && response?.request !== 'started') {
              break;
            }

            if (Date.now() >= deadline) {
              timedOut = true;
              break;
            }

            await lastValueFrom(timer(responsePollingInterval));
          }
        }
      } finally {
        // The abort mode is sticky, so reset it even when the abort itself failed. Otherwise the next OS
        // command issued on this device is aborted as soon as it starts.
        await lastValueFrom(this.setOsCommandMode(deviceRef, OsCommandMode.EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY));
      }

      return {
        request: timedOut ? 'failed' : 'succeeded',
        wasInProgress,
        lastStatus: response?.response?.at(0),
        lastErrorCode: response?.errorCode,
        lastErrorName: response?.errorName,
        ...(timedOut && { timeout: true }),
      } satisfies OsCommandAbortResult;
    });
  }

  /**
   * Read and parse the OS command response, resolving to `undefined` when it cannot be read or parsed.
   *
   * {@link MotionMasterReqResClient#readOsCommandResponse} throws on an unknown status byte, and reading
   * 0x1023:03 can fail outright while the drive is busy executing a command. Neither is a reason to give up
   * on {@link MotionMasterReqResClient#abortOsCommand}, which needs the abort and the mode reset to happen
   * regardless of what the drive reports.
   */
  private async readOsCommandResponseOrUndefined(deviceRef: DeviceRef): Promise<OsCommandResponse | undefined> {
    try {
      return await lastValueFrom(this.readOsCommandResponse(deviceRef));
    } catch {
      return undefined;
    }
  }

  runEncoderRegisterCommunicationOsCommand(
    deviceRef: DeviceRef,
    encoderOrdinal: number = 1,
    slaveAddress: number = 0,
    rw: number = 0,
    registerAddress: number = 0,
    registerWriteValue: number = 0,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createEncoderRegisterCommunicationOsCommand(
      encoderOrdinal,
      slaveAddress,
      rw,
      registerAddress,
      registerWriteValue
    );
    return this.runOsCommand<EncoderRegisterCommunicationOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runResetCirculoEncoderPositionOsCommand(
    deviceRef: DeviceRef,
    encoderOrdinal: number = 1,
    commandTimeout = 5000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    return this.runEncoderRegisterCommunicationOsCommand(
      deviceRef,
      encoderOrdinal,
      0,
      1,
      0x75,
      0x09,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runIcMuCalibrationModeOsCommand(
    deviceRef: DeviceRef,
    encoderOrdinal: number = 1,
    modeEnable: number = 0,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createIcMuCalibrationModeOsCommand(encoderOrdinal, modeEnable);
    return this.runOsCommand<IcMuCalibrationModeOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runOpenLoopFieldModeOsCommand(
    deviceRef: DeviceRef,
    parameterIndex: number = 0,
    parameterValue: number = 0,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createOpenLoopFieldModeOsCommand(parameterIndex, parameterValue);
    return this.runOsCommand<OpenLoopFieldModeOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runHrdStreamingOsCommand(
    deviceRef: any,
    action: HrdStreamingOsCommandAction = 0,
    dataIndex: HrdStreamingOsCommandDataIndex = 0,
    duration: number = 1000,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createHrdStreamingOsCommand(action, dataIndex, duration);
    return this.runOsCommand<HrdStreamingOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runMotorPhaseOrderDetectionOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createMotorPhaseOrderDetectionOsCommand();
    return this.runOsCommand<MotorPhaseOrderDetectionOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runCommutationOffsetMeasurementOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 30000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createCommutationOffsetMeasurementOsCommand();
    return this.runOsCommand<CommutationOffsetMeasurementOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runOpenPhaseDetectionOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createOpenPhaseDetectionOsCommand();
    return this.runOsCommand<OpenPhaseDetectionOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runPolePairDetectionOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 60000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createPolePairDetectionOsCommand();
    return this.runOsCommand<PolePairDetectionOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runPhaseResistanceMeasurementOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createPhaseResistanceMeasurementOsCommand();
    return this.runOsCommand<PhaseResistanceMeasurementOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runPhaseInductanceMeasurementOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createPhaseInductanceMeasurementOsCommand();
    return this.runOsCommand<PhaseInductanceMeasurementOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runTorqueConstantMeasurementOsCommand(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createTorqueConstantMeasurementOsCommand();
    return this.runOsCommand<TorqueConstantMeasurementOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runSmmAcyclicHandlerOsCommand(
    deviceRef: DeviceRef,
    command: Uint8Array,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false,
    fsBufferContent: boolean | Uint8Array = false,
    fsBufferReadWriteTimeout = 30000
  ) {
    return this.runOsCommand<SmmAcyclicHandlerOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode,
      fsBufferContent,
      fsBufferReadWriteTimeout
    );
  }

  runSkippedCyclesCounterOsCommand(
    deviceRef: DeviceRef,
    firmwareService = 0,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createSkippedCyclesCounterOsCommand(firmwareService);
    return this.runOsCommand<SkippedCyclesCounterOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runIgnoreBissStatusOsCommand(
    deviceRef: DeviceRef,
    trigger = 1,
    encoderOrdinal = 1,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createIgnoreBissStatusBitsOsCommand(trigger, encoderOrdinal);
    return this.runOsCommand<IgnoreBissStatusBitsOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runSystemIdentificationOsCommand(
    deviceRef: DeviceRef,
    parameterIndex: number = 0,
    parameterValue: number = 0,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createSystemIdentificationOsCommand(parameterIndex, parameterValue);
    return this.runOsCommand<SystemIdentificationOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runTriggerErrorOsCommand(
    deviceRef: DeviceRef,
    firmwareService: number = 0,
    errorType: number = 11,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    const command = createTriggerErrorOsCommand(firmwareService, errorType);
    return this.runOsCommand(deviceRef, command, commandTimeout, responsePollingInterval, osCommandMode);
  }

  runKublerEncoderCommandOsCommand(
    deviceRef: DeviceRef,
    command: Uint8Array,
    commandTimeout = 10000,
    responsePollingInterval = 1000,
    osCommandMode = false
  ) {
    return this.runOsCommand<KublerEncoderCommandOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runUseInternalEncoderVelocityOsCommand(
    deviceRef: DeviceRef,
    trigger: number = 0,
    commandTimeout: number = 10000,
    responsePollingInterval: number = 1000,
    osCommandMode = false
  ) {
    const command = createUseInternalEncoderVelocityOsCommand(trigger);
    return this.runOsCommand<UseInternalEncoderVelocityOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  runKublerEncoderRegisterCommunicationOsCommand(
    deviceRef: DeviceRef,
    rw: number = 0,
    registerAddress: number = 0,
    registerLength: number = 0,
    registerWriteValue: number = 0,
    commandTimeout: number = 10000,
    responsePollingInterval: number = 1000,
    osCommandMode = false
  ) {
    const command = createKublerEncoderRegisterCommunicationOsCommand(
      rw,
      registerAddress,
      registerLength,
      registerWriteValue
    );
    return this.runOsCommand<KublerEncoderRegisterCommunicationOsCommandResponse>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode
    );
  }

  /**
   * Resets the multiturn position of the Kubler encoder to 0.
   * This function is available starting from firmware version SW00029_MagPac_w_crc_v0_9_202410221549.bin.
   */
  resetKublerEncoderMultiturnPosition(
    deviceRef: DeviceRef
  ): Observable<KublerEncoderRegisterCommunicationOsCommandResponse> {
    return concat(
      this.runKublerEncoderRegisterCommunicationOsCommand(deviceRef, 1, 0x56, 1, 4), // set to 0
      this.runKublerEncoderRegisterCommunicationOsCommand(deviceRef, 1, 0x54, 1, 1) // save value
    );
  }

  readOsCommandResponse(deviceRef: DeviceRef): Observable<OsCommandResponse> {
    return this.getParameterValue<Uint8Array>(deviceRef, 0x1023, 3).pipe(map((value) => parseOsCommandResponse(value)));
  }

  runOsCommandAndWriteFsBuffer<T extends OsCommandResponse>(
    deviceRef: DeviceRef,
    command: Uint8Array,
    fsBufferContent: Uint8Array,
    commandTimeout: number = 30000,
    responsePollingInterval: number = 1000,
    osCommandMode: boolean | OsCommandMode = false,
    fsBufferReadWriteTimeout: number = 60000
  ): Observable<T> {
    return this.runOsCommand<T>(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      osCommandMode,
      fsBufferContent,
      fsBufferReadWriteTimeout
    );
  }

  runOsCommandAndReadFsBuffer<T extends OsCommandResponse>(
    deviceRef: DeviceRef,
    command: Uint8Array,
    commandTimeout: number = 30000,
    responsePollingInterval: number = 1000,
    osCommandMode: boolean | OsCommandMode = false
  ): Observable<T> {
    return this.runOsCommand<T>(deviceRef, command, commandTimeout, responsePollingInterval, osCommandMode, true);
  }

  /**
   * Set halt bit.
   *
   * The behavior of the halt function (bit 8) in the controlword depends on the specific operation mode of the device.
   * When the halt bit is set to 1, the commanded motion will be interrupted, and the behavior of the device will follow the defined rules for the halt option code.
   *
   * After releasing the halt function by setting the halt bit to 0, the drive device will attempt to continue the commanded motion if possible,
   * based on the specific operation mode and the conditions of the system.
   */
  async setHaltBit(deviceRef: DeviceRef, value: boolean) {
    let controlword = await this.upload(deviceRef, 0x6040, 0);

    controlword = value ? controlword | 0x0100 : controlword & 0xfeff;

    await this.download(deviceRef, 0x6040, 0, controlword);
  }

  getCachedDeviceByDeviceRefObj(deviceRefObj: DeviceRefObj): Device | undefined {
    if (deviceRefObj.deviceSerialNumber) {
      for (const device of this.devices) {
        if (device.serialNumber === deviceRefObj.deviceSerialNumber) {
          return device;
        }
      }
    } else if (typeof deviceRefObj.devicePosition === 'number') {
      for (const device of this.devices) {
        if (device.position === deviceRefObj.devicePosition) {
          return device;
        }
      }
    } else if (typeof deviceRefObj.deviceAddress === 'number') {
      for (const device of this.devices) {
        if (device.deviceAddress === deviceRefObj.deviceAddress) {
          return device;
        }
      }
    }
    return;
  }

  /**
   * Reloads the firmware by transitioning the device to the EtherCAT BOOT state, unloading the current firmware and loading the bootloader.
   * Afterward, transitions back to OP mode, reloading the firmware during the INIT to PREOP phase.
   */
  reloadFirmware(deviceRef: DeviceRef): Observable<RequestStatusMessage> {
    const deviceRefObj = makeDeviceRefObj(deviceRef);
    const State = MotionMasterMessage.Request.SetEthercatNetworkState.State;
    return concat(
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.INIT }, 5000)),
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.BOOT }, 5000)),
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.INIT }, 5000)),
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.PREOP }, 5000)),
      // The custom PDO padding applied by Motion Master is lost upon firmware reload,
      // so we must reapply the custom PDO padding to the device after reloading.
      // defer(() => this.updatePdoParameterGroupedValues(deviceRef, defaultRxPdoGroupedValues, 0x1600)),
      // defer(() => this.updatePdoParameterGroupedValues(deviceRef, defaultTxPdoGroupedValues, 0x1A00)),
      // Motion Master automatically transitions to SAFEOP when an OP request is made.
      // This is necessary becase the map_pdos call on VDM is triggered only by a transition request from PREOP to OP.
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.SAFEOP }, 5000)),
      defer(() => this.setEthercatNetworkState({ ...deviceRefObj, state: State.OP }, 5000))
    ).pipe(map(() => ({ request: 'succeeded' } as RequestStatusMessage)));
  }

  /**
   * Performs a factory reset by deleting all files except those specified in the `filesToKeep` argument.
   *
   * Optionally installs an empty firmware after the file removal.
   *
   * If empty firmware is not installed:
   * - The currently installed firmware can optionally be reloaded.
   * - The file SOMANET_CiA_402.xml and its parts will not be removed.
   *
   * To retain multipart files (e.g., SOMANET_CiA_402.xml.zip.part000), include only the base name (e.g., "SOMANET_CiA_402.xml") in the `filesToKeep` list.
   */
  factoryReset(
    deviceRef: DeviceRef,
    filesToKeep = factoryResetDefaultFilesToKeep,
    installEmptyFirmware = true,
    reloadFirmware = false
  ): Observable<RequestStatusMessage> {
    return this.getFiles(deviceRef).pipe(
      mergeMap((files) => {
        if (!installEmptyFirmware) {
          filesToKeep.push('SOMANET_CiA_402.xml', 'stack_image.svg');
        }

        const filesToDelete = files.filter(
          (file) => file !== '' && !filesToKeep.some((fileToKeep) => file.startsWith(fileToKeep))
        );

        const fileDeletionObservables = filesToDelete.map((file) => {
          if (file.startsWith('.')) {
            return defer(() =>
              this.getFile(deviceRef, stackUnlockFilename).pipe(mergeMap(() => this.deleteFile(deviceRef, file)))
            );
          } else {
            return defer(() => this.deleteFile(deviceRef, file));
          }
        });

        return fileDeletionObservables.length > 0 ? forkJoin(fileDeletionObservables) : of(true); // continue even if there are no files to delete
      }),
      mergeMap(() => {
        if (installEmptyFirmware) {
          return this.startDeviceFirmwareInstallation(
            {
              ...makeDeviceRefObj(deviceRef),
              firmwarePackageContent: new Uint8Array(base64ToArrayBuffer(emptyAppBase64)),
              skipSiiInstallation: true,
            },
            120000
          );
        } else if (reloadFirmware) {
          return this.reloadFirmware(deviceRef);
        }
        return of({ request: 'succeeded' } as RequestStatusMessage);
      })
    );
  }

  /**
   * Computes the Feed Constant for a given device.
   *
   * The Feed Constant is calculated as the quotient of:
   * - `0x6092:01`: Feed
   * - `0x6092:02`: Shaft Revolutions
   *
   * @param deviceRef - Reference to the device for which the Feed Constant is computed.
   * @param requestTimeout - Optional timeout in milliseconds for the parameter request (default: 5000).
   * @returns An Observable that emits the computed Feed Constant as a number.
   */
  computeFeedConstant(deviceRef: DeviceRef, requestTimeout = 5000): Observable<number> {
    return this.getParameterValues(
      [
        [deviceRef, 0x6092, 1], // Feed
        [deviceRef, 0x6092, 2], // Shaft revolutions
      ],
      false,
      requestTimeout
    ).pipe(map((values) => (values[0] as number) / (values[1] as number)));
  }

  /**
   * Computes the mechanical Gear Ratio for the specified device.
   *
   * The Gear Ratio is derived from device parameters representing:
   * - Motor-side revolutions (`0x6091:01`)
   * - Shaft-side revolutions (`0x6091:02`)
   *
   * Additionally, encoder assignments for the torque and position control loops
   * are retrieved to determine whether an external mechanical ratio is present.
   *
   * If both control loops use the same encoder source (non-zero and equal),
   * the returned ratio is `1`, indicating no external mechanical reduction.
   *
   * Otherwise, the ratio is computed as:
   *
   *    motorRevolutions / shaftRevolutions
   *
   * If shaftRevolutions is `0` or unavailable, a fallback value of `1` is returned.
   *
   * @param deviceRef Reference to the device whose Gear Ratio is computed.
   * @param loadFromCache If true, previously cached parameter values will be used instead of initiating device requests.
   * @param requestTimeout Timeout (in milliseconds) applied when reading parameter values from the device. Defaults to `5000`.
   *
   * @returns An Observable emitting a numeric Gear Ratio.
   */
  computeGearRatio(deviceRef: DeviceRef, loadFromCache = false, requestTimeout = 5000): Observable<number> {
    return this.getParameterValues(
      [
        [deviceRef, 0x6091, 0x01], // Motor revolutions (numerator)
        [deviceRef, 0x6091, 0x02], // Shaft revolutions (denominator)
        [deviceRef, 0x2010, 0x0c], // Torque controller encoder source
        [deviceRef, 0x2012, 0x09], // Position controller encoder source
      ],
      loadFromCache,
      requestTimeout
    ).pipe(
      map((values) => {
        const [motorRevolutions, shaftRevolutions, torqueControllerEncoderSource, positionControllerEncoderSource] =
          values as number[];

        return computeGearRatio({
          motorRevolutions,
          shaftRevolutions,
          torqueControllerEncoderSource,
          positionControllerEncoderSource,
        });
      })
    );
  }

  /**
   * Save the modified parameter values to the config.csv file so that they are used the next time the device is power cycled.
   * The configuration is saved using the 0x1010: Store parameters object.
   */
  saveConfig(deviceRef: DeviceRef): Observable<void> {
    return this.storeParameters(deviceRef);
  }

  /**
   * Replace or merge the contents of the config.csv file with the provided data and instruct the device to load the updated configuration.
   *
   * The new configuration is loaded using the `0x1011:01 Restore All Default Parameters` command.
   *
   * This function performs several key operations to ensure consistency:
   * 1. **Resolve:** Determines the final content based on the strategy (Replace vs Merge).
   * 2. **Upload:** Writes the content to `config.csv` on the device.
   * 3. **Load:** Triggers the device to load the new settings from the file.
   * 4. **PDO Guard:** Identifies parameters mapped to RxPDOs and re-applies their values via SDO. This prevents the master's cyclic data (which may be zero) from immediately overwriting the new configuration.
   * 5. **Refresh:** Optionally reads the final values back from the device to update the local Motion Master cache.
   *
   * @param deviceRef A reference to the device where the configuration will be applied.
   * @param data The new configuration data to be written to the config.csv file (string or Uint8Array).
   * @param strategy The strategy to use when loading the configuration.
   * - `'replace'` (default): Overwrites the existing configuration file completely.
   * - `'merge'`: Reads the existing file, appends/updates it with the new data (input takes precedence), and writes the result back.
   * @param retryConfig Configuration settings for retrying the restore default parameters operation.
   * @param refresh A flag to refresh device parameter values after loading the new configuration. Defaults to `true`.
   * @returns An observable that emits `void` once the configuration has been successfully loaded and (optionally) refreshed.
   */
  loadConfig(
    deviceRef: DeviceRef,
    data: Uint8Array | string,
    strategy: LoadConfigStrategy = 'replace',
    retryConfig: RetryConfig = { count: 10, delay: 500 },
    refresh = true
  ): Observable<void> {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    const content = typeof data === 'string' ? encoder.encode(data) : data;

    // Prepare the content and the list of parameters to be used in later steps.
    // This resolves the "Merge" vs "Replace" logic upfront.
    const prepareConfig$ = defer(() => {
      const inputCsv = decoder.decode(content);
      const inputConfigFile = new ConfigFile(inputCsv);

      if (strategy === 'replace') {
        return of({
          contentToWrite: content,
          finalParameters: inputConfigFile.parameters,
        });
      } else {
        // Merge strategy
        return this.getDecodedFile(deviceRef, 'config.csv').pipe(
          map((deviceCsv) => {
            const deviceConfigFile = new ConfigFile(deviceCsv);

            // Merge: Input overwrites Device.
            const mergedParams = [...inputConfigFile.parameters, ...deviceConfigFile.parameters]
              .filter(
                (item, index, array) =>
                  array.findIndex((obj) => obj.index === item.index && obj.subindex === item.subindex) === index
              )
              .sort((a, b) => a.index - b.index || a.subindex - b.subindex);

            deviceConfigFile.parameters = mergedParams;

            return {
              contentToWrite: encoder.encode(deviceConfigFile.toString()),
              finalParameters: mergedParams,
            };
          })
        );
      }
    });

    const deviceRefObj = makeDeviceRefObj(deviceRef);

    return prepareConfig$.pipe(
      // Write the file (using the resolved content)
      mergeMap(({ contentToWrite, finalParameters }) =>
        this.setFile(deviceRef, 'config.csv', contentToWrite, true).pipe(
          map(() => finalParameters) // Pass parameters to next step
        )
      ),

      // Restore Default Parameters (Load the file on device)
      mergeMap((finalParameters) =>
        this.restoreDefaultParameters(
          deviceRef,
          RestoreDefaultParametersType.RESTORE_ALL_DEFAULT_PARAMETERS,
          retryConfig
        ).pipe(
          map(() => finalParameters) // Pass parameters to next step
        )
      ),

      // Apply config values mapped to RxPDOs (Using finalParameters).
      // This ensures that the master doesn't override the values we just set from the config file.
      mergeMap((finalParameters) =>
        this.getUiConfigOrDefaultPdoMapping(deviceRef).pipe(
          mergeMap((pdoMapping) => {
            const entries = Object.values(pdoMapping.rx) // 1. Get all RxPDO arrays
              .flat() // 2. Flatten into one list of ID strings
              .map((id) => parsePdoMappingEntry(id)); // 3. Parse strings into objects
            const mappedKeys = new Set(entries.map((entry) => `${entry.index}:${entry.subindex}`)); // 4. Create a set of "index:subindex" keys for quick lookup

            // 5. Filter finalParameters to only those that are mapped to RxPDOs
            const pdoMappedParams = finalParameters.filter((p) => mappedKeys.has(`${p.index}:${p.subindex}`));

            // If no parameters are mapped to RxPDOs, skip the write step
            if (!pdoMappedParams.length) {
              return of(finalParameters);
            }

            // Batch write all RxPDO-mapped parameters in one request
            const ids = pdoMappedParams.map(
              (p) => [deviceRef, p.index, p.subindex, p.value] as DeviceParameterAddressValue
            );
            return this.setParameterValues(ids, 10000).pipe(map(() => finalParameters));
          })
        )
      ),

      // Refresh parameters (Using finalParameters).
      mergeMap((finalParameters) => {
        if (!refresh) {
          return of(undefined); // Emit undefined instead of completing empty
        }

        return this.getDeviceParameterValues(
          {
            ...deviceRefObj,
            // Now safely using the complete list of parameters
            parameters: finalParameters.map((p) => ({
              index: p.index,
              subindex: p.subindex,
              loadFromCache: false,
            })),
            sendProgress: false,
          },
          30000
        ).pipe(map(() => undefined));
      })
    );
  }

  /**
   * Checks if the device has firmware by reading the Manufacturer Software Version parameter (0x100A:00).
   */
  hasFirmware(deviceRef: DeviceRef): Observable<boolean> {
    return this.getParameterValue(deviceRef, 0x100a, 0).pipe(
      map((value) => (value ? true : false)),
      catchError(() => of(false))
    );
  }

  /**
   * Reads the contents of configuration CSV files in the specified order and returns a list of unique configuration parameters.
   * If the files are provided in the order: '.factory_config', 'config.csv', '.assembly_config', this function
   * mimics the firmware's behavior during "Restore All Default Parameters," retaining only the most recent value of any duplicated parameter.
   *
   * If config files do not exist or cannot be parsed, the function will continue executing for any files that can be processed.
   */
  getConfigFileParameters(
    deviceRef: DeviceRef,
    filenames: string[] = ['.factory_config', 'config.csv', '.assembly_config']
  ): Observable<ConfigParameter[]> {
    return from(filenames).pipe(
      concatMap((name) => {
        // Retrieve and decode each config file into a UTF-8 string.
        return this.getDecodedFile(deviceRef, name).pipe(
          map((content) => {
            const configFile = new ConfigFile(content); // Parse the content and create config parameters.
            return configFile.parameters;
          }),
          catchError(() => of([])) // Handles potential errors, such as a missing file or failure to instantiate ConfigFile due to parsing issues.
        );
      }),
      toArray(), // Collect all emitted parameters into a single array and emit them as a single emission after completion.
      map((allParameters) => {
        const parameters = allParameters.flat(); // Flatten the array into a single list of parameters.
        // Filter out duplicate parameters by 'index' and 'subindex', keeping only the most recent one.
        return Array.from(
          parameters
            .reduce((map, param) => {
              const key = makeParameterId(param.index, param.subindex);
              map.set(key, param); // Replace any existing entry with the same key.
              return map;
            }, new Map<string, ConfigParameter>())
            .values()
        );
      })
    );
  }

  /**
   * Retrieves the firmware version of the Kübler encoder by reading register address 4.
   * The returned number follows this format: 9240828, representing version 9 from August 28, 2024.
   */
  getIntegroEncoderFirmwareVersion(
    deviceRef: DeviceRef
  ): Observable<KublerEncoderRegisterCommunicationOsCommandResponse> {
    return this.runKublerEncoderRegisterCommunicationOsCommand(deviceRef, 0, 4, 4);
  }

  /**
   * Reads and parses the error register from an Integro integrated encoder.
   *
   * This method sends a command to the specified device to read the encoder error
   * register (register address 0x62, length 4 bytes) using the Kübler Encoder
   * Register Communication OS command protocol.
   *
   * The raw 32-bit register value returned by the device is automatically parsed
   * into a structured object containing:
   * - the extracted 16-bit error code,
   * - the associated 16-bit debug information,
   * - an optional human-readable error description if the code is recognized.
   *
   * @param deviceRef Reference to the target device from which to read the error register.
   * @returns An Observable that emits the parsed encoder error details.
   */
  readoutIntegroIntegratedEncoderError(deviceRef: DeviceRef): Observable<IntegroIntegratedEncoderError> {
    return this.runKublerEncoderRegisterCommunicationOsCommand(deviceRef, 0, 0x62, 4, 0, 5000).pipe(
      map((response) => parseIntegroIntegratedEncoderError(response.kublerRegisterValue))
    );
  }

  /**
   * @todo Document this method and consider removing the void return type.
   */
  runPhaseResistanceMeasurement(deviceRef: DeviceRef): Observable<void | PhaseInductanceMeasurementOsCommandResponse> {
    return concat(
      this.setModesOfOperationAndTransitionToCia402State(
        deviceRef,
        ModesOfOperation.DIAGNOSTICS_MODE,
        Cia402State.OPERATION_ENABLED
      ),
      this.runPhaseResistanceMeasurementOsCommand(deviceRef)
    );
  }

  /**
   * Sets the device mode of operation and then transitions the device
   * to the specified CiA 402 state.
   *
   * The operations are executed sequentially:
   * 1. The mode of operation is written to the device.
   * 2. The device is transitioned to the requested CiA 402 state.
   *
   * @param deviceRef - Reference to the target device.
   * @param modesOfOperation - Desired CiA 402 mode of operation.
   * @param cia402State - Target CiA 402 state to transition to.
   *
   * @returns Observable that completes when both operations finish.
   */
  setModesOfOperationAndTransitionToCia402State(
    deviceRef: DeviceRef,
    modesOfOperation: ModesOfOperation,
    cia402State: Cia402State
  ): Observable<void> {
    return concat(
      this.setModesOfOperation(deviceRef, modesOfOperation),
      this.transitionToCia402State(deviceRef, cia402State)
    );
  }

  /**
   * Installs the Integro Encoder Firmware by performing the following steps:
   * 1. Switches to the Kubler encoder bootloader firmware.
   * 2. Validates the firmware update size.
   * 3. Erases the existing encoder firmware.
   * 4. Writes the new firmware.
   * 5. Exits bootloader mode.
   *
   * @param deviceRef The reference to the device on which the firmware will be installed.
   * @param content The firmware content to be written, provided as a Uint8Array.
   * @returns An observable that emits {@link KublerEncoderCommandOsCommandResponse} upon completion of the installation.
   */
  installIntegroEncoderFirmware(
    deviceRef: DeviceRef,
    content: Uint8Array
  ): Observable<KublerEncoderCommandOsCommandResponse> {
    const switchToBootloaderFirmwareCommand = createKublerEncoderOsCommand(
      KublerEncoderSubcommandId.SWITCH_TO_BOOTLOADER_FIRMWARE
    );
    const checkFirmwareUpdateSizeCommand = createKublerEncoderCommandCheckFirmwareUpdateSizeOsCommand(
      content.byteLength
    );
    const eraseFirmwareCommand = createKublerEncoderOsCommand(KublerEncoderSubcommandId.ERASE_FIRMWARE);
    const writeFirmwareCommand = createKublerEncoderCommandWriteFirmwareOsCommand(content.byteLength);
    const exitBootloaderModeCommand = createKublerEncoderOsCommand(KublerEncoderSubcommandId.EXIT_BOOTLOADER_MODE);

    return concat(
      this.runKublerEncoderCommandOsCommand(deviceRef, switchToBootloaderFirmwareCommand),
      this.runKublerEncoderCommandOsCommand(deviceRef, checkFirmwareUpdateSizeCommand),
      this.runKublerEncoderCommandOsCommand(deviceRef, eraseFirmwareCommand),
      this.runOsCommandAndWriteFsBuffer(deviceRef, writeFirmwareCommand, content),
      this.runKublerEncoderCommandOsCommand(deviceRef, exitBootloaderModeCommand)
    );
  }

  /**
   * @link https://www.wrike.com/open.htm?id=1375749957
   *
   * @todo: Verify if the device is an ACTILINK-S device and check the corresponding variant file.
   */
  async isIntegroWithMultiturnEncoder(deviceRef: DeviceRef): Promise<boolean> {
    const hardwareDescription = await lastValueFrom(this.getHardwareDescription(deviceRef));

    if (hardwareDescription) {
      const id = hardwareDescription?.device?.id;

      if (isSomanetProductIdInRange(id, 'Integro')) {
        // The product ID 9006 does not have a Wiegand counter (MT).
        if (id === '9006') {
          return false;
        }

        // The presence of MT in product ID 9010 is determined by the variant file.
        if (id === '9010') {
          const integroVariant = await lastValueFrom(this.getIntegroVariant(deviceRef));
          return !!integroVariant?.selectedOptionId.multiturnAbility[0];
        }

        // All other Integro devices are equipped with MT.
        return true;
      }
    }

    return false;
  }

  /**
   * Fetches the UI feature configuration for a specified device.
   *
   * @param deviceRef - A reference to the target device.
   * @returns An object containing the UI feature configuration.
   */
  async getUIFeaturesConfig(deviceRef: DeviceRef): Promise<UIFeaturesConfig> {
    // Create a default UI features configuration object.
    const uiFeaturesConfig: UIFeaturesConfig = { setupWizard: 'default' };

    // Check if the device is an ACTILINK-S device.
    // If the device is an ACTILINK-S device, the setup wizard should be set to 'actilink'.
    const hardwareDescription = await lastValueFrom(this.getHardwareDescription(deviceRef));
    if (hardwareDescription?.assembly && isSomanetProductIdInRange(hardwareDescription.assembly?.id, 'ACTILINK-S')) {
      uiFeaturesConfig.setupWizard = 'actilink';
    }

    // Read the parameters from the assembly configuration file.
    uiFeaturesConfig.parameters = await lastValueFrom(this.getConfigFileParameters(deviceRef, ['.assembly_config']));

    // Parameters in the assembly configuration file are read-only.
    // Set write access to false to prevent modifications.
    uiFeaturesConfig.parameters?.map((parameter) => (parameter.writeAccess = false));

    // Check if the encoder configuration is disabled.
    // Encoder configuration is disabled if the parameters 0x2110 and 0x2112 are present in the assembly configuration file.
    uiFeaturesConfig.encoderConfigurationDisabled = uiFeaturesConfig.parameters?.some(
      (parameter) => parameter.index === 0x2110 || parameter.index === 0x2112
    );

    // Determine which encoder configurations are disabled.
    // An encoder configuration is considered disabled if the assembly configuration file
    // contains parameter 0x2110 or 0x2112 with subindex 1, indicating it's assigned to a port.
    uiFeaturesConfig.disabledEncoderConfigurationIndexes = [];
    [0x2110, 0x2112].forEach((index) => {
      if (uiFeaturesConfig.parameters?.some((p) => p.index === index && p.subindex === 1)) {
        uiFeaturesConfig.disabledEncoderConfigurationIndexes?.push(index);
      }
    });

    // 0x607B: The position range limit is a special case. While the EtherCAT conformance test requires these parameters to be flagged as "rw",
    // users are not permitted to modify them because the firmware adjusts these values based on the encoder type and resolution.
    // To enforce this restriction, we set them as read-only here.
    uiFeaturesConfig.parameters ??= [];
    for (let i = 1; i <= 2; i++) {
      const parameter = uiFeaturesConfig.parameters.find(
        (parameter) => parameter.index === 0x607b && parameter.subindex === i
      );
      if (parameter) {
        parameter.writeAccess = false;
      } else {
        uiFeaturesConfig.parameters.push({ index: 0x607b, subindex: i, writeAccess: false });
      }
    }

    return uiFeaturesConfig;
  }

  /**
   * Updates the grouped PDO (Process Data Object) parameter values for a specific device.
   *
   * This function processes the provided `pdoGroupedValues` sequentially. Each group of PDO values is filtered,
   * mapped, and padded with zeros (if necessary), before being sent to the device at the specified `pdoIndex`.
   * The update is done in order to ensure each group is updated one by one.
   *
   * The function performs the following steps for each grouped value:
   * 1. Clears the current PDO mapping for the specified device and PDO index.
   * 2. Sets the new parameter values.
   * 3. Updates the number of mapped parameters in the PDO.
   *
   * @param {DeviceRef} deviceRef - The reference to the device whose PDO parameters will be updated.
   * @param {number[][]} pdoGroupedValues - A 2D array where each inner array represents a group of PDO values.
   *                                         The first element in each group is ignored, and the remaining values are processed.
   * @param {0x1600 | 0x1A00} pdoIndex - The PDO index to update, either `0x1600` for RxPDO or `0x1A00` for TxPDO.
   *
   * @returns {Promise<void>} Resolves when all PDO parameter updates are complete.
   *                          The function processes and applies updates sequentially to ensure they are performed in order.
   */
  async updatePdoParameterGroupedValues(
    deviceRef: DeviceRef,
    pdoGroupedValues: number[][],
    pdoIndex: 0x1600 | 0x1a00
  ): Promise<void> {
    for (const [i, groupedValues] of pdoGroupedValues.entries()) {
      let values = groupedValues
        .slice(1)
        .filter((value) => value !== 0)
        .map((value) => (typeof value === 'string' ? parseInt(value, 10) : value));
      const n = values.length; // number of new mapped parameters
      values = values.concat(new Array(10 - n).fill(0));

      await lastValueFrom(this.setParameterValues([[deviceRef, pdoIndex + i, 0, 0]]));

      const parameterValues = values.reduce((acc, value, j) => {
        acc.push([deviceRef, pdoIndex + i, j + 1, value, 'uintValue']);
        return acc;
      }, [] as DeviceParameterAddressValue[]);

      await lastValueFrom(this.setParameterValues(parameterValues));
      await lastValueFrom(this.setParameterValues([[deviceRef, pdoIndex + i, 0, n]]));
    }
  }

  /**
   * Reads the SMM parameter for a given device.
   *
   * @param deviceRef - The reference to the device to which the command will be sent.
   * @param index - The index of the SMM parameter to read.
   * @param subindex - The subindex of the SMM parameter to read. Defaults to 0.
   * @param commandTimeout - The timeout for the command in milliseconds.
   * @param responsePollingInterval - The interval between polling attempts for a response in milliseconds.
   *
   * @returns An observable that emits the response from the command once it is received.
   */
  readSmmParameter(
    deviceRef: DeviceRef,
    index: number,
    subindex: number = 0,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<OsCommandResponse> {
    const command = createSmmAcyclicHandlerReadSmmParameterOsCommand(index, subindex);
    return this.runOsCommandAndReadFsBuffer(
      deviceRef,
      command,
      commandTimeout,
      responsePollingInterval,
      OsCommandMode.EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY
    ).pipe(last());
  }

  /**
   * Reads the SMM parameter value as a number.
   *
   * This method reads a parameter from a device and extracts its value based on the provided `dataViewExtractor`.
   * The default behavior is to extract a 32-bit integer in little-endian format.
   *
   * @param deviceRef - A reference to the device from which the SMM parameter is being read.
   * @param index - The index of the parameter to read.
   * @param subindex - The subindex of the parameter (default is 0).
   * @param dataViewExtractor - A function that extracts a number from the `DataView` (default is `view.getInt32(0, true)`).
   *
   * @returns An observable that emits the extracted number value.
   *
   * @throws {Error} If the response buffer is empty or invalid.
   */
  readSmmParameterNumberValue(
    deviceRef: DeviceRef,
    index: number,
    subindex = 0,
    dataViewExtractor: (view: DataView) => number = (view) => view.getInt32(0, true)
  ): Observable<number> {
    return this.readSmmParameter(deviceRef, index, subindex).pipe(
      map((response) => {
        if (!response.fsBuffer) {
          throw new Error('Reading SMM parameter failed. The response buffer is empty.');
        }
        const buffer = Uint8Array.from(response.fsBuffer);
        const view = new DataView(buffer.buffer);
        return dataViewExtractor(view);
      })
    );
  }

  /**
   * Reads the SMM parameter value as a string.
   *
   * This method reads a parameter from a device and decodes its value as a UTF-8 string from the response buffer.
   *
   * @param deviceRef - A reference to the device from which the SMM parameter is being read.
   * @param index - The index of the parameter to read.
   * @param subindex - The subindex of the parameter (default is 0).
   *
   * @returns An observable that emits the decoded string value.
   *
   * @throws {Error} If the response buffer is empty or invalid.
   */
  readSmmParameterStringValue(deviceRef: DeviceRef, index: number, subindex = 0): Observable<string> {
    return this.readSmmParameter(deviceRef, index, subindex).pipe(
      map((response) => {
        if (!response.fsBuffer) {
          throw new Error('Reading SMM parameter failed. The response buffer is empty.');
        }
        const buffer = Uint8Array.from(response.fsBuffer);
        return new TextDecoder().decode(buffer);
      })
    );
  }

  /**
   * Reads the SMM parameter value as an array of numbers.
   *
   * This method reads a parameter from a device and returns the values as an array of numbers,
   * where each element corresponds to a byte from the response buffer.
   *
   * @param deviceRef - A reference to the device from which the SMM parameter is being read.
   * @param index - The index of the parameter to read.
   * @param subindex - The subindex of the parameter (default is 0).
   *
   * @returns An observable that emits an array of numbers, representing the byte values in the response buffer.
   *
   * @throws {Error} If the response buffer is empty or invalid.
   */
  readSmmParameterArrayValue(deviceRef: DeviceRef, index: number, subindex = 0): Observable<number[]> {
    return this.readSmmParameter(deviceRef, index, subindex).pipe(
      map((response) => {
        if (!response.fsBuffer) {
          throw new Error('Reading SMM parameter failed. The response buffer is empty.');
        }
        const buffer = Uint8Array.from(response.fsBuffer);
        return Array.from(buffer);
      })
    );
  }

  /**
   * Reads the SMM firmware version from a device as an array of bytes.
   *
   * This method reads the firmware version (MCU Software Version 0x0001:00) of the device as a tuple of two numbers.
   * The first byte represents the minor version, and the second byte represents the major version.
   *
   * @param deviceRef - A reference to the device from which the firmware version is being read.
   *
   * @returns An observable that emits a tuple of two numbers, representing the byte values of the firmware version.
   */
  readSmmFirmwareVersion(deviceRef: DeviceRef): Observable<[number, number]> {
    return this.readSmmParameterArrayValue(deviceRef, 0x0001, 0).pipe(map((bytes) => [bytes[0], bytes[1]]));
  }

  /**
   * Reads the SMM firmware version from a device as a number.
   *
   * This function retrieves a specific parameter from the device's SMM memory
   * and interprets the result as a 16-bit version number.
   *
   * @param deviceRef - A reference to the target device.
   * @returns An `Observable<number>` emitting the firmware version, represented as an integer.
   */
  readSmmFirmwareVersionAsNumber(deviceRef: DeviceRef): Observable<number> {
    return this.readSmmFirmwareVersion(deviceRef).pipe(map((bytes) => (bytes[1] << 8) | bytes[0]));
  }

  /**
   * Resolves the SMM parameter structure version for the specified device based on its firmware version.
   *
   * @param deviceRef - The reference to the target device.
   *
   * @returns An Observable that emits the appropriate SMM parameter structure version:
   *          - 0x0041 for firmware versions below 2.3 (0x0203).
   *          - 0x0203 for firmware versions 2.3 (0x0203) and above.
   *
   * @remarks
   * - The function first reads the firmware version of the device.
   * - Based on the firmware version, it returns the corresponding parameter structure version:
   *   - If the firmware version is less than 0x0203, the parameter structure version is 0x0041.
   *   - If the firmware version is 0x0203 or greater, the parameter structure version is 0x0203.
   */
  resolveSmmParameterStructureVersion(deviceRef: DeviceRef): Observable<number> {
    return this.readSmmFirmwareVersionAsNumber(deviceRef).pipe(
      map((version) => {
        if (version < 0x0203) {
          return 0x0041;
        } else {
          return 0x0203;
        }
      })
    );
  }

  /**
   * Reads the SMM serial number from a device.
   *
   * This method reads the serial number of the device as a string.
   * The serial number is retrieved from the device using the appropriate SMM parameter (0x0003:00).
   *
   * @param deviceRef - A reference to the device from which the serial number is being read.
   *
   * @returns An observable that emits the serial number as a string.
   */
  readSmmSerialNumber(deviceRef: DeviceRef): Observable<string> {
    return this.readSmmParameterStringValue(deviceRef, 0x0003, 0);
  }

  /**
   * Reads the SMM temperature value from a device.
   *
   * This method reads the temperature of the device as a number. The temperature is retrieved from the device
   * using the appropriate SMM parameter (0x000a:00).
   *
   * @param deviceRef - A reference to the device from which the temperature is being read.
   *
   * @returns An observable that emits the temperature value as a number.
   */
  readSmmTemperature(deviceRef: DeviceRef): Observable<number> {
    return this.readSmmParameterNumberValue(deviceRef, 0x000a, 0);
  }

  /**
   * Reads the SMM parameter check value from a device.
   *
   * This method reads the parameter check value from the device as a 16-bit unsigned integer. The value represents
   * the number of parameters available on the SMM and is retrieved from the device using the appropriate SMM parameter (0x000b:00).
   * and the custom data extraction function.
   *
   * @param deviceRef - A reference to the device from which the parameter check value is being read.
   *
   * @returns An observable that emits the parameter check value as a 16-bit unsigned integer, which represents
   *          the number of parameters available on the SMM.
   */
  readSmmParameterCheck(deviceRef: DeviceRef): Observable<number> {
    return this.readSmmParameterNumberValue(deviceRef, 0x000b, 0, (view) => view.getUint16(0, true));
  }

  /**
   * Reads the SMM maximum position discrepancy value from a device.
   *
   * This method reads the maximum position discrepancy value from the device as a 32-bit unsigned integer.
   * The value is retrieved from the device using the appropriate SMM parameter and the custom data extraction function.
   *
   * @param deviceRef - A reference to the device from which the maximum position discrepancy value is being read.
   *
   * @returns An observable that emits the maximum position discrepancy value as a 32-bit unsigned integer.
   */
  readSmmMaxPositionDiscrepancy(deviceRef: DeviceRef): Observable<number> {
    return this.readSmmParameterNumberValue(deviceRef, 0x0011, 0, (view) => view.getUint32(0, true));
  }

  /**
   * Logs into the SMM for parameter download with the provided credentials.
   *
   * This function sends a login command to the device using the provided username and password. It returns an observable
   * that emits `true` if the login is successful and `false` otherwise. If the device is already logged in, the next login
   * attempt will fail unless the user logs out beforehand.
   *
   * @param deviceRef - The reference to the device to which the login command will be sent.
   * @param username - The name of the user who logs in, 4 to 24 printable ASCII characters. It tells who configured
   *   the SMM, so pass the real user's name.
   * @param password - The password used for logging in to the SMM, 1 to 24 printable ASCII characters.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the username or the password is not valid, in which case nothing is sent, or if the login
   *   attempt fails.
   *
   * @remarks
   * If the device is already logged in, the next login attempt will fail unless the user logs out first.
   */
  loginToSmmForParameterDownload(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<void> {
    return defer(() => {
      assertValidSmmCredential(username, 'Username', SMM_USERNAME_MIN_LENGTH);
      assertValidSmmCredential(password, 'Password');
      const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.PARAMETER_DOWNLOAD_LOGIN);
      const content = packSmmCredentials(username, password);
      // @ts-ignore
      return this.runOsCommandAndWriteFsBuffer(deviceRef, command, content, commandTimeout, responsePollingInterval);
    }).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Login to SMM for parameter download failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Logs into the SMM for software update with the provided credentials.
   *
   * This function sends a login command to the device using the provided username and password. It returns an observable
   * that emits `true` if the login is successful and `false` otherwise. If the device is already logged in, the next login
   * attempt will fail unless the user logs out beforehand.
   *
   * @param deviceRef - The reference to the device to which the login command will be sent.
   * @param username - The name of the user who logs in, 4 to 24 printable ASCII characters. It tells who configured
   *   the SMM, so pass the real user's name.
   * @param password - The password used for logging in to the SMM, 1 to 24 printable ASCII characters.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the username or the password is not valid, in which case nothing is sent, or if the login
   *   attempt fails.
   *
   * @remarks
   * If the device is already logged in, the next login attempt will fail unless the user logs out first.
   */
  loginToSmmForSoftwareUpdate(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    commandTimeout = 10000,
    responsePollingInterval = 500
  ): Observable<void> {
    return defer(() => {
      assertValidSmmCredential(username, 'Username', SMM_USERNAME_MIN_LENGTH);
      assertValidSmmCredential(password, 'Password');
      const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.SOFTWARE_UPDATE_LOGIN);
      const content = packSmmCredentials(username, password);
      // @ts-ignore
      return this.runOsCommandAndWriteFsBuffer(deviceRef, command, content, commandTimeout, responsePollingInterval);
    }).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Login to SMM for software update failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Logs out from the SMM.
   *
   * This function sends a logout command to the device and returns an observable that emits `true` if the logout
   * is successful and `false` otherwise. The command times out after 10 seconds if no response is received.
   *
   * @param deviceRef - The reference to the device to which the logout command will be sent.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the logout attempt fails.
   */
  logoutFromSmm(deviceRef: DeviceRef, commandTimeout = 10000, responsePollingInterval = 500): Observable<void> {
    const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.LOGOUT);
    return this.runOsCommand(deviceRef, command, commandTimeout, responsePollingInterval).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Logout from SMM failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Logs out from the SMM and then logs back in with the provided credentials for parameter download.
   *
   * This function first sends a logout command to the device, followed by a login command using the provided username
   * and password. It returns an observable that emits `true` if both the logout and login attempts are successful
   * and `false` otherwise. The login process is handled through the `loginToSmmForParameterDownload` function.
   *
   * @param deviceRef - The reference to the device to which the logout and login commands will be sent.
   * @param username - The username used for logging in to the SMM.
   * @param password - The password used for logging in to the SMM.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If either the logout or login attempt fails.
   */
  reloginToSmmForParameterDownload(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    commandTimeout = 10000,
    responsePollingInterval = 500
  ): Observable<void> {
    return this.logoutFromSmm(deviceRef, commandTimeout, responsePollingInterval).pipe(
      mergeMap(() =>
        this.loginToSmmForParameterDownload(deviceRef, username, password, commandTimeout, responsePollingInterval)
      )
    );
  }

  /**
   * Changes the SMM password for the specified device.
   *
   * The sequence is: write `<newPassword><oldPassword>` to the fs-buffer and run CHANGE_PASSWORD. If, and only if, the
   * SMM accepts the change, log in for parameter download with the new password until the SMM accepts the login. The
   * SMM is not restarted, because it does not need a restart to use a new password.
   *
   * The SMM acknowledges CHANGE_PASSWORD before it stores the new password. It writes the password to its EEPROM in the
   * background, refuses every login while the write runs, and closes the session itself when the write is done. A
   * restart during the write leaves a stored password that the SMM discards at boot, so neither the new nor the old
   * password logs in and only the master password gets in again. A logout does not wait for the write either. The
   * first login that the SMM accepts with the new password is therefore the proof that the write is done and that the
   * SMM stored the intended password.
   *
   * After the call, a session for parameter download is open, logged in with the new password, so a caller can go on
   * with SMM commands without a new login. Log out with {@link logoutFromSmm} when done.
   *
   * @param deviceRef - A reference to the target device.
   * @param username - The name of the logged-in user, 4 to 24 printable ASCII characters, for the login with the new
   *   password. Pass the same name the user logged in with: it tells who configured the SMM.
   * @param oldPassword - The current SMM password.
   * @param newPassword - The new password to set for the SMM, 4 to 24 printable ASCII characters.
   * @param commandTimeout - The timeout in milliseconds for the CHANGE_PASSWORD command. On firmware below 5.2.0 it also
   *   covers the FoE write of the fs-buffer, because the buffer is written before the command is issued. Defaults to
   *   12000: the drive gives up on the SMM after 10 s, so do not set it lower. If it runs out, the result of the change
   *   is unknown. Do not retry after a timeout, because the SMM may still be running the change.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   * @param verifyTimeout - How long to keep trying the login with the new password, in milliseconds, counted from the
   *   accepted change. Defaults to 10000.
   * @param verifyInterval - The pause in milliseconds between two login attempts with the new password. Defaults to
   *   500.
   *
   * @throws {Error} If the username is not 4, or a password not 1 (old) or 4 (new), to 24 printable ASCII characters
   *   (nothing is sent then), if the SMM rejects the change (the session then stays open), or if the SMM does not
   *   accept a login with the new password within `verifyTimeout`.
   *
   * @remarks
   * Completion means the SMM accepted a login with the new password, so the new password is stored.
   *
   * When the SMM does not accept the new password within `verifyTimeout`, the stored password is unknown. Do not run
   * the change again. Try to log in with the new password, then with the old one.
   *
   * Do not restart the SMM, and do not power the device off, until the call completes. Either one during the write
   * leaves a stored password that the SMM discards.
   *
   * Rules a caller has to follow, because the SMM and the drive do not enforce them for you:
   *
   * - A login for parameter download must be open. A login fails while a session is already open, so log out first:
   *   use {@link reloginToSmmForParameterDownload}. The SMM closes a session after 5 minutes.
   * - The OS command object (0x1023) and the fs-buffer are a single slot per device. Nothing else may issue OS commands
   *   to the same device during the sequence: no second script, no Motion Master Console on the OS Commands page, no
   *   other tool. Issue SMM functions one at a time and wait for each to complete.
   * - When the SMM rejects the change, the response does not say why: a wrong old password, a missing or expired
   *   session and an SMM that is still booting all produce the same "the SMM rejected the request (status 2)".
   */
  changeSmmPassword(
    deviceRef: DeviceRef,
    username: string,
    oldPassword: string,
    newPassword: string,
    commandTimeout = 12000,
    responsePollingInterval = 500,
    verifyTimeout = 10000,
    verifyInterval = 500
  ): Observable<void> {
    return defer(() => {
      assertValidSmmCredential(username, 'Username', SMM_USERNAME_MIN_LENGTH);
      assertValidSmmCredential(oldPassword, 'Old password');
      assertValidSmmCredential(newPassword, 'New password', SMM_PASSWORD_MIN_LENGTH);

      const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.CHANGE_PASSWORD);
      const content = packSmmChangePassword(newPassword, oldPassword);
      // @ts-ignore
      return this.runOsCommandAndWriteFsBuffer(deviceRef, command, content, commandTimeout, responsePollingInterval);
    }).pipe(
      last(),
      concatMap((response: OsCommandResponse) => {
        if (response.request !== 'succeeded') {
          throw new Error(
            `Changing SMM password failed: ${describeOsCommandFailure(response)}. The password was not changed.`
          );
        }
        // The SMM acknowledges the change before it writes the password to its EEPROM, and refuses logins until the
        // write is done. A restart during the write makes the SMM discard the stored password (issue #185), so the
        // first accepted login with the new password is what ends the write, not a logout or a delay. That login's
        // session stays open for the caller.
        const deadline = Date.now() + verifyTimeout;
        return defer(() =>
          this.loginToSmmForParameterDownload(deviceRef, username, newPassword, undefined, responsePollingInterval)
        ).pipe(
          retry({
            delay: (err) =>
              Date.now() + verifyInterval < deadline
                ? timer(verifyInterval)
                : throwError(
                    () =>
                      new Error(
                        `The SMM accepted the password change, but did not accept a login with the new password within ${verifyTimeout} ms, so the stored password is unknown. Do not change the password again; try to log in with the new password, then with the old one. ${
                          err?.message ?? err
                        }`
                      )
                  ),
          }),
          last(null, undefined)
        );
      }),
      last(null, undefined),
      map(() => undefined)
    );
  }

  /**
   * Runs an SMM sequence and then logs out from the SMM, on completion and on error alike.
   *
   * Unlike a `finalize(() => logout.subscribe())`, the logout is part of the returned observable: the result is
   * delivered only after the logout has finished, so a caller's next SMM command cannot run alongside it on the
   * device's single OS command slot. A failing logout does not replace the sequence's own result or error.
   */
  private logoutFromSmmAfter<T>(deviceRef: DeviceRef, sequence$: Observable<T>): Observable<T> {
    const logout$ = defer(() => this.logoutFromSmm(deviceRef)).pipe(
      catchError(() => EMPTY),
      filter((): boolean => false)
    ) as unknown as Observable<T>;

    return concat(
      sequence$.pipe(
        catchError((err) =>
          concat(
            logout$,
            throwError(() => err)
          )
        )
      ),
      logout$
    );
  }

  /**
   * Triggers an SMM restart for the specified device.
   *
   * This method sends a command to the device to trigger an SMM restart and waits for a response.
   * The method will return `true` if the restart was successful, otherwise `false`.
   *
   * SMM may take up to 30 seconds to boot the newly flashed firmware during a software update,
   * with some variability in the exact duration.
   *
   * @param deviceRef - The reference to the device to restart.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the SMM restart attempt fails.
   */
  triggerSmmRestart(deviceRef: DeviceRef, commandTimeout = 40000, responsePollingInterval = 500): Observable<void> {
    const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.TRIGGER_RESTART);
    return this.runOsCommand(deviceRef, command, commandTimeout, responsePollingInterval).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Triggering SMM restart failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Acknowledges an I/O failure on the specified device.
   *
   * This method sends a command to the device to acknowledge an I/O failure and waits for a response.
   * The method will return `true` if the acknowledgment was successful, otherwise `false`.
   *
   * @param deviceRef - The reference to the device to acknowledge the I/O failure on.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the acknowledgment attempt fails.
   */
  acknowledgeIoFailureOnSmm(
    deviceRef: DeviceRef,
    commandTimeout = 10000,
    responsePollingInterval = 500
  ): Observable<void> {
    const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.IO_FAILURE_ACKNOWLEDGE);
    return this.runOsCommand(deviceRef, command, commandTimeout, responsePollingInterval).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Acknowledging I/O failure on SMM failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Configures the SMM software update process for the specified device.
   * Prepares the device to receive a software update of the given file size.
   *
   * @param deviceRef - A reference to the target device.
   * @param fileSize - The size of the software update file, in bytes.
   *
   * @throws {Error} If the configuration attempt fails.
   */

  configureSmmSoftwareUpdate(deviceRef: DeviceRef, fileSize: number): Observable<void> {
    const command = createSmmAcyclicHandlerConfigureSoftwareUpdateOsCommand(fileSize);
    return this.runOsCommand(deviceRef, command, 10000, 100).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Configuring SMM software update failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Performs a complete SMM software update on the specified device.
   * This includes authentication, configuration, data transmission in chunks,
   * CRC validation, and device restart.
   *
   * @param deviceRef - Reference to the target device.
   * @param username - Username used for authentication.
   * @param password - Password used for authentication.
   * @param buffer - The software update file as a `Uint8Array`.
   * @param crc - CRC checksum of the software update file for integrity verification.
   * @param chunkSize - (Optional) Size of each data chunk in bytes. Defaults to 1000.
   * @param commandTimeout - (Optional) Timeout for each OS command. Defaults to 60,000 ms.
   * @param responsePollingInterval - (Optional) Polling interval for command responses. Defaults to 1000 ms.
   * @param fsBufferReadWriteTimeout - (Optional) Timeout for fs-buffer read/write operations. Defaults to 120,000 ms.
   *
   * @throws {Error} If any step in the software update process fails.
   *
   * @remarks
   * The software update procedure follows these sequential steps:
   * 1. Log out from any existing SMM session.
   * 2. Authenticate and log in to the SMM device for software update.
   * 3. Configure the SMM device with the expected update file size.
   * 4. Transmit the file in data chunks (default: 1000 bytes each).
   * 5. Send the CRC checksum for data integrity validation.
   * 6. Finalize the software update.
   * 7. Trigger a device restart to apply the update.
   *
   * Each step is implemented as a deferred observable.
   * The commands execute only when subscribed to, ensuring a strict sequence.
   */
  updateSmmSoftware(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    buffer: Uint8Array,
    crc: number,
    chunkSize = 1000,
    commandTimeout = 30000,
    responsePollingInterval = 1000,
    fsBufferReadWriteTimeout = 120000
  ): Observable<void> {
    return this.updateSmmSoftwareWithProgress(
      deviceRef,
      username,
      password,
      buffer,
      crc,
      chunkSize,
      commandTimeout,
      responsePollingInterval,
      fsBufferReadWriteTimeout
    ).pipe(
      last(),
      map(() => undefined)
    );
  }

  /**
   * Performs a complete SMM software update like {@link updateSmmSoftware}, reporting progress as it goes.
   *
   * A {@link SmmSoftwareUpdateProgress} value is emitted when each stage starts, once per transmitted chunk,
   * and a final `done` value after the SMM restart has been triggered. Use it to show the user what the
   * update is doing; transmitting a large software file can take many minutes.
   *
   * Parameters are the same as for {@link updateSmmSoftware}.
   *
   * @throws {Error} If `buffer` is empty or `chunkSize` is not a positive integer. Nothing is sent to the device then.
   * @throws {Error} If any step in the software update process fails.
   */
  updateSmmSoftwareWithProgress(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    buffer: Uint8Array,
    crc: number,
    chunkSize = 1000,
    commandTimeout = 30000,
    responsePollingInterval = 1000,
    fsBufferReadWriteTimeout = 120000
  ): Observable<SmmSoftwareUpdateProgress> {
    const fileSize = buffer.byteLength;

    // A NaN chunk size (e.g. from a disabled form control) would otherwise skip every chunk
    // and finalize an update with no software transmitted.
    if (!Number.isInteger(chunkSize) || chunkSize <= 0) {
      return throwError(
        () => new Error(`Invalid SMM software update chunk size: ${chunkSize}. It must be a positive integer.`)
      );
    }

    if (fileSize === 0) {
      return throwError(() => new Error('The SMM software file is empty.'));
    }

    const chunks = Math.ceil(fileSize / chunkSize); // Total number of chunks to send.

    // Reports the stage, then runs its work. Deferred, so each step starts only after the previous one completes.
    const step = (
      stage: SmmSoftwareUpdateStage,
      work: () => Observable<unknown>,
      chunk = 0
    ): Observable<SmmSoftwareUpdateProgress> =>
      concat(of({ stage, chunk, chunks }), defer(work).pipe(ignoreElements()));

    const observables = [
      // Step 1: Ensure any existing session is logged out before starting the update.
      step('logout', () => this.logoutFromSmm(deviceRef)),

      // Step 2: Log in to the device specifically for software update permissions.
      step('login', () => this.loginToSmmForSoftwareUpdate(deviceRef, username, password)),

      // Step 3: Inform the device about the incoming file size for proper setup.
      step('configure', () => this.configureSmmSoftwareUpdate(deviceRef, fileSize)),
    ];

    // Step 4: Transmit the update file chunk-by-chunk.
    const transmitSoftwareCommand = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.TRANSMIT_SOFTWARE);
    for (let i = 0; i < chunks; i++) {
      const start = i * chunkSize;
      const fsBufferContent = buffer.slice(start, start + chunkSize);
      observables.push(
        step(
          'transmit',
          () =>
            this.runOsCommandAndWriteFsBuffer(
              deviceRef,
              transmitSoftwareCommand,
              fsBufferContent,
              commandTimeout,
              responsePollingInterval,
              false,
              fsBufferReadWriteTimeout
            ).pipe(
              last(), // Only consider the final response of the command.
              map((response) => {
                if (response.request !== 'succeeded') {
                  throw new Error(
                    `Transmitting SMM software chunk ${i + 1}/${chunks} failed: ${describeOsCommandFailure(response)}.`
                  );
                }
              })
            ),
          i + 1
        )
      );
    }

    // Step 5: Send the CRC to validate data integrity and finalize the update.
    const packedCrc = packSmmSoftwareUpdateCrc(crc);
    const finalizeSoftwareUpdateCommand = createSmmAcyclicHandlerOsCommand(
      SmmAcyclicHandlerSubcommandId.FINALIZE_SOFTWARE_UPDATE
    );
    observables.push(
      step(
        'finalize',
        () =>
          // @ts-ignore
          this.runOsCommandAndWriteFsBuffer(deviceRef, finalizeSoftwareUpdateCommand, packedCrc).pipe(
            last(),
            map((response) => {
              if (response.request !== 'succeeded') {
                throw new Error(
                  `Finalizing SMM software update failed: ${describeOsCommandFailure(
                    response
                  )}. Check that the CRC matches the software file.`
                );
              }
            })
          ),
        chunks
      ),

      // Step 6: Trigger a restart so the new software takes effect.
      step('restart', () => this.triggerSmmRestart(deviceRef), chunks),
      of({ stage: 'done' as const, chunk: chunks, chunks })
    );

    return concat(...observables);
  }

  /**
   * Waits until the SMM reports the expected software version.
   *
   * After an SMM software update the SMM restarts and boots the new software, which can take around 30 s.
   * Reads may fail or return the previous version during that time, so the version is polled until it
   * matches or the timeout expires.
   *
   * @param deviceRef - Reference to the target device.
   * @param expectedVersion - The expected SMM software version, bitwise encoded (e.g. 25344 for v99.0).
   * @param timeout - (Optional) How long to wait, in ms. Defaults to 90,000 ms.
   * @param pollingInterval - (Optional) Delay between reads, in ms. Defaults to 2000 ms.
   *
   * @throws {Error} If the SMM does not report the expected version within `timeout`.
   */
  waitForSmmFirmwareVersion(
    deviceRef: DeviceRef,
    expectedVersion: number,
    timeout = 90000,
    pollingInterval = 2000
  ): Observable<void> {
    let lastVersion: number | undefined;
    let lastError: unknown;

    const read$ = defer(() => this.readSmmFirmwareVersionAsNumber(deviceRef)).pipe(
      tap((version) => (lastVersion = version)),
      catchError((err) => {
        lastError = err;
        return EMPTY;
      })
    );

    return read$.pipe(
      repeat({ delay: () => (lastVersion === expectedVersion ? EMPTY : timer(pollingInterval)) }),
      filter((version) => version === expectedVersion),
      take(1),
      map(() => undefined),
      takeUntil(
        timer(timeout).pipe(
          mergeMap(() => {
            const reported =
              lastVersion !== undefined
                ? `it reports v${bitwiseDecodeNumberVersion(lastVersion)}`
                : `reading the version failed: ${lastError instanceof Error ? lastError.message : String(lastError)}`;
            return throwError(
              () =>
                new Error(
                  `SMM did not report software v${bitwiseDecodeNumberVersion(expectedVersion)} within ${
                    timeout / 1000
                  } s after the update; ${reported}.`
                )
            );
          })
        )
      )
    );
  }

  /**
   * Resolves whether an automated SMM software update is allowed for the specified device.
   *
   * This method evaluates the device and the provided firmware binary against a set of
   * preconditions required for automated installation. It returns a detailed
   * {@link SmmAutomatedUpdate} object describing each individual check result, which
   * can be used to give precise feedback to the user when automation is not possible.
   *
   * The automation is only permitted when **all** of the following conditions are met:
   * - The device is a recognized Somanet product (`isValidProduct`).
   * - The device belongs to the Circulo product family (`isCirculo`). ACTILINK-JD / JD Duo devices are judged
   *   by the underlying Circulo from their hardware description, see {@link resolveEncoderProductId}.
   * - The binary is encrypted (`isBinaryEncrypted`).
   * - The binary targets one of the supported SMM versions — v1.4 (260) or v2.56 (568) (`isAllowedBinaryVersion`).
   * - If targeting v2.56, the device SoC firmware version is ≥ 5.5.0 (`canInstallV256`).
   * - The device is running a legacy SMM firmware below v1.3, i.e. version < 259 (`isDeviceLw1`).
   *
   * If the device is not a recognized product, the method returns early with all flags set
   * to `false` and default zero values to avoid unnecessary device communication.
   *
   * @param deviceRef - A reference to the target device.
   * @param buffer - The SMM firmware binary to be installed, provided as a `Uint8Array`.
   *                 Its header is parsed to determine the target version and encryption status.
   *
   * @returns A promise that resolves to an {@link SmmAutomatedUpdate} object containing:
   * - `isValidProduct` — Whether the device maps to a known Somanet product.
   * - `isCirculo` — Whether the device is a Circulo or a Circulo-based ACTILINK-JD / JD Duo.
   * - `isCirculo7` — Whether the device is a Circulo 7.
   * - `isCirculo9` — Whether the device is a Circulo 9.
   * - `isDeviceLw1` — Whether the device SMM firmware is below v1.3 (version < 259).
   * - `isBinaryEncrypted` — Whether the provided firmware binary is encrypted.
   * - `isAllowedBinaryVersion` — Whether the binary targets a supported version (v1.4 or v2.56).
   * - `canInstallV256` — Whether v2.56 can be installed (requires SoC firmware ≥ 5.5.0).
   * - `isAutomationAllowed` — Whether all preconditions for automated update are satisfied.
   * - `allowedTargetSmmVersions` — The list of supported target SMM versions: `[260, 568]`.
   * - `deviceFirmwareVersion` — The SoC firmware version string read from the device (index `0x100a:0`).
   * - `deviceSmmVersion` — The current SMM firmware version as a raw integer.
   * - `binaryVersion` — The target SMM version encoded in the binary header.
   */
  async resolveSmmAutomatedUpdateConditions(deviceRef: DeviceRef, buffer: Uint8Array): Promise<SmmAutomatedUpdate> {
    const device = await lastValueFrom(this.resolveDevice(deviceRef));
    // ACTILINK-JD / JD Duo wrap a Circulo; judge the product by the underlying Circulo device.
    const product = findSomanetProductById(
      resolveEncoderProductId(device?.hardwareDescription ?? device?.serialNumber)
    );
    const isValidProduct = !!product;
    // v1.4 (no safe torque), v2.56 (safe torque)
    const allowedTargetSmmVersions = [260, 568];

    if (!isValidProduct) {
      return {
        isValidProduct: false,
        isDeviceLw1: false,
        isCirculo: false,
        isCirculo7: false,
        isCirculo9: false,
        isBinaryEncrypted: false,
        isAllowedBinaryVersion: false,
        canInstallV256: false,
        isAutomationAllowed: false,
        allowedTargetSmmVersions,
        deviceFirmwareVersion: 'v0.0.0',
        deviceSmmVersion: 0,
        isV990: false,
        isV770: false,
        isV771: false,
        binaryVersion: 0,
        deviceSupportsStreaming: false,
      };
    }

    const deviceSmmVersion = await lastValueFrom(this.readSmmFirmwareVersionAsNumber(deviceRef));
    const deviceFirmwareVersion = (await this.upload(deviceRef, 0x100a, 0)) as string;
    const binaryHeader = parseSmmBinaryHeaders(buffer);
    const binaryInformation = getInformationFromSmmFirmwareVersion(binaryHeader.swVersion);
    // Allow installation of smm v2.56 only if device SoC firmware version is >=5.5.0
    const canInstallV256 =
      binaryHeader.swVersion === 568 ? semver.gte(semver.coerce(deviceFirmwareVersion) ?? '0.0.0', '5.5.0') : true;
    const isCirculo = product.family === 'Circulo';
    const isDeviceLw1 = deviceSmmVersion < 259; // <v1.3
    const isV990 = deviceSmmVersion === 25344;
    const isV770 = deviceSmmVersion === 19712;
    const isV771 = deviceSmmVersion === 19713;
    const isBinaryEncrypted = binaryInformation.isEncrypted;
    const isAllowedBinaryVersion = allowedTargetSmmVersions.includes(binaryInformation.version);
    const isCirculo7 = isCirculo && product.group === 'Circulo 7';
    const isCirculo9 = isCirculo && product.group === 'Circulo 9';
    const deviceSupportsStreaming = semver.gte(semver.coerce(deviceFirmwareVersion) ?? '0.0.0', '5.2.0');

    const isAutomationAllowed =
      isCirculo &&
      isBinaryEncrypted &&
      isAllowedBinaryVersion &&
      canInstallV256 &&
      (isDeviceLw1 || isV990 || isV770 || isV771); // Allow automation for devices on legacy firmware (lw1) or already on one of the intermediate versions (v99.0, v77.0, v77.1)

    return {
      isValidProduct,
      isDeviceLw1,
      isCirculo,
      isCirculo7,
      isCirculo9,
      isBinaryEncrypted,
      isAllowedBinaryVersion,
      canInstallV256,
      isAutomationAllowed,
      allowedTargetSmmVersions,
      deviceFirmwareVersion,
      deviceSmmVersion,
      isV990,
      isV770,
      isV771,
      binaryVersion: binaryInformation.version,
      deviceSupportsStreaming,
    };
  }

  /**
   * Performs an automated, multi-step SMM software update to an encrypted firmware version on a Circulo device,
   * or a Circulo-based ACTILINK-JD / JD Duo (judged by the underlying Circulo).
   *
   * Before installing the target binary, this method evaluates the device state by calling
   * {@link resolveSmmAutomatedUpdateConditions}. Based on the result, it determines which
   * sequence of intermediate firmware versions must be installed first to transition the device
   * from unencrypted to encrypted firmware.
   *
   * The installation sequence depends on the current SMM version and device group:
   * - **Legacy (< v1.3) + Circulo 7**: v99.0 → v77.0 → target
   * - **Legacy (< v1.3) + Circulo 9**: v99.0 → v77.1 → target
   * - **v99.0 + Circulo 7**: v77.0 → target
   * - **v99.0 + Circulo 9**: v77.1 → target
   * - **v77.0 or v77.1**: target only
   *
   * After each installation the SMM restarts. The next step starts only once the SMM reports the
   * version that was just installed (see {@link waitForSmmFirmwareVersion}), so a failed or
   * unfinished installation stops the sequence with an error instead of continuing blindly.
   *
   * Transfer settings that are not given are resolved from the device firmware with
   * {@link resolveSmmSoftwareUpdateTimings}. On firmware without fs-buffer streaming (< v5.2.0),
   * `chunkSize` is always 1000 and `commandTimeout` is at least 420,000 ms.
   *
   * @param deviceRef - Reference to the target device.
   * @param username - Username used for SMM authentication.
   * @param password - Password used for SMM authentication.
   * @param buffer - The target encrypted firmware binary as a `Uint8Array`.
   * @param crc - CRC32 checksum of the target firmware binary for integrity verification.
   * @param chunkSize - (Optional) Size of each data chunk transmitted per step, in bytes.
   *                    Defaults to 1,000,000 with streaming (one command per file) and 1000 without.
   * @param commandTimeout - (Optional) Timeout for each OS command in ms.
   *                         Defaults to 180,000 ms with streaming and 420,000 ms without.
   * @param responsePollingInterval - (Optional) Polling interval for command responses in ms. Defaults to 1000 ms.
   * @param fsBufferReadWriteTimeout - (Optional) Timeout for fs-buffer read/write operations in ms.
   *                                   Defaults to 150,000 ms with streaming and 5000 ms without.
   *
   * @returns An `Observable` of {@link SmmAutomatedUpdateProgress} that reports each step and stage,
   *          and completes when the full installation sequence finishes successfully.
   *
   * @throws {Error} With message `'Unknown product. Cannot perform SMM software update.'` if the device
   *                 serial number does not map to a known Somanet product.
   * @throws {Error} With a descriptive message listing unmet preconditions if automation is not allowed —
   *                 e.g. the device is not a Circulo, the binary is not encrypted, the target version is
   *                 unsupported, the SoC firmware is below v5.5.0 (for SMM v2.56), or the device is
   *                 already on SMM v1.3+.
   * @throws {Error} If any installation step fails, or the SMM does not report the installed version afterwards.
   */
  updateSmmSoftwareToEncrypted(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    buffer: Uint8Array,
    crc: number,
    chunkSize?: number,
    commandTimeout?: number,
    responsePollingInterval = 1000,
    fsBufferReadWriteTimeout?: number
  ): Observable<SmmAutomatedUpdateProgress> {
    return defer(() => from(this.resolveSmmAutomatedUpdateConditions(deviceRef, buffer))).pipe(
      mergeMap((conditions) => {
        if (!conditions.isValidProduct) {
          throw new Error('Unknown product. Cannot perform SMM software update.');
        }

        if (!conditions.isAutomationAllowed) {
          const reasons = [];
          if (!conditions.isCirculo) {
            reasons.push('The device is not a Circulo or a Circulo-based ACTILINK-JD.');
          }
          if (!conditions.isDeviceLw1 && !conditions.isV990 && !conditions.isV770 && !conditions.isV771) {
            reasons.push('The device SMM software version is not lower than 1.3.');
          }
          if (!conditions.isBinaryEncrypted) {
            reasons.push('The SMM binary is not encrypted.');
          }
          if (!conditions.isAllowedBinaryVersion) {
            reasons.push('The target SMM software version is not supported for encrypted update.');
          }
          if (!conditions.canInstallV256) {
            reasons.push('The device firmware must be minimum v5.5.0 for installing SMM v2.56.');
          }
          throw new Error(
            `The following conditions are not met for SMM software automated update: ${reasons.join(' ')}`
          );
        }

        const timings = resolveSmmSoftwareUpdateTimings(conditions.deviceSupportsStreaming);
        const transfer = conditions.deviceSupportsStreaming
          ? {
              chunkSize: chunkSize ?? timings.chunkSize,
              commandTimeout: commandTimeout ?? timings.commandTimeout,
              fsBufferReadWriteTimeout: fsBufferReadWriteTimeout ?? timings.fsBufferReadWriteTimeout,
            }
          : {
              // Without streaming the fs-buffer is written before each command, which only works with 1000-byte chunks.
              chunkSize: timings.chunkSize,
              commandTimeout: Math.max(commandTimeout ?? 0, timings.commandTimeout),
              fsBufferReadWriteTimeout: fsBufferReadWriteTimeout ?? timings.fsBufferReadWriteTimeout,
            };

        const decode = (base64: string) => Uint8Array.from(atob(base64), (c) => c.codePointAt(0) ?? 0);
        type InstallStep = { version: number; load: () => Promise<{ buffer: Uint8Array; crc: number }> };
        const v990: InstallStep = {
          version: 25344,
          load: () =>
            import('./smm/40701_DEBUG_Update_V99.0').then((m) => ({
              buffer: decode(m.smmBinaryV990),
              crc: 0xd1a3bb72,
            })),
        };
        const v770: InstallStep = {
          version: 19712,
          load: () =>
            import('./smm/40701_DEBUG_Update_V77.0').then((m) => ({
              buffer: decode(m.smmBinaryV770),
              crc: 0xe290a20a,
            })),
        };
        const v771: InstallStep = {
          version: 19713,
          load: () =>
            import('./smm/40701_DEBUG_Update_V77.1').then((m) => ({
              buffer: decode(m.smmBinaryV771),
              crc: 0xa4a11125,
            })),
        };
        const target: InstallStep = { version: conditions.binaryVersion, load: () => Promise.resolve({ buffer, crc }) };

        const intermediate = conditions.isCirculo7 ? v770 : conditions.isCirculo9 ? v771 : undefined;
        let plan: InstallStep[] = [];
        if (conditions.isV770 || conditions.isV771) {
          plan = [target];
        } else if (intermediate && conditions.isV990) {
          plan = [intermediate, target];
        } else if (intermediate && conditions.isDeviceLw1) {
          plan = [v990, intermediate, target];
        }

        if (plan.length === 0) {
          throw new Error('Cannot determine the SMM update path: the device is neither a Circulo 7 nor a Circulo 9.');
        }

        const steps = plan.length;
        return concat(
          ...plan.map(({ version, load }, i): Observable<SmmAutomatedUpdateProgress> => {
            const progress = { step: i + 1, steps, version };
            return concat(
              defer(() => from(load())).pipe(
                mergeMap((software) =>
                  this.updateSmmSoftwareWithProgress(
                    deviceRef,
                    username,
                    password,
                    software.buffer,
                    software.crc,
                    transfer.chunkSize,
                    transfer.commandTimeout,
                    responsePollingInterval,
                    transfer.fsBufferReadWriteTimeout
                  )
                ),
                map((install) => ({ ...progress, stage: 'install' as const, install }))
              ),
              of({ ...progress, stage: 'verify' as const }),
              defer(() => this.waitForSmmFirmwareVersion(deviceRef, version)).pipe(ignoreElements()),
              of({ ...progress, stage: 'installed' as const })
            );
          })
        );
      })
    );
  }

  /**
   * Transmits SMM parameters to the specified device.
   *
   * @param deviceRef - The reference to the target device.
   * @param values - An array of data values to transmit.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the transmission fails or if the `values` array is empty.
   *
   * @remarks
   * - If the `values` array is empty, the function throws an error.
   * - The function constructs an OS command for transmitting the parameters and writes the packed buffer to the fs-buffer file.
   */
  transmitSmmParameters(
    deviceRef: DeviceRef,
    values: DataType[],
    parameterStructureVersion: number | undefined = undefined,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<void> {
    if (values.length === 0) {
      throw new Error('Transmitting SMM parameters failed. The values array is empty.');
    }

    return (
      parameterStructureVersion ? of(parameterStructureVersion) : this.resolveSmmParameterStructureVersion(deviceRef)
    ).pipe(
      mergeMap((version) => {
        const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.TRANSMIT_PARAMETERS);
        const { buffer: content } = packSmmParameterValues(values, version);
        return this.runOsCommandAndWriteFsBuffer(
          deviceRef,
          command,
          // @ts-ignore
          content,
          commandTimeout,
          responsePollingInterval
        ).pipe(
          last(),
          map((response) => {
            if (response.request !== 'succeeded') {
              throw new Error(`Transmitting SMM parameters failed: ${describeOsCommandFailure(response)}.`);
            }
          })
        );
      })
    );
  }

  /**
   * Loads the SMM parameters from the device for verification purposes.
   *
   * Sends the `LOAD_PARAMETERS_FOR_VERIFICATION` subcommand to the specified device and reads
   * the fs-buffer in response. The received byte buffer is XOR-masked with `0xFF` and
   * then unpacked using the provided or inferred parameter structure version.
   *
   * @param deviceRef - The reference to the device from which the parameters should be loaded.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @returns An Observable that emits the response from the device, containing the valid parameter values for verification
   *          after the buffer is processed and reconstructed.
   *
   * @throws Will throw an error if the command fails or times out.
   */
  loadSmmParametersForVerification(
    deviceRef: DeviceRef,
    parameterStructureVersion: number | undefined = undefined,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<SmmAcyclicHandlerLoadParametersForVerificationOsCommandResponse> {
    return (
      parameterStructureVersion ? of(parameterStructureVersion) : this.resolveSmmParameterStructureVersion(deviceRef)
    ).pipe(
      mergeMap((version) => {
        const command = createSmmAcyclicHandlerOsCommand(
          SmmAcyclicHandlerSubcommandId.LOAD_PARAMETERS_FOR_VERIFICATION
        );
        return this.runOsCommandAndReadFsBuffer(deviceRef, command, commandTimeout, responsePollingInterval).pipe(
          last(),
          map((response) => {
            if (response.request === 'succeeded' && response.fsBuffer) {
              const bytes = Uint8Array.from(response.fsBuffer).map((byte) => byte ^ 0xff);
              const values = unpackSmmParameterValues(bytes, version);
              return { ...response, values } as SmmAcyclicHandlerLoadParametersForVerificationOsCommandResponse;
            }
            return { ...response, values: [] } as SmmAcyclicHandlerLoadParametersForVerificationOsCommandResponse;
          })
        );
      })
    );
  }

  /**
   * Verifies a group of SMM parameters for the specified device.
   *
   * This function calculates a bitmask from the provided values and group ordinal,
   * then sends an SMM OS command (subcommand VERIFY_PARAMETERS) along with the bitmask content
   * via `fs-buffer` to the device for verification.
   *
   * @param deviceRef - A reference to the target device.
   * @param values - An array of parameter values to verify.
   * @param groupIndex - The verification group index used for bitmask calculation.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws {Error} If the verification fails.
   *
   * @remarks
   * This function requires parameters to be previously loaded for verification to succeed. If parameters are not preloaded, the verification will fail.
   * The same group can be verified multiple times without causing an error.
   */
  verifySmmParameters(
    deviceRef: DeviceRef,
    values: DataType[],
    groupIndex: number,
    parameterStructureVersion: number | undefined = undefined,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<void> {
    return (
      parameterStructureVersion ? of(parameterStructureVersion) : this.resolveSmmParameterStructureVersion(deviceRef)
    ).pipe(
      mergeMap((version) => {
        const { buffer: content } = packSmmParameterValuesForVerification(values, groupIndex, version);
        const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.VERIFY_PARAMETERS);
        return this.runOsCommandAndWriteFsBuffer(
          deviceRef,
          command,
          content,
          commandTimeout,
          responsePollingInterval
        ).pipe(
          last(),
          map((response) => {
            if (response.request !== 'succeeded') {
              throw new Error(`Verifying SMM parameters failed: ${describeOsCommandFailure(response)}.`);
            }
          })
        );
      })
    );
  }

  /**
   * Loads the SMM validation file on the specified device.
   *
   * @param deviceRef - A reference to the target device.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   * @returns An Observable emitting the OS command response.
   *
   * @remarks
   * This function should only be called **after all parameter groups have been successfully verified**.
   */
  loadSmmValidationFile(
    deviceRef: DeviceRef,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<OsCommandResponse> {
    const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.LOAD_VALIDATION_FILE);
    return this.runOsCommand(deviceRef, command, commandTimeout, responsePollingInterval).pipe(last());
  }

  /**
   * Loads the SMM validation file and then retrieves the safety parameters report file.
   *
   * @param deviceRef - A reference to the target device.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   * @returns An Observable emitting the decoded content of the `.safety_parameters_report` file.
   *
   * @throws An error if loading the validation file fails.
   *
   * @remarks
   * This function should only be called **after all parameter groups have been successfully verified**.
   */
  loadSmmValidationFileAndReadSafetyParametersReportFile(
    deviceRef: DeviceRef,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<string> {
    return this.loadSmmValidationFile(deviceRef, commandTimeout, responsePollingInterval).pipe(
      last(),
      mergeMap((response) => {
        if (response.request !== 'succeeded') {
          throw new Error('Failed to load the validation file.');
        }
        return this.getDecodedFile(deviceRef, '.safety_parameters_report');
      })
    );
  }

  /**
   * Validates the SMM configuration on the specified device.
   *
   * @param deviceRef - The reference to the target device.
   * @param report - A `Uint8Array` representing the validation report data.
   * @param date - The date associated with the validation.
   * @param username - The username used for authentication.
   * @param password - The password used for authentication.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws An error if the validation fails.
   *
   * @remarks
   * - Packs the validation report, date, username, and password into a buffer.
   * - Constructs an OS command using the `VALIDATE_CONFIGURATION` subcommand.
   * - Sends the command to the device and writes the packed buffer to the fs-buffer file.
   * - **Important:** Once the configuration is successfully validated, subsequent calls to this function will fail. To revalidate, the user must go through the verification process again.
   */
  validateSmmConfiguration(
    deviceRef: DeviceRef,
    report: Uint8Array,
    date: Date,
    username: string,
    password: string,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<void> {
    const { buffer: content } = packSmmValidationReport(report, date, username, password);
    const command = createSmmAcyclicHandlerOsCommand(SmmAcyclicHandlerSubcommandId.VALIDATE_CONFIGURATION);
    return this.runOsCommandAndWriteFsBuffer(deviceRef, command, content, commandTimeout, responsePollingInterval).pipe(
      last(),
      map((response) => {
        if (response.request !== 'succeeded') {
          throw new Error(`Validating SMM configuration failed: ${describeOsCommandFailure(response)}.`);
        }
      })
    );
  }

  /**
   * Reads the safety parameters report from the device and validates the SMM configuration.
   *
   * @param deviceRef - The reference to the target device.
   * @param date - The date associated with the validation.
   * @param username - The username used for authentication.
   * @param password - The password used for authentication.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - The interval in milliseconds between polling for the response.
   *
   * @throws An error if the safety parameters report file is missing or if the validation fails.
   *
   * @remarks
   * - The function attempts to retrieve the `.safety_parameters_report` file from the device.
   * - If the file is missing, an error is thrown.
   * - The file content is read into a `Uint8Array` and passed to `validateSmmConfiguration`.
   * - The SMM configuration validation follows the same behavior as `validateSmmConfiguration`:
   *   - If validation succeeds, subsequent calls to this function will fail.
   *   - To revalidate, the user must repeat the verification process.
   */
  readSafetyParametersReportAndValidateSmmConfiguration(
    deviceRef: DeviceRef,
    date: Date,
    username: string,
    password: string,
    commandTimeout = 5000,
    responsePollingInterval = 500
  ): Observable<void> {
    return this.getFile(deviceRef, '.safety_parameters_report').pipe(
      last(),
      mergeMap((file) => {
        if (!file) {
          throw new Error("The '.safety_parameters_report' file is missing.");
        }
        const report = new Uint8Array(file);
        return this.validateSmmConfiguration(
          deviceRef,
          report,
          date,
          username,
          password,
          commandTimeout,
          responsePollingInterval
        );
      })
    );
  }

  /**
   * Retrieves SMM OD (Object Dictionary) parameters for a specific device.
   *
   * This function fetches and processes the safety parameters from the SMM object dictionary (JSON file),
   * transforming them into a consistent `SmmParameter` format for the provided device serial number.
   * It supports resolving the parameter structure version if not explicitly provided.
   *
   * @param deviceRef - The reference to the target device.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   * @param useValues - Specifies which source of values to use:
   * - 'default': Uses the default factory values.
   * - 'reset': Uses the reset values.
   * - 'device': Uses the current values from the device.
   * Defaults to 'default'.
   * @param loadFromCache - Whether to load parameters from cache (`true`) or force a fresh load (`false`). Defaults to `true`.
   *
   * @returns An `Observable` emitting an array of `SmmParameter` objects, each representing a processed
   *          parameter with metadata such as type, access rights, and UI-related configurations.
   *
   * @throws {Error} If the parameter structure version cannot be resolved or the SMM object dictionary fails to load.
   */
  getSmmOdParameters(
    deviceRef: DeviceRef,
    parameterStructureVersion: number | undefined = undefined,
    useValues: 'default' | 'reset' | 'device' = 'default',
    loadFromCache = true
  ): Observable<SmmParameter[]> {
    return zip(
      parameterStructureVersion ? of(parameterStructureVersion) : this.resolveSmmParameterStructureVersion(deviceRef),
      typeof deviceRef === 'string'
        ? of(deviceRef)
        : this.resolveDeviceAddress(deviceRef).pipe(map((address) => this.getSerialNumberByDeviceAddress(address)))
    ).pipe(
      map(([version, deviceSerialNumber]) => {
        if (!deviceSerialNumber) {
          throw new Error(
            `Failed to resolve the device serial number from the provided device reference: ${deviceRef}. A valid device serial number is required to construct SMM parameters.`
          );
        }

        const smmOd = resolveSmmOd(version);

        const parameters = smmOd.parameters.safety.map((p) => {
          const description = p.description;

          const variableName = p.variableName || p.parameterName.replace(' ', '_');
          const fieldName = variableName;

          const group = p.group;

          const index = p.index;
          const subindex = p.subindex;

          let id = `${variableName}.${deviceSerialNumber}`;
          if (index > 0 && subindex >= 0) {
            id = makeDeviceParameterId(deviceSerialNumber, index, subindex);
          }

          const min = p.min;
          const max = p.max;

          const name = p.parameterName;

          const readAccess = p.access?.includes('r') ?? true;
          let writeAccess = p.access?.includes('w') ?? false;

          // Safe fieldbus is writable when configuring via OBLAC and disabled when configuring via FSoE.
          if (index === 0x2620 && subindex === 0x02) {
            writeAccess = true;
          }

          // Safe address is always writable.
          if (index === 0x2620 && subindex === 0x03) {
            writeAccess = true;
          }

          const unit = p.unit?.toString() ?? '';

          let value = p.defaultValue;

          if (useValues === 'reset') {
            value = p.resetValue;
          }

          let inputType: ParameterInputType = 'number';
          let typeValue: ParameterTypeValueKey = 'intValue';

          if (p.type.startsWith('char')) {
            inputType = 'text';
            typeValue = 'stringValue';
          }

          let options: { [key: string]: number } | undefined = undefined;

          if (p.typeRef) {
            const ref = smmOd.types[p.typeRef];
            if (ref?.enum) {
              options = ref.enum.reduce((acc, cur) => {
                acc[cur.label] = cur.value;
                return acc;
              }, {} as { [key: string]: number });
            }
          }

          if (options) {
            inputType = 'select';
          }

          const defaultData = p.defaultValue;

          return {
            defaultData,
            description,
            deviceSerialNumber,
            fieldName,
            group,
            id,
            index,
            inputType,
            mandatory: false,
            max,
            min,
            name,
            options,
            readAccess,
            subindex,
            typeValue,
            unit,
            value,
            valueType: null,
            writeAccess,
          } as SmmParameter;
        });

        return [smmOd, parameters] as [SmmOd, SmmParameter[]];
      }),
      mergeMap(([smmOd, parameters]) => {
        if (useValues !== 'device') {
          return of(parameters);
        }

        // Create addresses for the device's safety parameters, skipping the first parameter (index 0x0000), which represents the Safety Parameter Structure Version.
        let addresses = smmOd.parameters.safety.slice(1).map((p) => [deviceRef, p.index, p.subindex]) as [
          DeviceRef,
          number,
          number
        ][];
        return this.getParameterValues(addresses, loadFromCache).pipe(
          map((values) => {
            for (let i = 0; i < addresses.length; i++) {
              parameters[i + 1].value = values[i];
            }
            return parameters;
          })
        );
      })
    );
  }

  /**
   * Retrieves SMM OD (Object Dictionary) parameters for a specific device, grouped by their `group` property.
   *
   * This function fetches and processes the safety parameters from the SMM object dictionary,
   * then organizes them into a `Map` where each key represents a unique group, and the value is an array
   * of corresponding `SmmParameter` objects belonging to that group.
   *
   * @param deviceRef - The reference to the target device.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   * @param useValues - Specifies which source of values to use:
   * - 'default': Uses the default factory values.
   * - 'reset': Uses the reset values.
   * - 'device': Uses the current values from the device.
   * Defaults to 'default'.
   * @param loadFromCache - Whether to load parameters from cache (`true`) or force a fresh load (`false`). Defaults to `true`.
   *
   * @returns An `Observable` emitting a `Map<string, SmmParameter[]>`:
   *          - The keys are group names.
   *          - The values are arrays of parameters belonging to the respective group.
   *
   * @throws {Error} If the parameter structure version cannot be resolved or the SMM object dictionary fails to load.
   *
   * @remark
   * The resulting groups are sorted to ensure their index matches the order expected by the `verifySmmParameters` function.
   * Additionally, parameters within each group are sorted in the sequence required for correctly packing verification values.
   * This ensures consistency between retrieval, packing, and verification stages.
   */
  getSmmOdGroupedParameters(
    deviceRef: DeviceRef,
    parameterStructureVersion: number | undefined = undefined,
    useValues: 'default' | 'reset' | 'device' = 'default',
    loadFromCache = true
  ): Observable<Map<string, SmmParameter[]>> {
    return this.getSmmOdParameters(deviceRef, parameterStructureVersion, useValues, loadFromCache).pipe(
      map((parameters) => mapByProperty(parameters, 'group'))
    );
  }

  /**
   * Performs a factory reset on the SMM parameters of the specified device.
   * It restores all safety-related parameters to their default reset values.
   *
   * @param deviceRef - A reference to the target device.
   * @param parameterStructureVersion - (Optional) A specific SMM parameter structure version to use.
   * If not provided, the version is resolved dynamically from the device.
   *
   * @throws {Error} If the factory reset operation fails.
   *
   * @remarks
   * This function first determines the SMM parameter structure version — either from the provided argument
   * or by resolving it from the device. It then extracts the reset values from the safety parameters
   * and transmits them to the device.
   */
  factoryResetSmm(deviceRef: DeviceRef, parameterStructureVersion: number | undefined = undefined): Observable<void> {
    return (
      parameterStructureVersion ? of(parameterStructureVersion) : this.resolveSmmParameterStructureVersion(deviceRef)
    ).pipe(
      mergeMap((version) => {
        const smmOd = resolveSmmOd(version);
        const values = smmOd.parameters.safety.map((p) => p.resetValue) as DataType[];
        return this.transmitSmmParameters(deviceRef, values, version);
      })
    );
  }

  /*
   * Reads a value from an external I²C register of a Circulo Integrated Encoder device (e.g., iC-PVL or EEPROM),
   * by configuring the iC-MU's I²C bridging mechanism via command sequencing.
   *
   * This method writes the required configuration parameters to the iC-MU registers in a specific order
   * to trigger an I²C read transaction from the specified external device and register.
   *
   * The communication sequence is based on configuring the I²C device ID, retry parameters,
   * and transfer region (RAM/EEPROM), and finally triggering the `I2C_COM` command.
   *
   * @param deviceRef - Reference to the connected device used for communication.
   * @param encoderOrdinal - Index of the encoder on the bus (typically 1 if only one encoder is present).
   * @param i2cDeviceId - I²C device ID to access (EEPROM, PVL limited, PVL full).
   * @param registerAddress - Target register address on the external I²C device to read from.
   * @param commandTimeout - Optional timeout in milliseconds for each command (default: 2000 ms).
   *
   * @returns An `Observable<EncoderRegisterCommunicationOsCommandResponse>` that emits the final response
   * from the OS command sequence, containing the value read from the specified external I²C register.
   */
  readCirculoEncoderExternalRegister(
    deviceRef: DeviceRef,
    encoderOrdinal: number,
    i2cDeviceId: CirculoIntegratedEncoderI2CDeviceId,
    registerAddress: number,
    commandTimeout = 2000
  ): Observable<EncoderRegisterCommunicationOsCommandResponse> {
    return concat(
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_DEVID.address,
          i2cDeviceId,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RETRY.address,
          3,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RAM_START.address,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_DEV_START.address,
          registerAddress,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RAM_END.address,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.CMD_MU.address,
          CirculoIntegratedEncoderIcmuCommands.I2C_COM,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          0,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          0x00,
          commandTimeout
        )
      )
    ).pipe(last());
  }

  /**
   * Writes a value to an external I²C register of a Circulo Integrated Encoder device (e.g., iC-PVL or EEPROM),
   * by issuing a series of register configuration commands through the iC-MU I²C communication bridge.
   *
   * The function sets up the I²C device ID, retry count, target register, and data value to be written,
   * and then triggers the `I2C_COM` command to initiate the write transaction over the external I²C bus.
   *
   * This is typically used to write configuration or operational parameters to EEPROM or iC-PVL RAM registers.
   *
   * @param deviceRef - Reference to the connected device used for communication.
   * @param encoderOrdinal - Index of the encoder on the bus (typically 1 if only one encoder is present).
   * @param i2cDeviceId - I²C device ID specifying the target external device (EEPROM, PVL limited, or PVL full).
   * @param registerAddress - Target register address on the external I²C device.
   * @param registerValue - Value to write to the specified register.
   * @param commandTimeout - Optional timeout in milliseconds for each command (default: 2000 ms).
   *
   * @returns An `Observable<EncoderRegisterCommunicationOsCommandResponse>` that emits the final response
   * from the OS command sequence, confirming the write operation.
   */
  writeCirculoEncoderExternalRegister(
    deviceRef: DeviceRef,
    encoderOrdinal: number,
    i2cDeviceId: CirculoIntegratedEncoderI2CDeviceId,
    registerAddress: number,
    registerValue: number,
    commandTimeout = 2000
  ): Observable<EncoderRegisterCommunicationOsCommandResponse> {
    return concat(
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_DEVID.address,
          i2cDeviceId,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RETRY.address,
          3,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RAM_START.address,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_DEV_START.address,
          registerAddress,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.I2C_RAM_END.address,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.USER_EXCHANGE_REGISTERS_1.address,
          registerValue,
          commandTimeout
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          1,
          CirculoIntegratedEncoderIcmuParameters.CMD_MU.address,
          CirculoIntegratedEncoderIcmuCommands.I2C_COM,
          commandTimeout
        )
      )
    ).pipe(last());
  }

  /**
   * This is a special function that reads the key configuration registers of a Circulo integrated encoder.
   * It sends a series of commands to the device to read the registers and returns the responses as an array.
   * It was added as part of a fix for the EEPROM issue in Circulo integrated encoders.
   *
   * A register read completes on the drive within a few tens of milliseconds, so the response is polled every
   * 50 ms rather than at the 1000 ms default for OS commands. The first poll usually comes before the read has
   * completed; at 1000 ms each of those reads then waits a full second, and the 33 registers take about 30 s
   * instead of 1 to 2 s (measured on a Circulo 7 Safe Motion).
   *
   * The registers are read through the encoder configured in 0x2110 (encoder 1) or 0x2112 (encoder 2). When its
   * sensor port (subindex 1) is 0, no encoder is configured: no OS command is sent, and the observable fails at
   * once. Otherwise the firmware times out each read after 5 s and then refuses further OS commands.
   *
   * @param deviceRef - The reference to the device to which the command will be sent.
   * @param encoderOrdinal - The ordinal number of the encoder.
   * @param commandTimeout - The timeout in milliseconds for the command to complete.
   * @param responsePollingInterval - How often, in milliseconds, the response of each register read is polled.
   * @returns An observable that emits an array of `EncoderRegisterCommunicationOsCommandResponse` objects.
   * @throws Error when the encoder is not configured.
   */
  readCirculoIntegratedEncoderKeyConfigurationRegisters(
    deviceRef: DeviceRef,
    encoderOrdinal: number,
    commandTimeout = 5000,
    responsePollingInterval = 50
  ): Observable<EncoderRegisterCommunicationOsCommandResponse[]> {
    const registerAddresses = circuloIntegratedEncoderKeyConfigurationRegisterAddresses.map((addr) => addr.ram);
    const defferedOsCommands = registerAddresses.map((registerAddress) =>
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          0,
          registerAddress,
          undefined,
          commandTimeout,
          responsePollingInterval
        ).pipe(last())
      )
    );
    const configurationIndex = { 1: 0x2110, 2: 0x2112 }[encoderOrdinal];
    if (configurationIndex === undefined) {
      return concat(...defferedOsCommands).pipe(toArray());
    }
    return defer(() => this.upload<number>(deviceRef, configurationIndex, 1)).pipe(
      mergeMap((sensorPort) => {
        if (sensorPort === 0) {
          const index = `0x${configurationIndex.toString(16).toUpperCase()}`;
          return throwError(
            () =>
              new Error(
                `Encoder ${encoderOrdinal} is not configured: its sensor port (${index}:01) is 0. Configure the ` +
                  'integrated encoder before reading its registers.'
              )
          );
        }
        return concat(...defferedOsCommands).pipe(toArray());
      })
    );
  }

  /**
   * Reads the key configuration registers of a Circulo integrated encoder, `HARD_REV` (register 0x74) and the
   * amplitude control gain (registers 0x2B and 0x2F), and builds its report: the chip revision, health monitoring,
   * the Nonius track offset table and every register with the raw values of its fields. See
   * {@link buildCirculoIntegratedEncoderReport}.
   *
   * The device serial number is resolved alongside; when it can't be, the report says it is unknown.
   *
   * @param deviceRef - The reference to the device.
   * @param encoderOrdinal - The ordinal number of the encoder, 1 or 2.
   * @param commandTimeout - The timeout in milliseconds for each register read.
   * @param responsePollingInterval - How often, in milliseconds, the response of each register read is polled.
   * @returns An observable that emits the report and completes.
   * @throws Error when a register read returns no value, so the report never shows a value it didn't read.
   */
  getCirculoIntegratedEncoderReport(
    deviceRef: DeviceRef,
    encoderOrdinal: number,
    commandTimeout = 5000,
    responsePollingInterval = 50
  ): Observable<CirculoIntegratedEncoderReport> {
    const valueOf = (response: EncoderRegisterCommunicationOsCommandResponse, ram: number): number => {
      if (response.registerValue === undefined) {
        const reason = [response.request, response.errorCode, response.errorName, response.errorDescription]
          .filter((part) => part !== undefined && part !== '')
          .join(', ');
        throw new Error(`Register 0x${ram.toString(16).padStart(2, '0')} returned no value (${reason}).`);
      }
      return response.registerValue;
    };
    const readRegister = (ram: number) =>
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          0,
          ram,
          undefined,
          commandTimeout,
          responsePollingInterval
        ).pipe(last())
      );
    const extraRegisters = [
      icMuHardRevRegister,
      ...circuloIntegratedEncoderAmplitudeControlRegisters.map((r) => r.ram),
    ];
    const deviceSerialNumber$ = this.resolveDevice(deviceRef).pipe(
      map((device) => device.id as string | undefined),
      catchError(() => of(undefined))
    );
    // The OS command channel runs one command at a time, so HARD_REV and the amplitude control registers are read
    // after the key configuration registers, not alongside.
    return forkJoin([
      concat(
        this.readCirculoIntegratedEncoderKeyConfigurationRegisters(
          deviceRef,
          encoderOrdinal,
          commandTimeout,
          responsePollingInterval
        ),
        concat(...extraRegisters.map(readRegister)).pipe(toArray())
      ).pipe(toArray()),
      deviceSerialNumber$,
    ]).pipe(
      map(([[responses, extraResponses], deviceSerialNumber]) => {
        const values = responses.map((response, i) =>
          valueOf(response, circuloIntegratedEncoderKeyConfigurationRegisterAddresses[i].ram)
        );
        const [hardRev, ...amplitudeControlValues] = extraResponses.map((response, i) =>
          valueOf(response, extraRegisters[i])
        );
        return buildCirculoIntegratedEncoderReport(values, {
          encoderOrdinal,
          deviceSerialNumber,
          readAt: new Date(),
          hardRev,
          amplitudeControlValues,
        });
      })
    );
  }

  /**
   * Writes the integrated encoder configuration to a binary file on the device.
   *
   * The function reads all key encoder configuration registers, converts them
   * into a binary format, and saves them as a `.bin` file on the device.
   *
   * ⚠️ **When to call**:
   * This should be invoked **after encoder configuration and calibration** have been
   * completed, so that the correct values are stored for persistence.
   *
   * ℹ️ **How the file is used**:
   * On firmware initialization, the device checks the encoder configuration.
   * If it detects an incorrect or missing configuration, the firmware will
   * automatically reconfigure the encoder using the parameters stored in this
   * binary file.
   *
   * 📄 **File naming**:
   * The written files are named based on the encoder ordinal:
   * - `circulo_integrated_encoder_1_config.bin` for encoder ordinal **1**
   * - `circulo_integrated_encoder_2_config.bin` for encoder ordinal **2**
   *
   * @param deviceRef - Reference to the target device.
   * @param encoderOrdinal - Encoder index/ordinal to identify which encoder to store configuration for.
   * @param commandTimeout - Optional timeout in milliseconds for the read commands (default: 5000).
   * @returns An observable that completes once the binary file has been written to the device.
   */
  writeCirculoIntegratedEncoderConfigBinFile(
    deviceRef: DeviceRef,
    encoderOrdinal: number,
    commandTimeout = 5000
  ): Observable<DeviceFileStatus> {
    return this.readCirculoIntegratedEncoderKeyConfigurationRegisters(deviceRef, encoderOrdinal, commandTimeout).pipe(
      mergeMap((responses) => {
        const name = `circulo_integrated_encoder_${encoderOrdinal}_config.bin`;
        const addresses = circuloIntegratedEncoderKeyConfigurationRegisterAddresses.map((r) => r.ram);
        const values = responses.map((r) => r.registerValue);
        const data = addresses.flatMap((addr, i) => [addr, values[i]]) as number[];
        const content = new Uint8Array(data.map((v) => v & 0xff));
        return this.setFile(deviceRef, name, content, true);
      })
    );
  }

  /**
   * Retrieves the supported analog input (AI) modes for the specified device.
   *
   * Resolves the device reference, fetches the supported AI options and firmware version,
   * then combines these to determine which AI modes are enabled or disabled.
   * Certain AI inputs are disabled based on the device's Integro variant options.
   *
   * @param deviceRef - A reference to the device for which to fetch supported AI modes.
   * @returns An Observable emitting a two-dimensional array of numbers, where each inner array corresponds to an AI pin
   *          and contains the supported modes. Pins with all modes disabled default to `[0]`.
   */
  getSupportedAis(deviceRef: DeviceRef): Observable<number[][]> {
    return this.resolveDevice(deviceRef).pipe(
      mergeMap((device) => {
        if (!device.versionedDeviceProductId) {
          return [];
        }

        const supportedOptionsPromise = fetchSupportedAis();
        const firmwareVersionPromise = this.upload<string>(deviceRef, 0x100a, 0);

        return extractFirmwareProductSupportedOptions(
          supportedOptionsPromise,
          firmwareVersionPromise,
          device.versionedDeviceProductId
        ).pipe(
          map((supportedOptions) => {
            const integroVariantOptionIds = device.integroVariant?.options.map((o) => o.id) ?? [];

            if (integroVariantOptionIds.includes(36)) supportedOptions[0] = [0]; // Disable AI Internal (Motor temperature sensor)
            if (integroVariantOptionIds.includes(37)) supportedOptions[1] = [0]; // Disable AI 1
            if (integroVariantOptionIds.includes(38)) supportedOptions[2] = [0]; // Disable AI 2

            return supportedOptions;
          })
        );
      })
    );
  }

  /**
   * Retrieves the supported digital input/output (DIO) pin modes for a given device.
   *
   * This method resolves the device reference, fetches both the supported DIO options and firmware version,
   * then combines this information to determine which DIO pin modes are enabled or disabled.
   * It also disables specific DIO pin modes based on the device's Integro variant options.
   *
   * @param deviceRef - A reference to the device whose supported DIO pin modes are being fetched.
   * @returns An Observable that emits a two-dimensional array of numbers. Each inner array corresponds to a DIO pin
   *          and lists the supported pin modes. If all pin modes for a pin are disabled, the array defaults to `[0]`.
   */
  getSupportedDios(deviceRef: DeviceRef): Observable<number[][]> {
    return this.resolveDevice(deviceRef).pipe(
      mergeMap((device) => {
        if (!device.versionedDeviceProductId) {
          return [];
        }

        const supportedOptionsPromise = fetchSupportedDios();
        const firmwareVersionPromise = this.upload<string>(deviceRef, 0x100a, 0);

        return extractFirmwareProductSupportedOptions(
          supportedOptionsPromise,
          firmwareVersionPromise,
          device.versionedDeviceProductId
        ).pipe(
          map((supportedOptions) => {
            const disablePinMode = (index: number, mode: number) => {
              supportedOptions[index] = supportedOptions[index].filter((d) => d !== mode);
              if (supportedOptions[index].length === 0) {
                supportedOptions[index] = [0];
              }
            };

            const integroVariantOptionsId = device.integroVariant?.options.map((o) => o.id) ?? [];

            if (integroVariantOptionsId.includes(28)) disablePinMode(0, 1); // Disable DI1
            if (integroVariantOptionsId.includes(29)) disablePinMode(0, 2); // Disable DO1
            if (integroVariantOptionsId.includes(30)) disablePinMode(1, 1); // Disable DI2
            if (integroVariantOptionsId.includes(31)) disablePinMode(1, 2); // Disable DO2
            if (integroVariantOptionsId.includes(32)) disablePinMode(2, 1); // Disable DI3
            if (integroVariantOptionsId.includes(33)) disablePinMode(3, 1); // Disable DI4

            return supportedOptions;
          })
        );
      })
    );
  }

  /**
   * Checks the Circulo encoder error status registers for a specified encoder ordinal.
   *
   * This method queries two status registers (STATUS0 and STATUS1) for the given encoder
   * on the device, then maps the register values to an array of encoder error objects.
   *
   * @param deviceRef - Reference to the device.
   * @param encoderOrdinal - The ordinal/index of the encoder to check.
   * @returns An Observable that emits an array of EncoderRegisterError objects representing
   *          all errors found in the queried encoder status registers.
   */
  checkCirculoEncoderErrors(deviceRef: DeviceRef, encoderOrdinal: number): Observable<EncoderRegisterError[]> {
    return concat(
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          0,
          CirculoEncoderRegisterAddress.STATUS0
        )
      ),
      defer(() =>
        this.runEncoderRegisterCommunicationOsCommand(
          deviceRef,
          encoderOrdinal,
          0,
          0,
          CirculoEncoderRegisterAddress.STATUS1
        )
      )
    ).pipe(
      toArray(),
      map(([status0Response, status1Response]) => [
        ...mapCirculoEncoderRegisterValueToErrors(status0Response.registerValue ?? 0, 0),
        ...mapCirculoEncoderRegisterValueToErrors(status1Response.registerValue ?? 0, 1),
      ])
    );
  }

  /**
   * Uploads and configures SMM parameters on the specified device from a CSV configuration file.
   *
   * This function performs the following steps in sequence:
   * 1. Parses the CSV config file into structured parameters.
   * 2. Resolves the SMM parameter structure version for the device.
   * 3. Groups and maps parameters according to the SMM Object Dictionary.
   * 4. Validates that the parameter counts match between the config and SMM structure.
   * 5. Logs out from the SMM if already logged in.
   * 6. Logs in for parameter download using provided credentials.
   * 7. Transmits the parameters to the device.
   * 8. Verifies transmitted parameters by reloading and matching values.
   * 9. Sequentially verifies each parameter group on the device.
   * 10. Loads and validates the safety parameters validation file.
   * 11. Reloads the SMM parameters to refresh the stored values.
   * 12. Ensures proper logout in all cases, including errors.
   *
   * @param deviceRef - Reference to the target device to configure.
   * @param username - Username used to log in for parameter download.
   * @param password - Password used to log in for parameter download.
   * @param configFileContent - The raw CSV content of the configuration file.
   *
   * @returns An Observable that emits a generated safety parameters report on success.
   * On failure at any step, it emits an error with a descriptive message.
   *
   * @remarks
   * This method uses RxJS operators to ensure that each asynchronous step completes before the next begins.
   * It also guarantees logout from the device when the operation finishes or if any error occurs.
   *
   * ---
   *
   * ⚠️ **WARNING: SAFETY-CRITICAL APPLICATION & MANDATORY COMPLIANCE**
   *
   * 1. **Scope of Automation**
   *    This script is designed to automate the parameterization of Safe Motion Module.
   *    Use of this script is strictly limited to configurations where a known, valid configuration (.csv) is already available.
   *
   * 2. **Mandatory Checklist Adherence**
   *    Execution of this script does **NOT** bypass the requirements outlined in the
   *    “Commissioning of Joints with Known Safety Configuration” checklist.
   *    The user must ensure all physical prerequisites are met, including:
   *      - Correct mechanical mounting per IEC 61800-5-2:2016.
   *      - Proper encoder calibration.
   *      - Verification that the FSoE Watchdog is correctly configured on the FSoE Master.
   *
   * 3. **Validation of Limits and Reactions**
   *    As per standard operating procedure,
   *    the safety configuration must be manually checked by
   *    intentionally violating limits to trigger and verify the expected reaction.
   *    If safe torque values are used, they must be validated by applying specific torque and verifying the reported data.
   *
   * 4. **System-Level Functional Testing**
   *    Automation of individual drive parameters does not validate the safety of the machine.
   *    The user is legally required to validate the safety functions of the entire system by
   *    triggering every implemented safety function, including establishing a process for repeated testing of STO/SBC functions.
   *
   * 5. **Liability and Archiving**
   *    The developer is not liable for configurations that produce violations during regular operation;
   *    safety margins must be manually adjusted.
   *    Upon completion, the user **MUST** export and archive the safety parameter report along with the commissioning checklists.
   *
   * **BY EXECUTING THIS SCRIPT,
   *  YOU ACKNOWLEDGE THAT YOU HAVE COMPLETED ALL PRE-COMMISSIONING STEPS
   *  AND WILL PERFORM ALL POST-COMMISSIONING VALIDATION TESTS LISTED IN THE PROJECT CHECKLIST.**
   */
  configureSmmFromFile(
    deviceRef: DeviceRef,
    username: string,
    password: string,
    configFileContent: string
  ): Observable<string> {
    // Parse the configuration file content into a ConfigFile object.
    const configFile = new ConfigFile(configFileContent);

    // Fail lambda to handle errors in a consistent way.
    const fail = (message: string) => throwError(() => new Error(message));

    // Resolve the SMM parameter structure version for getting the correct SMM OD.
    const sequence$ = this.resolveSmmParameterStructureVersion(deviceRef).pipe(
      concatMap((parameterStructureVersion) => {
        const smmOd = resolveSmmOd(parameterStructureVersion);

        // Group SMM safety parameters by their 'group' property,
        // then for each group, map the SMM OD parameters to the matching
        // config file values (or 0 if missing). Also add the structure version
        // as a special 'None' group used for transmission.
        const groupedValues: Record<string, ParameterValueType[]> = {};
        for (const [group, smmOdParameters] of mapByProperty(smmOd.parameters.safety, 'group')) {
          const values = smmOdParameters.map(
            ({ index, subindex }) =>
              configFile.parameters.find((p) => p.index === index && p.subindex === subindex)?.value ?? 0
          );
          groupedValues[group] = values;
        }
        groupedValues['None'] = [parameterStructureVersion];

        // Convert the grouped values into a format suitable for transmission.
        const values = convertGroupedSmmParametersToTransmitData(groupedValues);

        // Ensure the number of values (excluding the structure version) matches the config file parameter count.
        if (values.length - 1 !== configFile.parameters.length) {
          return fail(
            `Mismatch: the configuration file contains ${
              configFile.parameters.length
            } parameters, but the SMM parameter structure expects ${values.length - 1}.`
          );
        }

        // Log out from SMM if already logged in, then log in for parameter download.
        return this.logoutFromSmm(deviceRef).pipe(
          concatMap(() => this.loginToSmmForParameterDownload(deviceRef, username, password)),
          concatMap(() => {
            // Transmit the SMM parameters, then verify them by loading and checking against the original values.
            return this.transmitSmmParameters(deviceRef, values as DataType[], parameterStructureVersion).pipe(
              concatMap(() => {
                // Load the SMM parameters for verification.
                return this.loadSmmParametersForVerification(deviceRef).pipe(
                  concatMap((status) => {
                    if (status.request === 'failed') {
                      const message =
                        status.errorName && status.errorDescription
                          ? `${status.errorName}: ${status.errorDescription}`
                          : status.errorName || status.errorDescription || '';
                      return fail(message);
                    }

                    // Verify that the loaded values match the original values.
                    if (status.values.join(',') !== values.join(',')) {
                      return fail('Verification mismatch after loading parameters.');
                    }

                    // Group entries excluding the structure version for verification.
                    const groupEntries = Object.entries(groupedValues).slice(1);

                    // Sequentially verify each parameter group on the SMM.
                    // For each group entry, call verifySmmParameters and fail if any group fails verification.
                    // Uses concatMap to ensure groups are verified one after another in order.
                    return from(groupEntries).pipe(
                      concatMap(([_, groupValues], i) =>
                        this.verifySmmParameters(
                          deviceRef,
                          groupValues as DataType[],
                          i + 1, // Skipping the first group (None).
                          parameterStructureVersion
                        ).pipe(concatMap(() => of(true)))
                      ),
                      toArray(), // Collect all verification results.
                      concatMap(() =>
                        this.loadSmmValidationFileAndReadSafetyParametersReportFile(deviceRef).pipe(
                          concatMap((safetyParametersReport) => {
                            if (!safetyParametersReport) {
                              return fail(
                                'Failed to load the safety parameter validation file or read the safety parameter report.'
                              );
                            }

                            // Encode validation file content as Uint8Array for transmission.
                            const report = new TextEncoder().encode(safetyParametersReport);
                            return this.validateSmmConfiguration(
                              deviceRef,
                              report,
                              new Date(),
                              username,
                              password
                            ).pipe(
                              concatMap(() => {
                                // Re-read the SMM parameters to update the values stored on the Motion Master.
                                return this.getSmmOdParameters(
                                  deviceRef,
                                  parameterStructureVersion,
                                  'device',
                                  false
                                ).pipe(map(() => safetyParametersReport));
                              })
                            );
                          })
                        )
                      )
                    );
                  })
                );
              })
            );
          })
        );
      })
    );

    // Log out at the end, on success and on failure, before the result is delivered.
    return this.logoutFromSmmAfter(deviceRef, sequence$);
  }

  /**
   * Executes a forward call by sending the provided data, then parses the JSON response.
   *
   * @param data - The data to send. Can be a Uint8Array, an object (which will be JSON-stringified), or a string.
   * @param requestTimeout - Optional timeout for the request in milliseconds. Defaults to 5000 ms.
   * @param messageId - Optional message identifier.
   *
   * @returns An Observable that emits the parsed JSON response object upon a successful call.
   *
   * @throws Will throw an error if:
   * - `data` is not a Uint8Array, string, or object.
   * - The forward call fails.
   * - The response cannot be parsed as valid JSON.
   *
   * @example
   * ```ts
   * const data = { command: 'status' };
   * this.executeForwardCallAndParseResponse(data)
   *   .subscribe({
   *     next: (response) => console.log('Parsed response:', response),
   *     error: (err) => console.error('Error:', err),
   *   });
   * ```
   */
  executeForwardCallAndParseResponse(
    data: Uint8Array | string | object,
    requestTimeout: number = 5000,
    messageId?: string
  ): Observable<any> {
    const props: MotionMasterMessage.Request.IExecuteForwardCall = {};

    if (data instanceof Uint8Array) {
      props.message = data;
    } else {
      let message: string;
      if (typeof data === 'object') {
        message = JSON.stringify(data);
      } else if (typeof data === 'string') {
        message = data;
      } else {
        throw new Error('Data must be a string, object, or Uint8Array.');
      }
      const encoder = new TextEncoder();
      props.message = encoder.encode(message);
    }

    return this.executeForwardCall(props, requestTimeout, messageId).pipe(
      map((status) => {
        if (status.request === 'succeeded') {
          const bytes = status.response;
          const uint8array = Array.isArray(bytes) ? new Uint8Array(bytes) : bytes;
          if (uint8array) {
            try {
              const decoder = new TextDecoder('utf-8');
              const jsonString = decoder.decode(uint8array);
              return JSON.parse(jsonString);
            } catch (parseError) {
              throw new Error(
                `Failed to parse JSON response: ${parseError instanceof Error ? parseError.message : parseError}`
              );
            }
          }
        }
        throw new Error(`Failed to execute forward call: ${status.error?.message || 'Unknown error'}`);
      })
    );
  }

  /**
   * Auto-tunes the notch filter parameters based on the device's plant model.
   *
   * @param deviceSerialNumber - The serial number of the target device.
   * @returns An `Observable<NotchFilter>` that emits the parsed notch filter configuration upon success.
   *
   * @throws Will throw an error if:
   * - `deviceRef` is invalid.
   * - The device does not have a plant model.
   * - The forward call to the device fails.
   * - The auto-tune notch filter does not return finite valid values.
   * - The response cannot be parsed as valid JSON.
   */
  autoTuneNotchFilter(deviceRef: DeviceRef): Observable<NotchFilter> {
    return this.getPlantModel(deviceRef).pipe(
      mergeMap((plantModel: PlantModel | undefined) => {
        if (!plantModel) {
          throw new Error('Plant model is not available on the device.');
        }

        return this.executeForwardCallAndParseResponse({
          run: 'auto_tune_notch',
          data: {
            plant_model: {
              numerator: plantModel.numerator,
              denominator: plantModel.denominator,
            },
          },
        });
      }),
      map((response) => {
        if (response.notch_dp === null || response.notch_fc === null || response.notch_fw === null) {
          throw new Error('Auto-tune notch filter returned null values.');
        }

        return {
          enabled: response.notch_en ?? false,
          centerFrequency: response.notch_fc,
          rejectionBand: response.notch_fw,
          depth: response.notch_dp,
        };
      })
    );
  }

  /**
   *
   * Writes notch filter parameter values to the specified device.
   *
   * This method sets the individual parameters of the notch filter (0x2023) on the device:
   * - Subindex `0x01` → enabled flag
   * - Subindex `0x02` → center frequency (rounded to nearest integer)
   * - Subindex `0x03` → rejection band (rounded to nearest integer)
   * - Subindex `0x04` → depth
   *
   * @param deviceRef - Reference to the target device.
   * @param notchFilter - Notch filter values to write to the device.
   * @param requestTimeout - Optional timeout in milliseconds for the request (default: 1000).
   * @returns An `Observable<void>` that completes when the parameter values have been written successfully.
   *
   */
  setNotchFilterParameterValues(
    deviceRef: DeviceRef,
    notchFilter: NotchFilter,
    requestTimeout = 1000
  ): Observable<void> {
    return this.setParameterValues(
      [
        [deviceRef, 0x2023, 0x01, notchFilter.enabled ? 1 : 0],
        [deviceRef, 0x2023, 0x02, Math.round(notchFilter.centerFrequency)],
        [deviceRef, 0x2023, 0x03, Math.round(notchFilter.rejectionBand)],
        [deviceRef, 0x2023, 0x04, notchFilter.depth],
      ],
      requestTimeout
    );
  }

  /**
   * Deletes all HRD data files (`hr_data0.bin` to `hr_data4.bin`) from the device.
   *
   * The operation attempts to remove each file sequentially, regardless of whether
   * it exists or not. Missing files do not cause the sequence to stop or fail.
   *
   * @param deviceRef - Reference to the target device.
   * @returns An observable that completes once all delete operations have finished.
   */
  removeAllHrdFiles(deviceRef: DeviceRef): Observable<void> {
    const filenames = Array.from({ length: 5 }, (_, i) => `hr_data${i}.bin`);

    // Attempt to delete all HRD files regardless of whether they exist
    return from(filenames).pipe(
      concatMap((filename) => this.deleteFile(deviceRef, filename)),
      toArray(),
      map(() => void 0)
    );
  }

  /**
   * Reads a sequence of HRD files (`hr_data0.bin` to `hr_data4.bin`) from a device,
   * combines their contents into a single `Uint8Array`, and returns it as an Observable.
   *
   * Each file is read sequentially. If a file is missing or its content is `null`/`undefined`,
   * it is treated as an empty `Uint8Array`, so the sequence continues without errors.
   *
   * @param deviceRef - Reference to the device from which the files are read.
   * @returns An `Observable<Uint8Array>` that emits a single `Uint8Array` containing
   *          the combined contents of all read files.
   */
  readAllHrdFiles(deviceRef: DeviceRef): Observable<Uint8Array> {
    const readFileObservable = (filename: string) =>
      from(this.getFile(deviceRef, filename)).pipe(
        map((content) => {
          if (content) {
            return Array.isArray(content) ? new Uint8Array(content) : content;
          } else {
            return new Uint8Array();
          }
        })
      );

    const filenames = Array.from({ length: 5 }, (_, i) => `hr_data${i}.bin`);

    return from(filenames).pipe(
      concatMap((filename) => readFileObservable(filename)),
      toArray(),
      map((arrays: Uint8Array[]) => {
        const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
        const combined = new Uint8Array(totalLength);

        let offset = 0;
        for (const arr of arrays) {
          combined.set(arr, offset);
          offset += arr.length;
        }

        return combined;
      })
    );
  }

  /**
   * Runs HRD streaming on the device.
   *
   * The procedure performs the following steps:
   * 1. Configures HRD streaming with the specified data index and duration.
   * 2. Starts the streaming process.
   * 3. Reads and combines all HRD files once streaming completes.
   *
   * @param deviceRef - Device to stream from.
   * @param dataIndex - Index of the HRD streaming data (default is 0).
   * @param duration - Streaming duration in milliseconds (default is 1000).
   * @returns Observable that emits a combined `Uint8Array` containing all HRD file data.
   */
  runHrdStreaming(
    deviceRef: DeviceRef,
    dataIndex: HrdStreamingOsCommandDataIndex = 0,
    duration: number = 1000
  ): Observable<number[][]> {
    return concat(
      // Removing all HRD files upfront is unnecessary, as the firmware automatically handles this when configuring the stream.
      // defer(() => this.removeAllHrdFiles(deviceRef)),
      defer(() =>
        this.runHrdStreamingOsCommand(
          deviceRef,
          HrdStreamingOsCommandAction.CONFIGURE_STREAM,
          dataIndex,
          duration,
          10000
        )
      ),
      defer(() =>
        this.runHrdStreamingOsCommand(deviceRef, HrdStreamingOsCommandAction.START_STREAM, 0, 0, duration + 10000)
      )
    ).pipe(
      last(),
      mergeMap(() => this.readAllHrdFiles(deviceRef)),
      map((combinedData) => parseHrdBuffer(combinedData))
    );
  }

  /**
   * Executes a chirp signal on the drive for system identification purposes.
   *
   * This method sends a sequence of system identification commands to the drive, configuring
   * the necessary parameters and optionally starting HRD streaming if requested.
   *
   * Key points:
   * - By executing the chirp signal on the drive, the firmware provides a very clean signal
   *   to the motion controller without communication jitter.
   * - The sine profile is used for system identification purposes.
   * - This profile is not available as a standard Motion Profile type (0x6086).
   *
   * Requirements for the drive to correctly follow the trajectory:
   * - The drive must have the correct commutation offset angle configured.
   * - The encoder for the selected control method must be functional.
   * - The controller must be tuned appropriately.
   *
   * Limitations:
   * - The statusword cannot report the status of the profiler for this sine profile type.
   *   It does not differentiate between different motion profile types.
   * - If any parameter is out of range or the start frequency is greater than the target
   *   frequency, the drive will report an "Invalid Parameter" error.
   * - If the start and target frequencies are equal, the output wave frequency will be constant (static sine).
   *
   * Implementation notes:
   * - The generated chirp signal is added to the control loop as an additional demanded torque,
   *   just after the notch filter.
   * - This allows system identification in CST, CSV, or CSP modes (or other profile modes).
   *
   * @param deviceRef - Reference to the target device.
   * @param options - Configuration options for the chirp signal, including:
   *   - `modesOfOperation` (optional): Modes of operation to configure before starting.
   *   - `startFrequency`: Frequency at which the chirp begins.
   *   - `targetFrequency`: Frequency at which the chirp ends.
   *   - `targetAmplitude`: Amplitude of the chirp signal.
   *   - `transitionTime`: Time for frequency/amplitude transition.
   *   - `signalType`: Type of signal to generate.
   *   - `startProcedure`: If set to `WAIT_FOR_HRD_STREAMING_TO_START`, the OS command for executing
   *     the chirp signal will wait for HRD streaming to start before running the chirp.
   *
   * @returns An observable that completes after the chirp signal sequence is finished.
   *          If `startProcedure` is set to `WAIT_FOR_HRD_STREAMING_TO_START`, HRD streaming
   *          will also be executed.
   */
  runChirpSignal(deviceRef: DeviceRef, options: ChirpSignalOptions): Observable<number[][]> {
    const observables = [
      options.modesOfOperation !== undefined
        ? defer(() => this.setModesOfOperation(deviceRef, options.modesOfOperation!))
        : EMPTY,
      defer(() => this.transitionToCia402State(deviceRef, Cia402State.OPERATION_ENABLED)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 0, options.startFrequency, 10000)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 1, options.targetFrequency, 10000)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 2, options.targetAmplitude, 10000)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 3, options.transitionTime, 10000)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 4, options.signalType, 10000)),
      // Rising edge to start the chirp signal
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 5, 0, 10000)),
      defer(() => this.runSystemIdentificationOsCommand(deviceRef, 5, options.startProcedure, 10000)),
    ];

    return concat(...observables).pipe(
      last(),
      mergeMap(() => {
        if (options.startProcedure === SystemIdentificationOsCommandStartProcedure.WAIT_FOR_HRD_STREAMING_TO_START) {
          // 6693 ms is the maximum duration for 5 files, each containing 8032 bytes
          return this.runHrdStreaming(
            deviceRef,
            HrdStreamingOsCommandDataIndex.SYSTEM_IDENTIFICATION_DATA,
            options.hrdStreamingDuration
          );
        } else {
          return EMPTY;
        }
      })
    );
  }

  /**
   * Retrieves the PDO mapping for the given device.
   *
   * This method attempts to get the device-specific UI configuration and returns
   * its PDO mapping. If the device does not have a specific UI configuration or
   * its PDO mapping is missing, the default PDO mapping (`uiConfigWithDefaultPdoMapping`)
   * is returned instead.
   *
   * @param deviceRef - Reference to the device for which the UI configuration is requested.
   * @returns An `Observable` that emits the RX and TX PDO mapping for the device.
   */
  getUiConfigOrDefaultPdoMapping(deviceRef: DeviceRef): Observable<UiPdoMapping> {
    return this.getUiConfig(deviceRef).pipe(
      map((uiConfig) => uiConfig.pdoMapping ?? uiConfigWithDefaultPdoMapping.pdoMapping!)
    );
  }

  /**
   * Runs the offset detection procedure for the given device and emits step-wise progress updates.
   *
   * The procedure executes a sequence of diagnostic steps sequentially and reports their state
   * (`idle`, `running`, `succeeded`, `failed`) via an observable stream.
   *
   * Behavior
   * - If `stepIds` is provided, only those steps are executed (in order).
   * - If `stepIds` is empty, all available offset detection steps are executed.
   * - Automatically inserts `motorPhaseOrderDetection` if required by
   *   `commutationOffsetMeasurement`.
   * - Transitions the device into diagnostics mode and ensures it is in
   *   `OPERATION ENABLED` state before executing steps.
   * - Restores the brake state and transitions the device to
   *   `SWITCH_ON_DISABLED` after completion, error, or cancellation.
   * - Certain steps may temporarily release or engage the brake as needed during execution.
   *
   * Cancellation
   * - Unsubscribing from the returned observable cancels further step execution.
   * - Cancellation stops the sequence cooperatively (in-progress async operations may still complete).
   * - Cancellation is treated as an error internally.
   *
   * Error Handling
   * - Emits an error if the device is not in the required CIA402 state.
   * - Emits an error if any step fails.
   * - Emits an error if step dependencies are violated.
   * - Cleanup operations (brake restore and state transition) are always attempted in `finally`.
   *
   * @param deviceRef - Reference to the target device.
   * @param stepIds - Optional list of step IDs to execute. If omitted or empty, all steps are executed.
   * @param brakeReleaseWaitMs - Additional time in milliseconds to wait on top of the brake pull time
   *   (0x2004:03) after releasing the brake before proceeding with each diagnostic step that requires
   *   an open brake. If the brake does not fully release in time, ensure the brake pull time
   *   (0x2004:03) is correctly configured for your hardware (default: 50).
   * @param brakeEngageWaitMs - Time in milliseconds to wait after engaging the brake before proceeding
   *   with each diagnostic step that requires an engaged brake (default: 50).
   * @returns An observable that emits the progress and final state of each offset detection step.
   *
   * @remarks
   * Each emission contains a cloned snapshot of all steps to allow safe external consumption
   * without mutation side effects.
   */
  runOffsetDetection(
    deviceRef: DeviceRef,
    stepIds: OffsetDetectionStepId[] = [],
    brakeReleaseWaitMs = 50,
    brakeEngageWaitMs = 50
  ): Observable<OffsetDetectionSteps> {
    return new Observable<OffsetDetectionSteps>((subscriber) => {
      let isCancelled = false;

      // Brake-related state restored in finally block
      let brakeReleaseStrategy = 0;
      let brakeStatusInit = 0;
      let measurementMethod = 0;

      // Throws if cancellation was requested
      const checkCancelled = () => {
        if (isCancelled) {
          throw new Error('Offset detection procedure was cancelled.');
        }
      };

      const runInternal = async () => {
        // Determine active steps (either provided subset or all steps)
        let activeStepIds = stepIds.length > 0 ? [...stepIds] : offsetDetectionSteps.map((s) => s.id);

        // Ensure motor phase order detection is included if required by commutation step
        const needsMotorPhaseOrderDetection = activeStepIds.includes('commutationOffsetMeasurement');
        const hasMotorPhaseOrderDetection = activeStepIds.includes('motorPhaseOrderDetection');

        if (needsMotorPhaseOrderDetection && !hasMotorPhaseOrderDetection) {
          const idx = activeStepIds.indexOf('commutationOffsetMeasurement');
          activeStepIds.splice(idx, 0, 'motorPhaseOrderDetection');
        }

        // Initial step snapshot with all steps in 'idle' state
        const steps = structuredClone(offsetDetectionSteps);
        if (!isCancelled) {
          subscriber.next(steps);
        }

        try {
          // Read initial brake configuration from device
          [brakeReleaseStrategy, brakeStatusInit, measurementMethod] = (await this.uploadMany([
            [deviceRef, 0x2004, 4],
            [deviceRef, 0x2004, 7],
            [deviceRef, 0x2009, 3],
          ])) as [number, number, number];

          checkCancelled();

          // Mapping of step IDs to their corresponding execution functions and result value keys
          const stepCommand: Record<OffsetDetectionStepId, { execute: () => Promise<any>; valueKey: string }> = {
            openPhaseDetection: {
              execute: async () => {
                return await lastValueFrom(this.runOpenPhaseDetectionOsCommand(deviceRef));
              },
              valueKey: 'phaseOpened',
            },
            phaseResistanceMeasurement: {
              execute: async () => {
                return await lastValueFrom(this.runPhaseResistanceMeasurementOsCommand(deviceRef));
              },
              valueKey: 'phaseResistance',
            },
            phaseInductanceMeasurement: {
              execute: async () => {
                return await lastValueFrom(this.runPhaseInductanceMeasurementOsCommand(deviceRef));
              },
              valueKey: 'phaseInductance',
            },
            polePairDetection: {
              execute: async () => {
                await this.releaseBrake(deviceRef, brakeReleaseWaitMs);
                return await lastValueFrom(this.runPolePairDetectionOsCommand(deviceRef));
              },
              valueKey: 'numberOfPolePairs',
            },
            motorPhaseOrderDetection: {
              execute: async () => {
                await this.releaseBrake(deviceRef, brakeReleaseWaitMs);
                return await lastValueFrom(this.runMotorPhaseOrderDetectionOsCommand(deviceRef));
              },
              valueKey: 'motorPhaseOrder',
            },
            commutationOffsetMeasurement: {
              execute: async () => {
                measurementMethod === 2
                  ? await this.engageBrake(deviceRef, brakeEngageWaitMs)
                  : await this.releaseBrake(deviceRef, brakeReleaseWaitMs);
                return await lastValueFrom(this.runCommutationOffsetMeasurementOsCommand(deviceRef));
              },
              valueKey: 'commutationAngleOffset',
            },
          };

          checkCancelled();

          // Switch device into diagnostics mode and ensure OPERATION ENABLED state
          await lastValueFrom(
            this.setModesOfOperationAndTransitionToCia402State(
              deviceRef,
              ModesOfOperation.DIAGNOSTICS_MODE,
              Cia402State.OPERATION_ENABLED
            )
          );

          // Execute each selected diagnostic step sequentially and emit progress updates
          for (const id of activeStepIds) {
            checkCancelled();

            // Verify device state before each step to ensure it is still in OPERATION ENABLED
            const cia402state = await lastValueFrom(this.getCia402State(deviceRef));
            if (cia402state !== Cia402State.OPERATION_ENABLED) {
              let errorMessage =
                'Device is not in the expected state for running diagnostics. Please ensure the device is in OPERATION ENABLED state.';
              if (cia402state === Cia402State.FAULT) {
                try {
                  const errorReport = await this.upload<string>(deviceRef, 0x203f, 0x01);
                  errorMessage += ` Device has a fault. Error report (0x203F): ${errorReport}`;
                } catch {}
              }
              subscriber.error(new Error(errorMessage));
              return;
            }

            checkCancelled();

            const step = steps.find((s: OffsetDetectionSteps[number]) => s.id === id);
            const command = stepCommand[id];

            // Validate configuration consistency
            if (!step || !command) {
              throw new Error(`Invalid diagnostic configuration for: "${id}"`);
            }

            // Mark step as running and emit progress
            step.status = 'running';
            subscriber.next(structuredClone(steps));

            // Execute diagnostic command
            const response = await command.execute();

            checkCancelled();

            if (response.request === 'succeeded') {
              // Mark success and store result value
              step.status = 'succeeded';
              step.value = response[command.valueKey];
            } else {
              // Mark failure and store error details
              step.status = 'failed';
              step.error = `${response.errorCode}: ${response.errorDescription} (${response.errorName})`;

              // Special validation: enforce dependency between steps
              const nextStepId = activeStepIds[activeStepIds.indexOf(id) + 1];
              if (id === 'motorPhaseOrderDetection' && nextStepId === 'commutationOffsetMeasurement') {
                subscriber.next(structuredClone(steps));
                subscriber.error(
                  new Error(
                    'Commutation Offset Measurement cannot be performed until Motor Phase Order Detection has successfully completed.'
                  )
                );
                return;
              }
            }

            // Emit updated progress after each step
            if (!isCancelled) {
              subscriber.next(structuredClone(steps));
            }
          }

          // Complete observable if not cancelled
          if (!isCancelled) {
            subscriber.complete();
          }
        } catch (error) {
          // Emit error unless cancellation occurred
          if (!isCancelled) {
            // Mark all running steps as failed on error
            steps.forEach((s: OffsetDetectionSteps[number]) => {
              if (s.status === 'running') {
                s.status = 'failed';
              }
            });
            // Emit final step states before error
            subscriber.next(structuredClone(steps));
            subscriber.error(error instanceof Error ? error : new Error(String(error)));
          } else {
            console.warn('Offset detection procedure was cancelled.');
          }
        } finally {
          // Always restore brake state if it was modified
          if (brakeReleaseStrategy !== 0) {
            try {
              await this.download(deviceRef, 0x2004, 7, brakeStatusInit);
            } catch (error) {
              console.error(
                `Failed to restore brake state (${brakeStatusInit}) after offset detection procedure: ${
                  error instanceof Error ? error.message : String(error)
                }`
              );
            }
          }

          // Always attempt to return device to a safe state
          try {
            await this.transitionToCia402State(deviceRef, Cia402State.SWITCH_ON_DISABLED);
          } catch (error) {
            console.error(
              `Failed to transition device to Switch on Disabled state after offset detection procedure: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
        }
      };

      runInternal();

      // Cancellation handler from observable unsubscribe
      return () => {
        isCancelled = true;
      };
    });
  }

  /**
   * Returns the display unit string for a device parameter, adjusted for the device's current
   * SI unit velocity setting (0x60A9) and feed constant configuration (0x6092:01).
   *
   * The feed constant is considered enabled when 0x6092:01 is a positive value other than 0xFFFFFFFF.
   * When enabled, velocity and position units are expressed in feed units (e.g. `mm/min`) instead of
   * the raw SI unit (e.g. `rpm`). Parameters not affected by these settings are returned unchanged.
   *
   * @param deviceRef - Reference to the device to read configuration from.
   * @param index - Object dictionary index of the parameter.
   * @param subindex - Object dictionary subindex of the parameter.
   * @param unit - Base unit string as stored in the parameter metadata (e.g. `"Inc"`, `"rpm"`).
   * @returns The formatted unit string for display purposes.
   */
  async getDeviceParameterFormattedUnitDisplay(
    deviceRef: DeviceRef,
    index: number,
    subindex: number,
    unit: string
  ): Promise<string> {
    const siUnitVelocityValue = (await this.upload(deviceRef, 0x60a9, 0)) as number;
    const feedConstantFeed = (await this.upload(deviceRef, 0x6092, 1)) as number;
    const feedConstantEnabled = feedConstantFeed !== 0xffffffff && feedConstantFeed > 0;
    return getParameterFormattedUnitDisplay(index, subindex, unit, siUnitVelocityValue, feedConstantEnabled);
  }
}
/**
 * Extracts supported firmware product options based on the provided firmware version and optional device product ID.
 *
 * @param supportedOptionsPromise - A promise resolving to the full set of supported firmware product options.
 * @param firmwareVersionPromise - A promise resolving to the firmware version string.
 * @param versionedDeviceProductId - (Optional) The specific device product ID to filter options for.
 * @returns An Observable that emits the array of supported options for the given firmware version and device ID,
 *          or an empty array if the version cannot be parsed or no options are found.
 */
function extractFirmwareProductSupportedOptions(
  supportedOptionsPromise: Promise<FirmwareProductSupportedOptions>,
  firmwareVersionPromise: Promise<string>,
  versionedDeviceProductId?: string
): Observable<number[][]> {
  return forkJoin([supportedOptionsPromise, firmwareVersionPromise]).pipe(
    map(([supportedOptions, firmwareVersion]) => {
      const parsedFirmwareVersion = semver.parse(firmwareVersion);
      if (!parsedFirmwareVersion) {
        return [];
      }

      const { major, minor } = parsedFirmwareVersion;

      return supportedOptions[`${major}.${minor}`]?.[`${versionedDeviceProductId}`] ?? [];
    })
  );
}
